import * as React from 'react';
import React__default, { useMemo, useState, createContext, useEffect, useContext, useCallback, useRef } from 'react';

var observerMap =  new Map();
var RootIds =  new WeakMap();
var rootId = 0;
var unsupportedValue = void 0;
function getRootId(root) {
  if (!root)
    return "0";
  if (RootIds.has(root))
    return RootIds.get(root);
  rootId += 1;
  RootIds.set(root, rootId.toString());
  return RootIds.get(root);
}
function optionsToId(options) {
  return Object.keys(options).sort().filter(
    (key) => options[key] !== void 0
  ).map((key) => {
    return `${key}_${key === "root" ? getRootId(options.root) : options[key]}`;
  }).toString();
}
function createObserver(options) {
  const id = optionsToId(options);
  let instance = observerMap.get(id);
  if (!instance) {
    const elements =  new Map();
    let thresholds;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        var _a;
        const inView = entry.isIntersecting && thresholds.some((threshold) => entry.intersectionRatio >= threshold);
        if (options.trackVisibility && typeof entry.isVisible === "undefined") {
          entry.isVisible = inView;
        }
        (_a = elements.get(entry.target)) == null ? void 0 : _a.forEach((callback) => {
          callback(inView, entry);
        });
      });
    }, options);
    thresholds = observer.thresholds || (Array.isArray(options.threshold) ? options.threshold : [options.threshold || 0]);
    instance = {
      id,
      observer,
      elements
    };
    observerMap.set(id, instance);
  }
  return instance;
}
function observe(element, callback, options = {}, fallbackInView = unsupportedValue) {
  if (typeof window.IntersectionObserver === "undefined" && fallbackInView !== void 0) {
    const bounds = element.getBoundingClientRect();
    callback(fallbackInView, {
      isIntersecting: fallbackInView,
      target: element,
      intersectionRatio: typeof options.threshold === "number" ? options.threshold : 0,
      time: 0,
      boundingClientRect: bounds,
      intersectionRect: bounds,
      rootBounds: bounds
    });
    return () => {
    };
  }
  const { id, observer, elements } = createObserver(options);
  const callbacks = elements.get(element) || [];
  if (!elements.has(element)) {
    elements.set(element, callbacks);
  }
  callbacks.push(callback);
  observer.observe(element);
  return function unobserve() {
    callbacks.splice(callbacks.indexOf(callback), 1);
    if (callbacks.length === 0) {
      elements.delete(element);
      observer.unobserve(element);
    }
    if (elements.size === 0) {
      observer.disconnect();
      observerMap.delete(id);
    }
  };
}
function useInView({
  threshold,
  delay,
  trackVisibility,
  rootMargin,
  root,
  triggerOnce,
  skip,
  initialInView,
  fallbackInView,
  onChange
} = {}) {
  var _a;
  const [ref, setRef] = React.useState(null);
  const callback = React.useRef();
  const [state, setState] = React.useState({
    inView: !!initialInView,
    entry: void 0
  });
  callback.current = onChange;
  React.useEffect(
    () => {
      if (skip || !ref)
        return;
      let unobserve;
      unobserve = observe(
        ref,
        (inView, entry) => {
          setState({
            inView,
            entry
          });
          if (callback.current)
            callback.current(inView, entry);
          if (entry.isIntersecting && triggerOnce && unobserve) {
            unobserve();
            unobserve = void 0;
          }
        },
        {
          root,
          rootMargin,
          threshold,
          trackVisibility,
          delay
        },
        fallbackInView
      );
      return () => {
        if (unobserve) {
          unobserve();
        }
      };
    },
    [
      Array.isArray(threshold) ? threshold.toString() : threshold,
      ref,
      root,
      rootMargin,
      triggerOnce,
      skip,
      trackVisibility,
      fallbackInView,
      delay
    ]
  );
  const entryTarget = (_a = state.entry) == null ? void 0 : _a.target;
  const previousEntryTarget = React.useRef();
  if (!ref && entryTarget && !triggerOnce && !skip && previousEntryTarget.current !== entryTarget) {
    previousEntryTarget.current = entryTarget;
    setState({
      inView: !!initialInView,
      entry: void 0
    });
  }
  const result = [setRef, state.inView, state.entry];
  result.ref = result[0];
  result.inView = result[1];
  result.entry = result[2];
  return result;
}

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;
  if (!css || typeof document === 'undefined') { return; }
  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';
  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }
  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z$d = ".styles-module_image-container__T46Z-{background-color:#fff;overflow:hidden;padding-bottom:12px;position:relative;width:170px}.styles-module_image-container__T46Z-.styles-module_vertical__DbclK{height:201px}.styles-module_image-container__T46Z-.styles-module_horizontal__gvAxA{height:170px}.styles-module_image-container__T46Z- .styles-module_sponsored-image__1ESCT{height:auto;left:50%;max-height:100%;max-width:100%;position:absolute;top:50%;transform:translate(-50%,-50%);width:auto}.styles-module_image-container__T46Z-:before{background-color:#fff;bottom:0;content:\"\";position:absolute;right:0;top:0;width:170px;z-index:-1}.styles-module_image-container__T46Z-:after{background-color:#fff;bottom:0;content:\"\";left:0;position:absolute;top:0;width:170px;z-index:-1}.styles-module_image-container__T46Z- .styles-module_meatstickers__UQeDY{bottom:2px;box-sizing:border-box;display:flex;position:absolute;width:100%}.styles-module_image-container__T46Z- .styles-module_meatstickers__UQeDY.styles-module_vertical__DbclK{margin-left:1px}.styles-module_image-container__T46Z- .styles-module_meatstickers__UQeDY.styles-module_horizontal__gvAxA{margin-left:15px}.styles-module_image-container__T46Z- .styles-module_sticker-event__-EdQb{font-family:Lato;font-size:10.348px;font-style:normal;font-weight:700;line-height:12.565px;margin-right:4px;padding:.739px 4.435px 2.217px;width:-moz-max-content;width:max-content}.styles-module_image-container__T46Z- .styles-module_sticker-badge__ZgBNo{bottom:0;position:absolute}";
var styles$5 = {"image-container":"styles-module_image-container__T46Z-","vertical":"styles-module_vertical__DbclK","horizontal":"styles-module_horizontal__gvAxA","sponsored-image":"styles-module_sponsored-image__1ESCT","meatstickers":"styles-module_meatstickers__UQeDY","sticker-event":"styles-module_sticker-event__-EdQb","sticker-badge":"styles-module_sticker-badge__ZgBNo"};
styleInject(css_248z$d);

var SponsoredProductImage = function (_a) {
    var imageUrl = _a.imageUrl, altText = _a.altText, meatStickers = _a.meatStickers, isVerticalBanner = _a.isVerticalBanner, customSize = _a.customSize;
    var imageURLMaker = function (filename) {
        return "/a/fa/product/static/images/".concat(filename);
    };
    var onErrorFallback = function (_a) {
        var currentTarget = _a.currentTarget;
        /* eslint-disable no-param-reassign */
        currentTarget.onerror = null;
        var fallbackSrc = imageURLMaker('defaultImage.jpg');
        currentTarget.src = fallbackSrc;
        currentTarget.srcset = fallbackSrc;
    };
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("div", null,
            React__default.createElement("div", null,
                React__default.createElement("div", { className: "".concat(styles$5['image-container'], " ").concat(isVerticalBanner ? styles$5['vertical'] : styles$5['horizontal']), style: customSize },
                    React__default.createElement("img", { src: imageUrl, alt: altText, className: styles$5['sponsored-image'], onError: onErrorFallback }),
                    React__default.createElement("div", { className: "".concat(styles$5['meatstickers'], " ").concat(isVerticalBanner ? styles$5['vertical'] : styles$5['horizontal']) }, meatStickers &&
                        meatStickers.map(function (sticker) { return (React__default.createElement("span", { key: sticker.label, id: "testId-Pod-stickers-".concat(sticker.label), className: styles$5['sticker-event'], style: {
                                backgroundColor: sticker.styles.backgroundColor,
                                color: sticker.styles.textColor,
                            } }, sticker.label)); })))))));
};

var propTypes = {exports: {}};

var reactIs = {exports: {}};

var reactIs_production_min = {};

/** @license React v16.13.1
 * react-is.production.min.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
var b="function"===typeof Symbol&&Symbol.for,c=b?Symbol.for("react.element"):60103,d=b?Symbol.for("react.portal"):60106,e=b?Symbol.for("react.fragment"):60107,f=b?Symbol.for("react.strict_mode"):60108,g=b?Symbol.for("react.profiler"):60114,h=b?Symbol.for("react.provider"):60109,k=b?Symbol.for("react.context"):60110,l=b?Symbol.for("react.async_mode"):60111,m=b?Symbol.for("react.concurrent_mode"):60111,n=b?Symbol.for("react.forward_ref"):60112,p=b?Symbol.for("react.suspense"):60113,q=b?
Symbol.for("react.suspense_list"):60120,r=b?Symbol.for("react.memo"):60115,t=b?Symbol.for("react.lazy"):60116,v=b?Symbol.for("react.block"):60121,w=b?Symbol.for("react.fundamental"):60117,x=b?Symbol.for("react.responder"):60118,y=b?Symbol.for("react.scope"):60119;
function z(a){if("object"===typeof a&&null!==a){var u=a.$$typeof;switch(u){case c:switch(a=a.type,a){case l:case m:case e:case g:case f:case p:return a;default:switch(a=a&&a.$$typeof,a){case k:case n:case t:case r:case h:return a;default:return u}}case d:return u}}}function A(a){return z(a)===m}reactIs_production_min.AsyncMode=l;reactIs_production_min.ConcurrentMode=m;reactIs_production_min.ContextConsumer=k;reactIs_production_min.ContextProvider=h;reactIs_production_min.Element=c;reactIs_production_min.ForwardRef=n;reactIs_production_min.Fragment=e;reactIs_production_min.Lazy=t;reactIs_production_min.Memo=r;reactIs_production_min.Portal=d;
reactIs_production_min.Profiler=g;reactIs_production_min.StrictMode=f;reactIs_production_min.Suspense=p;reactIs_production_min.isAsyncMode=function(a){return A(a)||z(a)===l};reactIs_production_min.isConcurrentMode=A;reactIs_production_min.isContextConsumer=function(a){return z(a)===k};reactIs_production_min.isContextProvider=function(a){return z(a)===h};reactIs_production_min.isElement=function(a){return "object"===typeof a&&null!==a&&a.$$typeof===c};reactIs_production_min.isForwardRef=function(a){return z(a)===n};reactIs_production_min.isFragment=function(a){return z(a)===e};reactIs_production_min.isLazy=function(a){return z(a)===t};
reactIs_production_min.isMemo=function(a){return z(a)===r};reactIs_production_min.isPortal=function(a){return z(a)===d};reactIs_production_min.isProfiler=function(a){return z(a)===g};reactIs_production_min.isStrictMode=function(a){return z(a)===f};reactIs_production_min.isSuspense=function(a){return z(a)===p};
reactIs_production_min.isValidElementType=function(a){return "string"===typeof a||"function"===typeof a||a===e||a===m||a===g||a===f||a===p||a===q||"object"===typeof a&&null!==a&&(a.$$typeof===t||a.$$typeof===r||a.$$typeof===h||a.$$typeof===k||a.$$typeof===n||a.$$typeof===w||a.$$typeof===x||a.$$typeof===y||a.$$typeof===v)};reactIs_production_min.typeOf=z;

var reactIs_development = {};

/** @license React v16.13.1
 * react-is.development.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
if (process.env.NODE_ENV !== "production") {
  (function() {
var hasSymbol = typeof Symbol === 'function' && Symbol.for;
var REACT_ELEMENT_TYPE = hasSymbol ? Symbol.for('react.element') : 0xeac7;
var REACT_PORTAL_TYPE = hasSymbol ? Symbol.for('react.portal') : 0xeaca;
var REACT_FRAGMENT_TYPE = hasSymbol ? Symbol.for('react.fragment') : 0xeacb;
var REACT_STRICT_MODE_TYPE = hasSymbol ? Symbol.for('react.strict_mode') : 0xeacc;
var REACT_PROFILER_TYPE = hasSymbol ? Symbol.for('react.profiler') : 0xead2;
var REACT_PROVIDER_TYPE = hasSymbol ? Symbol.for('react.provider') : 0xeacd;
var REACT_CONTEXT_TYPE = hasSymbol ? Symbol.for('react.context') : 0xeace;
var REACT_ASYNC_MODE_TYPE = hasSymbol ? Symbol.for('react.async_mode') : 0xeacf;
var REACT_CONCURRENT_MODE_TYPE = hasSymbol ? Symbol.for('react.concurrent_mode') : 0xeacf;
var REACT_FORWARD_REF_TYPE = hasSymbol ? Symbol.for('react.forward_ref') : 0xead0;
var REACT_SUSPENSE_TYPE = hasSymbol ? Symbol.for('react.suspense') : 0xead1;
var REACT_SUSPENSE_LIST_TYPE = hasSymbol ? Symbol.for('react.suspense_list') : 0xead8;
var REACT_MEMO_TYPE = hasSymbol ? Symbol.for('react.memo') : 0xead3;
var REACT_LAZY_TYPE = hasSymbol ? Symbol.for('react.lazy') : 0xead4;
var REACT_BLOCK_TYPE = hasSymbol ? Symbol.for('react.block') : 0xead9;
var REACT_FUNDAMENTAL_TYPE = hasSymbol ? Symbol.for('react.fundamental') : 0xead5;
var REACT_RESPONDER_TYPE = hasSymbol ? Symbol.for('react.responder') : 0xead6;
var REACT_SCOPE_TYPE = hasSymbol ? Symbol.for('react.scope') : 0xead7;
function isValidElementType(type) {
  return typeof type === 'string' || typeof type === 'function' ||
  type === REACT_FRAGMENT_TYPE || type === REACT_CONCURRENT_MODE_TYPE || type === REACT_PROFILER_TYPE || type === REACT_STRICT_MODE_TYPE || type === REACT_SUSPENSE_TYPE || type === REACT_SUSPENSE_LIST_TYPE || typeof type === 'object' && type !== null && (type.$$typeof === REACT_LAZY_TYPE || type.$$typeof === REACT_MEMO_TYPE || type.$$typeof === REACT_PROVIDER_TYPE || type.$$typeof === REACT_CONTEXT_TYPE || type.$$typeof === REACT_FORWARD_REF_TYPE || type.$$typeof === REACT_FUNDAMENTAL_TYPE || type.$$typeof === REACT_RESPONDER_TYPE || type.$$typeof === REACT_SCOPE_TYPE || type.$$typeof === REACT_BLOCK_TYPE);
}
function typeOf(object) {
  if (typeof object === 'object' && object !== null) {
    var $$typeof = object.$$typeof;
    switch ($$typeof) {
      case REACT_ELEMENT_TYPE:
        var type = object.type;
        switch (type) {
          case REACT_ASYNC_MODE_TYPE:
          case REACT_CONCURRENT_MODE_TYPE:
          case REACT_FRAGMENT_TYPE:
          case REACT_PROFILER_TYPE:
          case REACT_STRICT_MODE_TYPE:
          case REACT_SUSPENSE_TYPE:
            return type;
          default:
            var $$typeofType = type && type.$$typeof;
            switch ($$typeofType) {
              case REACT_CONTEXT_TYPE:
              case REACT_FORWARD_REF_TYPE:
              case REACT_LAZY_TYPE:
              case REACT_MEMO_TYPE:
              case REACT_PROVIDER_TYPE:
                return $$typeofType;
              default:
                return $$typeof;
            }
        }
      case REACT_PORTAL_TYPE:
        return $$typeof;
    }
  }
  return undefined;
}
var AsyncMode = REACT_ASYNC_MODE_TYPE;
var ConcurrentMode = REACT_CONCURRENT_MODE_TYPE;
var ContextConsumer = REACT_CONTEXT_TYPE;
var ContextProvider = REACT_PROVIDER_TYPE;
var Element = REACT_ELEMENT_TYPE;
var ForwardRef = REACT_FORWARD_REF_TYPE;
var Fragment = REACT_FRAGMENT_TYPE;
var Lazy = REACT_LAZY_TYPE;
var Memo = REACT_MEMO_TYPE;
var Portal = REACT_PORTAL_TYPE;
var Profiler = REACT_PROFILER_TYPE;
var StrictMode = REACT_STRICT_MODE_TYPE;
var Suspense = REACT_SUSPENSE_TYPE;
var hasWarnedAboutDeprecatedIsAsyncMode = false;
function isAsyncMode(object) {
  {
    if (!hasWarnedAboutDeprecatedIsAsyncMode) {
      hasWarnedAboutDeprecatedIsAsyncMode = true;
      console['warn']('The ReactIs.isAsyncMode() alias has been deprecated, ' + 'and will be removed in React 17+. Update your code to use ' + 'ReactIs.isConcurrentMode() instead. It has the exact same API.');
    }
  }
  return isConcurrentMode(object) || typeOf(object) === REACT_ASYNC_MODE_TYPE;
}
function isConcurrentMode(object) {
  return typeOf(object) === REACT_CONCURRENT_MODE_TYPE;
}
function isContextConsumer(object) {
  return typeOf(object) === REACT_CONTEXT_TYPE;
}
function isContextProvider(object) {
  return typeOf(object) === REACT_PROVIDER_TYPE;
}
function isElement(object) {
  return typeof object === 'object' && object !== null && object.$$typeof === REACT_ELEMENT_TYPE;
}
function isForwardRef(object) {
  return typeOf(object) === REACT_FORWARD_REF_TYPE;
}
function isFragment(object) {
  return typeOf(object) === REACT_FRAGMENT_TYPE;
}
function isLazy(object) {
  return typeOf(object) === REACT_LAZY_TYPE;
}
function isMemo(object) {
  return typeOf(object) === REACT_MEMO_TYPE;
}
function isPortal(object) {
  return typeOf(object) === REACT_PORTAL_TYPE;
}
function isProfiler(object) {
  return typeOf(object) === REACT_PROFILER_TYPE;
}
function isStrictMode(object) {
  return typeOf(object) === REACT_STRICT_MODE_TYPE;
}
function isSuspense(object) {
  return typeOf(object) === REACT_SUSPENSE_TYPE;
}
reactIs_development.AsyncMode = AsyncMode;
reactIs_development.ConcurrentMode = ConcurrentMode;
reactIs_development.ContextConsumer = ContextConsumer;
reactIs_development.ContextProvider = ContextProvider;
reactIs_development.Element = Element;
reactIs_development.ForwardRef = ForwardRef;
reactIs_development.Fragment = Fragment;
reactIs_development.Lazy = Lazy;
reactIs_development.Memo = Memo;
reactIs_development.Portal = Portal;
reactIs_development.Profiler = Profiler;
reactIs_development.StrictMode = StrictMode;
reactIs_development.Suspense = Suspense;
reactIs_development.isAsyncMode = isAsyncMode;
reactIs_development.isConcurrentMode = isConcurrentMode;
reactIs_development.isContextConsumer = isContextConsumer;
reactIs_development.isContextProvider = isContextProvider;
reactIs_development.isElement = isElement;
reactIs_development.isForwardRef = isForwardRef;
reactIs_development.isFragment = isFragment;
reactIs_development.isLazy = isLazy;
reactIs_development.isMemo = isMemo;
reactIs_development.isPortal = isPortal;
reactIs_development.isProfiler = isProfiler;
reactIs_development.isStrictMode = isStrictMode;
reactIs_development.isSuspense = isSuspense;
reactIs_development.isValidElementType = isValidElementType;
reactIs_development.typeOf = typeOf;
  })();
}

if (process.env.NODE_ENV === 'production') {
  reactIs.exports = reactIs_production_min;
} else {
  reactIs.exports = reactIs_development;
}

/*
object-assign
(c) Sindre Sorhus
@license MIT
*/
var getOwnPropertySymbols = Object.getOwnPropertySymbols;
var hasOwnProperty = Object.prototype.hasOwnProperty;
var propIsEnumerable = Object.prototype.propertyIsEnumerable;
function toObject(val) {
	if (val === null || val === undefined) {
		throw new TypeError('Object.assign cannot be called with null or undefined');
	}
	return Object(val);
}
function shouldUseNative() {
	try {
		if (!Object.assign) {
			return false;
		}
		var test1 = new String('abc');
		test1[5] = 'de';
		if (Object.getOwnPropertyNames(test1)[0] === '5') {
			return false;
		}
		var test2 = {};
		for (var i = 0; i < 10; i++) {
			test2['_' + String.fromCharCode(i)] = i;
		}
		var order2 = Object.getOwnPropertyNames(test2).map(function (n) {
			return test2[n];
		});
		if (order2.join('') !== '0123456789') {
			return false;
		}
		var test3 = {};
		'abcdefghijklmnopqrst'.split('').forEach(function (letter) {
			test3[letter] = letter;
		});
		if (Object.keys(Object.assign({}, test3)).join('') !==
				'abcdefghijklmnopqrst') {
			return false;
		}
		return true;
	} catch (err) {
		return false;
	}
}
var objectAssign = shouldUseNative() ? Object.assign : function (target, source) {
	var from;
	var to = toObject(target);
	var symbols;
	for (var s = 1; s < arguments.length; s++) {
		from = Object(arguments[s]);
		for (var key in from) {
			if (hasOwnProperty.call(from, key)) {
				to[key] = from[key];
			}
		}
		if (getOwnPropertySymbols) {
			symbols = getOwnPropertySymbols(from);
			for (var i = 0; i < symbols.length; i++) {
				if (propIsEnumerable.call(from, symbols[i])) {
					to[symbols[i]] = from[symbols[i]];
				}
			}
		}
	}
	return to;
};

var ReactPropTypesSecret$3 = 'SECRET_DO_NOT_PASS_THIS_OR_YOU_WILL_BE_FIRED';
var ReactPropTypesSecret_1 = ReactPropTypesSecret$3;

var has$2 = Function.call.bind(Object.prototype.hasOwnProperty);

var printWarning$1 = function() {};
if (process.env.NODE_ENV !== 'production') {
  var ReactPropTypesSecret$2 = ReactPropTypesSecret_1;
  var loggedTypeFailures = {};
  var has$1 = has$2;
  printWarning$1 = function(text) {
    var message = 'Warning: ' + text;
    if (typeof console !== 'undefined') {
      console.error(message);
    }
    try {
      throw new Error(message);
    } catch (x) {  }
  };
}
function checkPropTypes$1(typeSpecs, values, location, componentName, getStack) {
  if (process.env.NODE_ENV !== 'production') {
    for (var typeSpecName in typeSpecs) {
      if (has$1(typeSpecs, typeSpecName)) {
        var error;
        try {
          if (typeof typeSpecs[typeSpecName] !== 'function') {
            var err = Error(
              (componentName || 'React class') + ': ' + location + ' type `' + typeSpecName + '` is invalid; ' +
              'it must be a function, usually from the `prop-types` package, but received `' + typeof typeSpecs[typeSpecName] + '`.' +
              'This often happens because of typos such as `PropTypes.function` instead of `PropTypes.func`.'
            );
            err.name = 'Invariant Violation';
            throw err;
          }
          error = typeSpecs[typeSpecName](values, typeSpecName, componentName, location, null, ReactPropTypesSecret$2);
        } catch (ex) {
          error = ex;
        }
        if (error && !(error instanceof Error)) {
          printWarning$1(
            (componentName || 'React class') + ': type specification of ' +
            location + ' `' + typeSpecName + '` is invalid; the type checker ' +
            'function must return `null` or an `Error` but returned a ' + typeof error + '. ' +
            'You may have forgotten to pass an argument to the type checker ' +
            'creator (arrayOf, instanceOf, objectOf, oneOf, oneOfType, and ' +
            'shape all require an argument).'
          );
        }
        if (error instanceof Error && !(error.message in loggedTypeFailures)) {
          loggedTypeFailures[error.message] = true;
          var stack = getStack ? getStack() : '';
          printWarning$1(
            'Failed ' + location + ' type: ' + error.message + (stack != null ? stack : '')
          );
        }
      }
    }
  }
}
checkPropTypes$1.resetWarningCache = function() {
  if (process.env.NODE_ENV !== 'production') {
    loggedTypeFailures = {};
  }
};
var checkPropTypes_1 = checkPropTypes$1;

var ReactIs$1 = reactIs.exports;
var assign = objectAssign;
var ReactPropTypesSecret$1 = ReactPropTypesSecret_1;
var has = has$2;
var checkPropTypes = checkPropTypes_1;
var printWarning = function() {};
if (process.env.NODE_ENV !== 'production') {
  printWarning = function(text) {
    var message = 'Warning: ' + text;
    if (typeof console !== 'undefined') {
      console.error(message);
    }
    try {
      throw new Error(message);
    } catch (x) {}
  };
}
function emptyFunctionThatReturnsNull() {
  return null;
}
var factoryWithTypeCheckers = function(isValidElement, throwOnDirectAccess) {
  var ITERATOR_SYMBOL = typeof Symbol === 'function' && Symbol.iterator;
  var FAUX_ITERATOR_SYMBOL = '@@iterator';
  function getIteratorFn(maybeIterable) {
    var iteratorFn = maybeIterable && (ITERATOR_SYMBOL && maybeIterable[ITERATOR_SYMBOL] || maybeIterable[FAUX_ITERATOR_SYMBOL]);
    if (typeof iteratorFn === 'function') {
      return iteratorFn;
    }
  }
  var ANONYMOUS = '<<anonymous>>';
  var ReactPropTypes = {
    array: createPrimitiveTypeChecker('array'),
    bigint: createPrimitiveTypeChecker('bigint'),
    bool: createPrimitiveTypeChecker('boolean'),
    func: createPrimitiveTypeChecker('function'),
    number: createPrimitiveTypeChecker('number'),
    object: createPrimitiveTypeChecker('object'),
    string: createPrimitiveTypeChecker('string'),
    symbol: createPrimitiveTypeChecker('symbol'),
    any: createAnyTypeChecker(),
    arrayOf: createArrayOfTypeChecker,
    element: createElementTypeChecker(),
    elementType: createElementTypeTypeChecker(),
    instanceOf: createInstanceTypeChecker,
    node: createNodeChecker(),
    objectOf: createObjectOfTypeChecker,
    oneOf: createEnumTypeChecker,
    oneOfType: createUnionTypeChecker,
    shape: createShapeTypeChecker,
    exact: createStrictShapeTypeChecker,
  };
  function is(x, y) {
    if (x === y) {
      return x !== 0 || 1 / x === 1 / y;
    } else {
      return x !== x && y !== y;
    }
  }
  function PropTypeError(message, data) {
    this.message = message;
    this.data = data && typeof data === 'object' ? data: {};
    this.stack = '';
  }
  PropTypeError.prototype = Error.prototype;
  function createChainableTypeChecker(validate) {
    if (process.env.NODE_ENV !== 'production') {
      var manualPropTypeCallCache = {};
      var manualPropTypeWarningCount = 0;
    }
    function checkType(isRequired, props, propName, componentName, location, propFullName, secret) {
      componentName = componentName || ANONYMOUS;
      propFullName = propFullName || propName;
      if (secret !== ReactPropTypesSecret$1) {
        if (throwOnDirectAccess) {
          var err = new Error(
            'Calling PropTypes validators directly is not supported by the `prop-types` package. ' +
            'Use `PropTypes.checkPropTypes()` to call them. ' +
            'Read more at http://fb.me/use-check-prop-types'
          );
          err.name = 'Invariant Violation';
          throw err;
        } else if (process.env.NODE_ENV !== 'production' && typeof console !== 'undefined') {
          var cacheKey = componentName + ':' + propName;
          if (
            !manualPropTypeCallCache[cacheKey] &&
            manualPropTypeWarningCount < 3
          ) {
            printWarning(
              'You are manually calling a React.PropTypes validation ' +
              'function for the `' + propFullName + '` prop on `' + componentName + '`. This is deprecated ' +
              'and will throw in the standalone `prop-types` package. ' +
              'You may be seeing this warning due to a third-party PropTypes ' +
              'library. See https://fb.me/react-warning-dont-call-proptypes ' + 'for details.'
            );
            manualPropTypeCallCache[cacheKey] = true;
            manualPropTypeWarningCount++;
          }
        }
      }
      if (props[propName] == null) {
        if (isRequired) {
          if (props[propName] === null) {
            return new PropTypeError('The ' + location + ' `' + propFullName + '` is marked as required ' + ('in `' + componentName + '`, but its value is `null`.'));
          }
          return new PropTypeError('The ' + location + ' `' + propFullName + '` is marked as required in ' + ('`' + componentName + '`, but its value is `undefined`.'));
        }
        return null;
      } else {
        return validate(props, propName, componentName, location, propFullName);
      }
    }
    var chainedCheckType = checkType.bind(null, false);
    chainedCheckType.isRequired = checkType.bind(null, true);
    return chainedCheckType;
  }
  function createPrimitiveTypeChecker(expectedType) {
    function validate(props, propName, componentName, location, propFullName, secret) {
      var propValue = props[propName];
      var propType = getPropType(propValue);
      if (propType !== expectedType) {
        var preciseType = getPreciseType(propValue);
        return new PropTypeError(
          'Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + preciseType + '` supplied to `' + componentName + '`, expected ') + ('`' + expectedType + '`.'),
          {expectedType: expectedType}
        );
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }
  function createAnyTypeChecker() {
    return createChainableTypeChecker(emptyFunctionThatReturnsNull);
  }
  function createArrayOfTypeChecker(typeChecker) {
    function validate(props, propName, componentName, location, propFullName) {
      if (typeof typeChecker !== 'function') {
        return new PropTypeError('Property `' + propFullName + '` of component `' + componentName + '` has invalid PropType notation inside arrayOf.');
      }
      var propValue = props[propName];
      if (!Array.isArray(propValue)) {
        var propType = getPropType(propValue);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected an array.'));
      }
      for (var i = 0; i < propValue.length; i++) {
        var error = typeChecker(propValue, i, componentName, location, propFullName + '[' + i + ']', ReactPropTypesSecret$1);
        if (error instanceof Error) {
          return error;
        }
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }
  function createElementTypeChecker() {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];
      if (!isValidElement(propValue)) {
        var propType = getPropType(propValue);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected a single ReactElement.'));
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }
  function createElementTypeTypeChecker() {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];
      if (!ReactIs$1.isValidElementType(propValue)) {
        var propType = getPropType(propValue);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected a single ReactElement type.'));
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }
  function createInstanceTypeChecker(expectedClass) {
    function validate(props, propName, componentName, location, propFullName) {
      if (!(props[propName] instanceof expectedClass)) {
        var expectedClassName = expectedClass.name || ANONYMOUS;
        var actualClassName = getClassName(props[propName]);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + actualClassName + '` supplied to `' + componentName + '`, expected ') + ('instance of `' + expectedClassName + '`.'));
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }
  function createEnumTypeChecker(expectedValues) {
    if (!Array.isArray(expectedValues)) {
      if (process.env.NODE_ENV !== 'production') {
        if (arguments.length > 1) {
          printWarning(
            'Invalid arguments supplied to oneOf, expected an array, got ' + arguments.length + ' arguments. ' +
            'A common mistake is to write oneOf(x, y, z) instead of oneOf([x, y, z]).'
          );
        } else {
          printWarning('Invalid argument supplied to oneOf, expected an array.');
        }
      }
      return emptyFunctionThatReturnsNull;
    }
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];
      for (var i = 0; i < expectedValues.length; i++) {
        if (is(propValue, expectedValues[i])) {
          return null;
        }
      }
      var valuesString = JSON.stringify(expectedValues, function replacer(key, value) {
        var type = getPreciseType(value);
        if (type === 'symbol') {
          return String(value);
        }
        return value;
      });
      return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of value `' + String(propValue) + '` ' + ('supplied to `' + componentName + '`, expected one of ' + valuesString + '.'));
    }
    return createChainableTypeChecker(validate);
  }
  function createObjectOfTypeChecker(typeChecker) {
    function validate(props, propName, componentName, location, propFullName) {
      if (typeof typeChecker !== 'function') {
        return new PropTypeError('Property `' + propFullName + '` of component `' + componentName + '` has invalid PropType notation inside objectOf.');
      }
      var propValue = props[propName];
      var propType = getPropType(propValue);
      if (propType !== 'object') {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected an object.'));
      }
      for (var key in propValue) {
        if (has(propValue, key)) {
          var error = typeChecker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret$1);
          if (error instanceof Error) {
            return error;
          }
        }
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }
  function createUnionTypeChecker(arrayOfTypeCheckers) {
    if (!Array.isArray(arrayOfTypeCheckers)) {
      process.env.NODE_ENV !== 'production' ? printWarning('Invalid argument supplied to oneOfType, expected an instance of array.') : void 0;
      return emptyFunctionThatReturnsNull;
    }
    for (var i = 0; i < arrayOfTypeCheckers.length; i++) {
      var checker = arrayOfTypeCheckers[i];
      if (typeof checker !== 'function') {
        printWarning(
          'Invalid argument supplied to oneOfType. Expected an array of check functions, but ' +
          'received ' + getPostfixForTypeWarning(checker) + ' at index ' + i + '.'
        );
        return emptyFunctionThatReturnsNull;
      }
    }
    function validate(props, propName, componentName, location, propFullName) {
      var expectedTypes = [];
      for (var i = 0; i < arrayOfTypeCheckers.length; i++) {
        var checker = arrayOfTypeCheckers[i];
        var checkerResult = checker(props, propName, componentName, location, propFullName, ReactPropTypesSecret$1);
        if (checkerResult == null) {
          return null;
        }
        if (checkerResult.data && has(checkerResult.data, 'expectedType')) {
          expectedTypes.push(checkerResult.data.expectedType);
        }
      }
      var expectedTypesMessage = (expectedTypes.length > 0) ? ', expected one of type [' + expectedTypes.join(', ') + ']': '';
      return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` supplied to ' + ('`' + componentName + '`' + expectedTypesMessage + '.'));
    }
    return createChainableTypeChecker(validate);
  }
  function createNodeChecker() {
    function validate(props, propName, componentName, location, propFullName) {
      if (!isNode(props[propName])) {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` supplied to ' + ('`' + componentName + '`, expected a ReactNode.'));
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }
  function invalidValidatorError(componentName, location, propFullName, key, type) {
    return new PropTypeError(
      (componentName || 'React class') + ': ' + location + ' type `' + propFullName + '.' + key + '` is invalid; ' +
      'it must be a function, usually from the `prop-types` package, but received `' + type + '`.'
    );
  }
  function createShapeTypeChecker(shapeTypes) {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];
      var propType = getPropType(propValue);
      if (propType !== 'object') {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type `' + propType + '` ' + ('supplied to `' + componentName + '`, expected `object`.'));
      }
      for (var key in shapeTypes) {
        var checker = shapeTypes[key];
        if (typeof checker !== 'function') {
          return invalidValidatorError(componentName, location, propFullName, key, getPreciseType(checker));
        }
        var error = checker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret$1);
        if (error) {
          return error;
        }
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }
  function createStrictShapeTypeChecker(shapeTypes) {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];
      var propType = getPropType(propValue);
      if (propType !== 'object') {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type `' + propType + '` ' + ('supplied to `' + componentName + '`, expected `object`.'));
      }
      var allKeys = assign({}, props[propName], shapeTypes);
      for (var key in allKeys) {
        var checker = shapeTypes[key];
        if (has(shapeTypes, key) && typeof checker !== 'function') {
          return invalidValidatorError(componentName, location, propFullName, key, getPreciseType(checker));
        }
        if (!checker) {
          return new PropTypeError(
            'Invalid ' + location + ' `' + propFullName + '` key `' + key + '` supplied to `' + componentName + '`.' +
            '\nBad object: ' + JSON.stringify(props[propName], null, '  ') +
            '\nValid keys: ' + JSON.stringify(Object.keys(shapeTypes), null, '  ')
          );
        }
        var error = checker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret$1);
        if (error) {
          return error;
        }
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }
  function isNode(propValue) {
    switch (typeof propValue) {
      case 'number':
      case 'string':
      case 'undefined':
        return true;
      case 'boolean':
        return !propValue;
      case 'object':
        if (Array.isArray(propValue)) {
          return propValue.every(isNode);
        }
        if (propValue === null || isValidElement(propValue)) {
          return true;
        }
        var iteratorFn = getIteratorFn(propValue);
        if (iteratorFn) {
          var iterator = iteratorFn.call(propValue);
          var step;
          if (iteratorFn !== propValue.entries) {
            while (!(step = iterator.next()).done) {
              if (!isNode(step.value)) {
                return false;
              }
            }
          } else {
            while (!(step = iterator.next()).done) {
              var entry = step.value;
              if (entry) {
                if (!isNode(entry[1])) {
                  return false;
                }
              }
            }
          }
        } else {
          return false;
        }
        return true;
      default:
        return false;
    }
  }
  function isSymbol(propType, propValue) {
    if (propType === 'symbol') {
      return true;
    }
    if (!propValue) {
      return false;
    }
    if (propValue['@@toStringTag'] === 'Symbol') {
      return true;
    }
    if (typeof Symbol === 'function' && propValue instanceof Symbol) {
      return true;
    }
    return false;
  }
  function getPropType(propValue) {
    var propType = typeof propValue;
    if (Array.isArray(propValue)) {
      return 'array';
    }
    if (propValue instanceof RegExp) {
      return 'object';
    }
    if (isSymbol(propType, propValue)) {
      return 'symbol';
    }
    return propType;
  }
  function getPreciseType(propValue) {
    if (typeof propValue === 'undefined' || propValue === null) {
      return '' + propValue;
    }
    var propType = getPropType(propValue);
    if (propType === 'object') {
      if (propValue instanceof Date) {
        return 'date';
      } else if (propValue instanceof RegExp) {
        return 'regexp';
      }
    }
    return propType;
  }
  function getPostfixForTypeWarning(value) {
    var type = getPreciseType(value);
    switch (type) {
      case 'array':
      case 'object':
        return 'an ' + type;
      case 'boolean':
      case 'date':
      case 'regexp':
        return 'a ' + type;
      default:
        return type;
    }
  }
  function getClassName(propValue) {
    if (!propValue.constructor || !propValue.constructor.name) {
      return ANONYMOUS;
    }
    return propValue.constructor.name;
  }
  ReactPropTypes.checkPropTypes = checkPropTypes;
  ReactPropTypes.resetWarningCache = checkPropTypes.resetWarningCache;
  ReactPropTypes.PropTypes = ReactPropTypes;
  return ReactPropTypes;
};

var ReactPropTypesSecret = ReactPropTypesSecret_1;
function emptyFunction() {}
function emptyFunctionWithReset() {}
emptyFunctionWithReset.resetWarningCache = emptyFunction;
var factoryWithThrowingShims = function() {
  function shim(props, propName, componentName, location, propFullName, secret) {
    if (secret === ReactPropTypesSecret) {
      return;
    }
    var err = new Error(
      'Calling PropTypes validators directly is not supported by the `prop-types` package. ' +
      'Use PropTypes.checkPropTypes() to call them. ' +
      'Read more at http://fb.me/use-check-prop-types'
    );
    err.name = 'Invariant Violation';
    throw err;
  }  shim.isRequired = shim;
  function getShim() {
    return shim;
  }  var ReactPropTypes = {
    array: shim,
    bigint: shim,
    bool: shim,
    func: shim,
    number: shim,
    object: shim,
    string: shim,
    symbol: shim,
    any: shim,
    arrayOf: getShim,
    element: shim,
    elementType: shim,
    instanceOf: getShim,
    node: shim,
    objectOf: getShim,
    oneOf: getShim,
    oneOfType: getShim,
    shape: getShim,
    exact: getShim,
    checkPropTypes: emptyFunctionWithReset,
    resetWarningCache: emptyFunction
  };
  ReactPropTypes.PropTypes = ReactPropTypes;
  return ReactPropTypes;
};

if (process.env.NODE_ENV !== 'production') {
  var ReactIs = reactIs.exports;
  var throwOnDirectAccess = true;
  propTypes.exports = factoryWithTypeCheckers(ReactIs.isElement, throwOnDirectAccess);
} else {
  propTypes.exports = factoryWithThrowingShims();
}
var PropTypes = propTypes.exports;

var css_248z$c = ".styles-module_rating-value__kV75c{color:#333;font-family:Lato,sans-serif;font-size:12px;font-style:normal;font-weight:500;line-height:15px;margin:0 1.5px}@media (max-width:430px){.styles-module_rating-value__kV75c{font-size:10px;margin-left:1px;margin-right:1px}}@media (max-width:350px){.styles-module_rating-value__kV75c{font-size:8px;margin-left:1px;margin-right:1px}}.styles-module_star-empty__H9ZQ-{margin:0 4px 0 0}.styles-module_star-empty__H9ZQ- path{fill:#ccc}@media (max-width:600px){.styles-module_star-empty__H9ZQ-{margin:0 2px 0 0;width:12px}}@media (max-width:420px){.styles-module_star-empty__H9ZQ-{margin:0 1px 0 0;width:9px}}@media (max-width:350px){.styles-module_star-empty__H9ZQ-{margin:0 1px 0 0;width:8px}}.styles-module_rating__VKBa1{display:flex}.styles-module_star-half-filled__rNU09{margin:0 4px 0 0;position:relative}.styles-module_star-half-filled__rNU09 path:first-child{fill:#ccc}.styles-module_star-half-filled__rNU09 path:last-child{fill:#ffc107;clip-path:inset(0 calc(100% - var(--clip-width-gold)) 0 0)}.styles-module_star-half-filled__rNU09:hover path:last-child{fill:#ffc107}@media (max-width:600px){.styles-module_star-half-filled__rNU09{margin:0 2px 0 0;width:12px}}@media (max-width:420px){.styles-module_star-half-filled__rNU09{margin:0 1px 0 0;width:9px}}@media (max-width:350px){.styles-module_star-half-filled__rNU09{margin:0 1px 0 0;width:8px}}.styles-module_star-filled__mAbRi{margin:0 4px 0 0}.styles-module_star-filled__mAbRi path{fill:#ffc107}@media (max-width:600px){.styles-module_star-filled__mAbRi{margin:0 2px 0 0;width:12px}}@media (max-width:420px){.styles-module_star-filled__mAbRi{margin:0 1px 0 0;width:9px}}@media (max-width:350px){.styles-module_star-filled__mAbRi{margin:0 1px 0 0;width:8px}}";
var styles$4 = {"rating-value":"styles-module_rating-value__kV75c","star-empty":"styles-module_star-empty__H9ZQ-","rating":"styles-module_rating__VKBa1","star-half-filled":"styles-module_star-half-filled__rNU09","star-filled":"styles-module_star-filled__mAbRi"};
styleInject(css_248z$c);

var Star = function (_a) {
    var filled = _a.filled, halfFilled = _a.halfFilled, _b = _a.clipWidth, clipWidth = _b === void 0 ? 0 : _b;
    var starClass = filled
        ? halfFilled
            ? styles$4['star-half-filled']
            : styles$4['star-filled']
        : styles$4['star-empty'];
    var starStyle = {
        '--clip-width-gold': "".concat(clipWidth, "%"),
        '--clip-width-grey': "".concat(100 - clipWidth, "%"),
    };
    var pathStart = "M13.2279 5.0701L9.00916 4.71854L7.36853 0.968536C7.30994 0.841583 7.2074 0.778107 7.06091 0.778107C6.91443 0.778107 6.81189 0.841583 6.7533 0.968536L5.09802 4.71854L0.879272 5.0701C0.732788 5.07986 0.635132 5.15311 0.586304 5.28983C0.547241 5.42654 0.581421 5.53885 0.688843 5.62674L3.89685 8.29276L2.93005 12.2625C2.90076 12.3992 2.9447 12.5115 3.06189 12.5994C3.17908 12.6775 3.30115 12.6824 3.4281 12.614L7.06091 10.5047L10.6791 12.614C10.806 12.6824 10.9281 12.6775 11.0453 12.5994C11.1722 12.5115 11.2162 12.3992 11.1771 12.2625L10.225 8.29276L13.4183 5.62674C13.5355 5.53885 13.5697 5.42654 13.5209 5.28983C13.472 5.15311 13.3744 5.07986 13.2279 5.0701Z";
    return (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "13", viewBox: "0 0 14 13", fill: "none", className: starClass, style: starStyle },
        React__default.createElement("path", { d: pathStart }),
        halfFilled && React__default.createElement("path", { d: pathStart })));
};
Star.propTypes = {
    filled: PropTypes.bool.isRequired,
    halfFilled: PropTypes.bool,
    clipWidth: PropTypes.number,
};

var SponsoredProductRating = function (_a) {
    var rating = _a.rating, maxRating = _a.maxRating, _b = _a.showRatingValue, showRatingValue = _b === void 0 ? false : _b;
    var fullStars = Math.floor(rating);
    var hddf = rating - fullStars;
    var hasHalfStar = hddf !== 0;
    var clipWidth = "".concat(hddf * 100, "%"); // Calculate clip-path width dynamically
    var clipWidthNumber = parseFloat(clipWidth || '0');
    return (React__default.createElement("div", { className: styles$4['rating'] },
        Array.from({ length: maxRating }, function (_, i) {
            if (i < fullStars) {
                return React__default.createElement(Star, { key: i, filled: true });
            }
            if (i === fullStars && hasHalfStar) {
                return React__default.createElement(Star, { key: i, filled: true, halfFilled: true, clipWidth: clipWidthNumber });
            }
            return React__default.createElement(Star, { key: i, filled: false });
        }),
        showRatingValue && React__default.createElement("p", { className: styles$4['rating-value'] }, rating.toFixed(1))));
};
SponsoredProductRating.propTypes = {
    rating: PropTypes.number.isRequired,
    maxRating: PropTypes.number.isRequired,
};

var css_248z$b = ".styles-module_sponsored-product-title__q1bUQ{color:#767676;font-family:Lato;font-size:12px;font-weight:700;margin-bottom:8px;text-transform:uppercase}@media (min-width:0px) and (max-width:1024px){.styles-module_sponsored-product-title__q1bUQ{gap:7px}}.styles-module_display-name__sd-rz{color:#4a4a4a;font-family:Lato;font-size:16px;font-weight:700}.styles-module_seller-name__Nm764{color:#333;display:flex;font-size:13px;margin-bottom:4px;margin-top:7px}.styles-module_seller-container__gPoqv{align-items:center;display:flex;justify-content:flex-start}.styles-module_seller-icon__I8sSX{padding-left:5px}.styles-module_truncate-3-lines__Xf6Xj{display:-webkit-box;font-size:14px;width:100%;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;padding-bottom:2px;text-overflow:ellipsis}";
var styles$3 = {"sponsored-product-title":"styles-module_sponsored-product-title__q1bUQ","display-name":"styles-module_display-name__sd-rz","seller-name":"styles-module_seller-name__Nm764","seller-container":"styles-module_seller-container__gPoqv","seller-icon":"styles-module_seller-icon__I8sSX","truncate-3-lines":"styles-module_truncate-3-lines__Xf6Xj"};
styleInject(css_248z$b);

var sodimacSellerIcon = React__default.forwardRef(function () {
    return (React__default.createElement("svg", { width: "18", height: "13", viewBox: "0 0 18 13", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
        React__default.createElement("path", { d: "M13.6613 1.23885H10.2882V0H7.7159V1.23885H4.35718L0 6.03766V13H18V6.03766L13.6613 1.23885Z", fill: "#1A1919" }),
        React__default.createElement("path", { d: "M1.21826 11.9495H8.25586V7.35352H1.21826V11.9495Z", fill: "#D62331" }),
        React__default.createElement("path", { d: "M9.60938 11.9495H16.647V7.35352H9.60938V11.9495Z", fill: "#FFFFFE" }),
        React__default.createElement("path", { d: "M16.3769 6.04008L13.1363 2.49561L6.36182 2.49463L9.6158 6.04008H16.3769Z", fill: "#0F66A3" }),
        React__default.createElement("path", { d: "M7.98493 6.04008L4.84733 2.49463L1.62402 6.04008H7.98493Z", fill: "#F9DB1D" })));
});

var falabellaSellerIcon = React__default.forwardRef(function () {
    return (React__default.createElement("svg", { width: "13", height: "18", viewBox: "0 0 13 18", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
        React__default.createElement("path", { d: "M0.89209 0H12.8999V18H0.89209V0Z", fill: "#ADD500" }),
        React__default.createElement("path", { d: "M8.80576 9.68903C8.80589 9.57345 8.84028 9.4605 8.90459 9.36446C8.96889 9.26842 9.06022 9.1936 9.16704 9.14945C9.27386 9.10531 9.39137 9.09383 9.50472 9.11646C9.61806 9.13908 9.72215 9.1948 9.80384 9.27657C9.88552 9.35835 9.94112 9.4625 9.96362 9.57587C9.98613 9.68924 9.97453 9.80674 9.93027 9.91351C9.88601 10.0203 9.81109 10.1115 9.71498 10.1757C9.61887 10.2399 9.50586 10.2742 9.39028 10.2742C9.31346 10.2742 9.23741 10.2591 9.16645 10.2296C9.09549 10.2002 9.03103 10.1571 8.97674 10.1028C8.92245 10.0484 8.87941 9.98391 8.85008 9.91292C8.82074 9.84193 8.80568 9.76585 8.80576 9.68903ZM4.4039 7.66299H6.34147L6.45774 7.12285L7.05089 4.23598C7.23392 3.41796 7.40225 2.70502 8.02479 2.10835C8.44416 1.69504 9.00846 1.46201 9.59726 1.45898C9.59726 1.45898 9.82917 1.46058 9.97515 1.48038L9.93169 1.67459C9.76169 1.64993 9.58903 1.64993 9.41903 1.67459C9.19575 1.72633 8.99387 1.84568 8.84089 2.01636C8.51701 2.34089 8.39915 2.83438 8.1928 3.71661L7.46772 7.15032L7.35816 7.66139H9.5139L9.4318 7.95684H7.29492L6.05656 13.7462C5.87449 14.5636 5.70519 15.2769 5.08297 15.8739C4.66044 16.2886 4.09231 16.5213 3.50026 16.5223L3.29712 16.5181L3.33258 16.3287C3.39508 16.3312 3.45767 16.3286 3.51976 16.321C3.66539 16.3093 3.80697 16.2673 3.93544 16.1977C4.06392 16.1281 4.17647 16.0325 4.26591 15.917C4.57223 15.5244 4.7067 15.1469 4.914 14.265L6.27631 7.95716H4.32245L4.4039 7.66299Z", fill: "white" })));
});

var linioSellerIcon = React__default.forwardRef(function () {
    return (React__default.createElement("svg", { version: "1.1", id: "Capa_1", xmlns: "http://www.w3.org/2000/svg", xmlnsXlink: "http://www.w3.org/1999/xlink", x: "0px", y: "0px", viewBox: "0 0 32 32", xmlSpace: "preserve" },
        React__default.createElement("style", { type: "text/css" }, ".st0{fill:url(#SVGID_1_);}\n      .st1{fill:#FFFFFF;}"),
        React__default.createElement("linearGradient", { id: "SVGID_1_", gradientUnits: "userSpaceOnUse", x1: "15.7584", y1: "1.4884", x2: "16.2521", y2: "31.143" },
            React__default.createElement("stop", { offset: "0", style: { stopColor: '#FF6600' } }),
            React__default.createElement("stop", { offset: "0.5", style: { stopColor: '#F30078' } }),
            React__default.createElement("stop", { offset: "0.7485", style: { stopColor: '#DA008F' } }),
            React__default.createElement("stop", { offset: "1", style: { stopColor: '#C400A3' } })),
        React__default.createElement("circle", { className: "st0", cx: "16", cy: "16", r: "15.77" }),
        React__default.createElement("g", null,
            React__default.createElement("path", { className: "st1", d: "M10.27,11.51l5.26-5.25l-5.7,2.07L4.29,20.19c-0.34,0.72-0.02,1.59,0.7,1.93l5.27,2.45L10.27,11.51z" }),
            React__default.createElement("path", { className: "st1", d: "M19.65,5.88c-0.56-0.56-1.48-0.56-2.04,0l-6.19,6.21V25.1c0,0.8,0.65,1.45,1.44,1.45l11.54,0\n        c0.79,0,1.44-0.65,1.44-1.45V12.08L19.65,5.88z M18.63,6.84c0.5,0,0.9,0.4,0.9,0.9c0,0.5-0.4,0.9-0.9,0.9c-0.5,0-0.9-0.4-0.9-0.9\n        C17.73,7.25,18.14,6.84,18.63,6.84z M22.82,24.46H14.5V11.97h2.77v9.71h5.55V24.46z" }))));
});

var tottusSellerIcon = React__default.forwardRef(function () {
    return (React__default.createElement("svg", { width: "18", height: "18", viewBox: "0 0 18 18", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
        React__default.createElement("path", { d: "M6.22217 9.00026C6.22217 7.46599 7.46599 6.22217 9.00026 6.22217C10.5328 6.22217 11.7749 7.46599 11.7749 9.00026C11.7749 10.5311 10.5328 11.7766 9.00026 11.7766C7.46599 11.7766 6.22217 10.5311 6.22217 9.00026Z", fill: "#3F752F" }),
        React__default.createElement("path", { d: "M0.989746 3.21256C0.989746 1.98582 1.98241 0.991454 3.20914 0.991454C4.43758 0.991454 5.43195 1.98582 5.43195 3.21256C5.43195 4.441 4.43758 5.43366 3.20914 5.43366C1.98241 5.43366 0.989746 4.441 0.989746 3.21256ZM12.5439 3.21256C12.5439 1.98582 13.5383 0.989746 14.765 0.989746C15.9918 0.989746 16.9895 1.98582 16.9895 3.21256C16.9895 4.441 15.9918 5.43366 14.765 5.43537C13.5383 5.43537 12.5439 4.441 12.5439 3.21256ZM12.5439 14.7667C12.5439 13.5417 13.5383 12.5491 14.765 12.5491C15.9969 12.5491 16.9895 13.5417 16.9895 14.7667C16.9895 15.9918 15.9969 16.9896 14.765 16.9896C13.5383 16.9896 12.5439 15.9918 12.5439 14.7667ZM0.989746 14.7667C0.989746 13.5417 1.98241 12.5491 3.20914 12.5491C4.43758 12.5491 5.43024 13.5417 5.43024 14.7667C5.43024 15.9918 4.43758 16.9896 3.20914 16.9896C1.98241 16.9896 0.989746 15.9918 0.989746 14.7667Z", fill: "#DAE254" }),
        React__default.createElement("path", { d: "M2.4996 6.49946C1.11739 6.49946 0 7.62026 0 9.00077C0 10.3813 1.11739 11.5004 2.4996 11.5004C3.87839 11.5004 4.99919 10.3813 4.99919 9.00077C4.99919 7.62026 3.87839 6.49946 2.4996 6.49946Z", fill: "#75C045" }),
        React__default.createElement("path", { d: "M15.5004 6.49946C14.1182 6.49946 12.9974 7.62026 12.9974 9.00077C12.9974 10.3813 14.1182 11.5004 15.5004 11.5004C16.8792 11.5004 18 10.3813 18 9.00077C18 7.61856 16.8792 6.49946 15.5004 6.49946Z", fill: "#75C045" }),
        React__default.createElement("path", { d: "M8.99991 12.9991C7.61771 12.9991 6.49861 14.1199 6.49861 15.5004C6.49861 16.8809 7.61771 18 8.99991 18C10.377 18 11.4978 16.8809 11.4978 15.5004C11.4978 14.1199 10.377 12.9991 8.99991 12.9991Z", fill: "#75C045" }),
        React__default.createElement("path", { d: "M8.99991 0C7.61941 0 6.49861 1.1208 6.49861 2.50301C6.49861 3.88181 7.61941 4.99919 8.99991 4.99919C10.377 4.99919 11.4978 3.88181 11.4978 2.50301C11.4978 1.1208 10.377 0 8.99991 0Z", fill: "#75C045" })));
});

var sellerIcons = {
    falabella: falabellaSellerIcon,
    sodimac: sodimacSellerIcon,
    tottus: tottusSellerIcon,
    linio: linioSellerIcon,
};
var useSellerIcon = function (sellerName) {
    var Icon = useMemo(function () {
        return sellerIcons[sellerName === null || sellerName === void 0 ? void 0 : sellerName.toLowerCase()] || null;
    }, [sellerName]);
    return Icon;
};

var constructQuery = function (queryParams) {
    return Object.keys(queryParams)
        .reduce(function (query, key) {
        return queryParams[key] ? "".concat(query).concat(key, "=").concat(encodeURIComponent(queryParams[key]), "&") : query;
    }, '?')
        .slice(0, -1);
};
var capitalizeText = function (text) {
    var _a, _b;
    return (_b = (_a = text === null || text === void 0 ? void 0 : text.split(' ')) === null || _a === void 0 ? void 0 : _a.map(function (word) { return (word === null || word === void 0 ? void 0 : word.charAt(0).toUpperCase()) + (word === null || word === void 0 ? void 0 : word.slice(1).toLowerCase()); })) === null || _b === void 0 ? void 0 : _b.join(' ');
};

// SponsoredProductTitle.tsx
var SponsoredProductTitle = function (props) {
    var brandName = props.brandName, productName = props.productName, sellerName = props.sellerName;
    var SellerIcon = useSellerIcon(sellerName);
    return (React__default.createElement("div", null,
        React__default.createElement("div", { className: styles$3['sponsored-product-title'] }, brandName),
        React__default.createElement("span", { className: styles$3['display-name'] }, productName),
        React__default.createElement("div", { className: styles$3['seller-container'] },
            React__default.createElement("span", { className: styles$3['seller-name'] },
                "Por ",
                capitalizeText(sellerName),
                " "),
            SellerIcon && (React__default.createElement("div", { className: styles$3['seller-icon'] },
                React__default.createElement(SellerIcon, null))))));
};

var __assign = function() {
  __assign = Object.assign || function __assign(t) {
      for (var s, i = 1, n = arguments.length; i < n; i++) {
          s = arguments[i];
          for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
      return t;
  };
  return __assign.apply(this, arguments);
};
function __rest(s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
      t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function")
      for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
          if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
              t[p[i]] = s[p[i]];
      }
  return t;
}
function __awaiter(thisArg, _arguments, P, generator) {
  function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
  return new (P || (P = Promise))(function (resolve, reject) {
      function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
      function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
      function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
      step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
}
function __generator(thisArg, body) {
  var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
  return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
  function verb(n) { return function (v) { return step([n, v]); }; }
  function step(op) {
      if (f) throw new TypeError("Generator is already executing.");
      while (g && (g = 0, op[0] && (_ = 0)), _) try {
          if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
          if (y = 0, t) op = [op[0] & 2, t.value];
          switch (op[0]) {
              case 0: case 1: t = op; break;
              case 4: _.label++; return { value: op[1], done: false };
              case 5: _.label++; y = op[1]; op = [0]; continue;
              case 7: op = _.ops.pop(); _.trys.pop(); continue;
              default:
                  if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                  if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                  if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                  if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                  if (t[2]) _.ops.pop();
                  _.trys.pop(); continue;
          }
          op = body.call(thisArg, _);
      } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
      if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
  }
}
function __spreadArray(to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
      if (ar || !(i in from)) {
          if (!ar) ar = Array.prototype.slice.call(from, 0, i);
          ar[i] = from[i];
      }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
}
typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
  var e = new Error(message);
  return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var css_248z$a = ".styles-module_price-list__Ov573{display:flex;flex-direction:column;font-family:Lato,sans-serif;font-weight:400;gap:4px;margin-bottom:0;margin-right:7px}@media (min-width:0px) and (max-width:1024px){.styles-module_price-list__Ov573{margin-right:-20px}}.styles-module_price-item__S4Je8{align-items:center;display:flex}.styles-module_price-item__S4Je8 .styles-module_symbol__hllLS{margin-right:5px}.styles-module_price-item__S4Je8 .styles-module_price__OOVUR{align-items:center;display:flex;margin-right:5px}.styles-module_price-item__S4Je8 .styles-module_price__OOVUR .styles-module_dollar__rrFcj{margin-right:3px}.styles-module_price-item__S4Je8 .styles-module_crossed__fqApY{text-decoration:line-through}.styles-module_price-item__S4Je8 .styles-module_label__bGGOU{font-style:italic;margin-left:10px}.styles-module_cmrPrice__g0LxV,.styles-module_eventPrice__ZiYNo,.styles-module_normalPrice__7xhvD{font-size:1.8rem}.styles-module_internetPrice__opZYb{font-size:1.4rem!important}.styles-module_cmrPriceBrand__mrcto,.styles-module_eventPriceBrand__55q0r,.styles-module_normalPriceBrand__4CLB4{font-size:1.6rem}@media (max-width:320px){.styles-module_cmrPriceBrand__mrcto,.styles-module_eventPriceBrand__55q0r,.styles-module_normalPriceBrand__4CLB4{font-size:1.4rem}}.styles-module_internetPriceBrand__3830z{font-size:1.4rem!important}";
var styles$2 = {"price-list":"styles-module_price-list__Ov573","price-item":"styles-module_price-item__S4Je8","symbol":"styles-module_symbol__hllLS","price":"styles-module_price__OOVUR","dollar":"styles-module_dollar__rrFcj","crossed":"styles-module_crossed__fqApY","label":"styles-module_label__bGGOU","eventPrice":"styles-module_eventPrice__ZiYNo","cmrPrice":"styles-module_cmrPrice__g0LxV","normalPrice":"styles-module_normalPrice__7xhvD","internetPrice":"styles-module_internetPrice__opZYb","normalPriceBrand":"styles-module_normalPriceBrand__4CLB4","cmrPriceBrand":"styles-module_cmrPriceBrand__mrcto","eventPriceBrand":"styles-module_eventPriceBrand__55q0r","internetPriceBrand":"styles-module_internetPriceBrand__3830z"};
styleInject(css_248z$a);

var css_248z$9 = ".styles-module_rating-styles__scIMu{display:flex;flex-direction:row;height:21px}.styles-module_cmrIcon__jWKGW{height:20px;width:65px}@media (max-width:420px){.styles-module_cmrIcon__jWKGW{height:20px;width:55px}}@media (max-width:350px){.styles-module_cmrIcon__jWKGW{height:20px;width:45px}}";
var pricesStyles = {"rating-styles":"styles-module_rating-styles__scIMu","cmrIcon":"styles-module_cmrIcon__jWKGW"};
styleInject(css_248z$9);

var CmrIcon = React__default.forwardRef(function (props, ref) {
    var _a = props.showDebitCardIcon, showDebitCardIcon = _a === void 0 ? false : _a;
    var width = showDebitCardIcon ? 65 : 43;
    var viewBox = showDebitCardIcon ? '0 0 65 20' : '0 0 43 20';
    return (React__default.createElement("svg", { width: width, height: 20, className: pricesStyles['cmrIcon'], viewBox: viewBox, fill: "none", xmlns: "http://www.w3.org/2000/svg" },
        showDebitCardIcon && React__default.createElement(DebitIcon, null),
        React__default.createElement("rect", { x: "21.5", y: "6.5", width: "21", height: "13", rx: "1.5", fill: "#3DAE2B" }),
        React__default.createElement("path", { d: "M40.2082 15.3559L39.5812 14.73L38.622 13.7744L38.7467 13.7464C39.601 13.5681 40.1755 13.0297 40.1755 11.9972C40.1755 10.7921 39.1651 10.122 38.1547 10.1197H36.0453C36.0255 10.1209 36.0057 10.1255 35.9871 10.1349C35.9719 10.1442 35.9649 10.1675 35.9649 10.2071V15.4607C35.9719 15.4969 35.9824 15.519 35.9975 15.526C36.022 15.5353 36.0465 15.5365 36.071 15.5318H36.0838L36.9602 15.4048C36.9742 15.4036 36.9893 15.399 37.0021 15.392C37.0184 15.3827 37.0243 15.3582 37.0243 15.3209V11.1523H38.1955C38.7013 11.1523 39.1418 11.5112 39.1418 11.9878C39.1418 12.4645 38.7899 12.7057 38.2899 12.8281C37.7888 12.9493 37.5475 13.3141 37.5452 13.6987C37.5499 13.9632 37.6513 14.2161 37.8307 14.4107C37.9181 14.5238 38.0137 14.6298 38.1174 14.73C38.1197 14.7324 38.1221 14.7347 38.1256 14.737L39.5287 16.1623C39.6161 16.2183 39.7187 16.2474 39.8224 16.2474C40.1173 16.2486 40.3562 16.0132 40.3608 15.7195C40.3632 15.5831 40.3084 15.4514 40.2093 15.3582M28.821 13.9923C28.7324 13.9364 28.6299 13.9061 28.525 13.9061C28.3793 13.9061 28.2418 13.9655 28.1416 14.0704C28.1113 14.1077 28.0425 14.1718 28.0157 14.1975C27.6509 14.5366 27.1696 14.7254 26.6697 14.7254C25.5742 14.7242 24.6861 13.8362 24.685 12.7395C24.6885 11.6464 25.5765 10.7618 26.6697 10.7618C27.0333 10.7618 27.5297 10.8271 27.9668 11.242C27.9714 11.2467 27.9761 11.2501 27.9819 11.2525C28.0052 11.2641 28.0309 11.2665 28.0553 11.2595L28.9923 11.122C29.0867 11.108 29.0413 11.0357 29.0413 11.0357C28.8746 10.7327 28.18 9.6687 26.5764 9.68385C24.8819 9.70016 23.614 11.0287 23.5977 12.757C23.6 13.4831 23.8599 14.1846 24.3319 14.7359C24.9146 15.4235 25.7688 15.8209 26.6697 15.8232C27.5134 15.8209 28.3175 15.4759 28.9002 14.8652C28.9329 14.8244 28.9608 14.7813 28.9853 14.7359C29.0401 14.6473 29.0692 14.5447 29.0716 14.441C29.0692 14.2604 28.976 14.0926 28.8245 13.9947M34.6212 9.91693C34.183 9.91693 33.9325 10.2269 33.739 10.6336L32.5421 13.1801C32.5421 13.1801 32.5305 13.2104 32.5118 13.2104C32.4932 13.2104 32.4792 13.1801 32.4792 13.1801L31.456 11.4389L31.1401 10.897C31.1215 10.8667 31.1029 10.8492 31.0842 10.8481C31.0749 10.8469 31.0644 10.8469 31.0551 10.8481L29.6566 11.045C29.6438 11.0485 29.631 11.0532 29.6193 11.0602C29.6065 11.0683 29.5995 11.0928 29.5995 11.1313V15.7206C29.6065 16.0073 29.8419 16.2369 30.1286 16.2369C30.4153 16.2357 30.6507 16.0073 30.6588 15.7206V12.1941L32.182 14.737L32.5083 15.2789C32.5142 15.2906 32.5246 15.2976 32.5363 15.2988C32.5515 15.2988 32.5608 15.2789 32.5608 15.2789L32.8253 14.737L34.3602 11.6266V15.6997C34.366 15.7346 34.3765 15.7568 34.394 15.7649C34.4138 15.7719 34.4347 15.7742 34.4557 15.7719H34.4627L35.3473 15.6449C35.3647 15.6437 35.3811 15.6379 35.3962 15.6309C35.4137 15.6204 35.4172 15.5983 35.4195 15.5656V10.7187C35.4114 10.2782 35.0559 9.92392 34.6154 9.91693", fill: "#FEFEFF" }),
        React__default.createElement("path", { d: "M10.0984 0.00761822C10.9967 0.00761822 11.8852 0.464711 12.5906 1.16994L16.9769 6.58432C17.2738 6.31006 17.6649 6.1577 18.0702 6.15987C19.1397 6.18817 19.9946 7.05991 20 8.13082V16.5707C20 18.4024 18.4213 20 16.5825 20H8.1296C6.52377 20 5.52581 18.1205 6.56494 16.9919L1.14606 12.6049C-0.0978668 11.3631 -0.542126 9.5293 0.903343 8.08293L8.09818 0.889155C8.61612 0.331937 9.33994 0.0108833 10.1006 0", fill: "#EF044E" }),
        React__default.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M5.03809 8.63633L4.72524 8.57723H4.72583C4.68643 8.56435 4.68643 8.54972 4.69878 8.5152L4.93224 7.9669C4.95084 7.9224 4.97243 7.92534 5.00306 7.92951C5.0131 7.93087 5.02412 7.93237 5.03633 7.93237L5.39857 7.99908C5.42562 8.00903 5.44326 8.02893 5.42562 8.05584L5.11513 8.60941C5.08749 8.63633 5.07514 8.63633 5.03809 8.63633H5.03809ZM8.84642 8.96205C8.57673 8.86959 8.21435 8.86374 8.01032 8.86374C7.6096 8.85672 7.23415 9.05684 7.01793 9.39273V8.99248C6.99619 8.88832 6.90159 8.81459 6.79465 8.81927H6.05022C5.92977 8.8181 5.83106 8.91406 5.82929 9.03402V9.03636V11.26C5.82929 11.6204 5.66066 12.1447 5.08485 12.1447C4.26109 12.1447 4.32337 11.1564 4.32337 11.1564V8.99189C4.31397 8.88422 4.22055 8.80347 4.11244 8.80932H3.35331C3.2452 8.80113 3.15119 8.88188 3.14355 8.98955C3.14238 9.00359 3.14355 9.01763 3.14473 9.03167L3.14473 9.03168V11.0628C3.14473 12.0979 3.33569 12.748 4.01079 13.0026C4.2799 13.0974 4.56427 13.1419 4.84924 13.1337C5.25055 13.1407 5.626 12.9394 5.84163 12.6023V12.9704C5.86043 13.0757 5.95562 13.1501 6.06255 13.1436H6.80699C6.92744 13.1424 7.02498 13.0459 7.02792 12.9259V10.7368C7.02792 10.3735 7.19185 9.8474 7.77236 9.8474C8.59377 9.8474 8.53384 10.8357 8.53384 10.8357V13.0049C8.53384 13.0939 8.56086 13.1307 8.62314 13.143H9.54385C9.69544 13.143 9.71248 13.1185 9.71248 12.9997V10.9042C9.71248 9.86905 9.52152 9.21893 8.84642 8.96205ZM10.1635 13.1608H10.9872C11.1411 13.1608 11.1634 13.1362 11.1634 13.0175V8.96052C11.1634 8.84174 11.1411 8.81716 10.9872 8.81716H10.1635C10.0448 8.81716 9.99254 8.84174 9.99254 8.96052V13.0175C9.99254 13.1362 10.0442 13.1608 10.1635 13.1608ZM18.4238 12.9458C17.8808 13.1623 17.2984 13.2641 16.7143 13.2448C15.2628 13.2448 14.8332 12.6491 14.8332 11.9031C14.8332 11.0751 15.3815 10.4653 16.5356 10.4653H17.4242V10.3366C17.4242 10.0399 17.3272 9.76605 16.7219 9.76605C16.3382 9.77132 15.9568 9.82281 15.5854 9.91936C15.4467 9.95155 15.4067 9.91936 15.3821 9.81579L15.2528 9.25228C15.2204 9.13115 15.2528 9.10131 15.3345 9.06971C15.8357 8.90177 16.3617 8.81809 16.89 8.82277C18.0588 8.82277 18.5948 9.31664 18.5948 10.305V12.6328C18.6142 12.7632 18.5442 12.8902 18.4238 12.9441V12.9458ZM16.776 11.2442C16.481 11.2442 16.0314 11.2986 16.0314 11.8124C16.0314 12.1904 16.307 12.3191 16.736 12.3191C16.9634 12.322 17.1903 12.2869 17.4059 12.2155V11.2448H16.7754L16.776 11.2442ZM14.4283 12.0191C14.567 11.9647 14.5969 11.9746 14.6392 12.0934L14.8649 12.6961C14.9219 12.8026 14.9295 12.8564 14.8055 12.9208C14.3431 13.1479 13.8324 13.2608 13.317 13.2497C12.0518 13.2497 11.3813 12.5083 11.3813 11.0653C11.3813 9.62225 12.1552 8.84164 13.3887 8.84164C13.8289 8.83404 14.2655 8.92708 14.6639 9.11316C14.8026 9.17753 14.8079 9.20971 14.7532 9.33786L14.5276 9.95813C14.4729 10.0769 14.4306 10.0693 14.2796 10.0272C14.0216 9.94058 13.7525 9.89259 13.4804 9.88382C12.8428 9.88382 12.5995 10.4298 12.5995 11.0647C12.5995 11.6996 12.7905 12.204 13.5027 12.204C13.8206 12.197 14.135 12.1338 14.4306 12.0161L14.4283 12.0191Z", fill: "#FEFEFF" })));
});
var DebitIcon = function () { return (React__default.createElement(React__default.Fragment, null,
    React__default.createElement("rect", { x: "44", y: "6.5", width: "21", height: "13", rx: "1.5", fill: "#3F3F3F" }),
    React__default.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M50.6364 17.054C50.6876 17.0307 50.7203 16.9782 50.7156 16.9211L50.7179 15.7196C50.7179 15.483 50.6492 15.3024 50.5152 15.18C50.3811 15.0576 50.169 14.9959 49.88 14.9959C49.7495 14.997 49.619 15.0052 49.4896 15.0215C49.3591 15.039 49.2297 15.0693 49.105 15.1136C49.0689 15.1229 49.0584 15.1509 49.0689 15.1928L49.1272 15.4224C49.1342 15.4469 49.1423 15.4632 49.1551 15.4679C49.1773 15.4714 49.1994 15.4702 49.2204 15.4632C49.3101 15.4376 49.401 15.4178 49.4931 15.4073C49.5957 15.391 49.6982 15.3816 49.8019 15.3816C49.8754 15.3793 49.9488 15.3898 50.0187 15.4108C50.0677 15.4236 50.1119 15.4481 50.1492 15.4819C50.1795 15.5133 50.2017 15.5529 50.2145 15.5949C50.2261 15.6415 50.2308 15.6893 50.2296 15.7371V15.8047H49.7635C49.6201 15.8023 49.478 15.8222 49.3416 15.8641C49.2379 15.8979 49.1412 15.9515 49.0584 16.0226C48.9873 16.0855 48.9337 16.1636 48.8999 16.251C48.8685 16.3384 48.8521 16.4305 48.8521 16.5226C48.8521 16.6111 48.8661 16.6985 48.8941 16.7813C48.9232 16.8652 48.9745 16.9386 49.0421 16.9945C49.1272 17.0633 49.2251 17.1123 49.33 17.1402C49.478 17.1798 49.6318 17.1973 49.7856 17.1938C49.8544 17.1938 49.9243 17.1938 49.9977 17.1892C50.07 17.1833 50.1457 17.1764 50.2192 17.167C50.2937 17.1554 50.3672 17.1402 50.4394 17.1216C50.507 17.1041 50.5723 17.082 50.6364 17.054ZM49.8229 16.1625H50.2238L50.2168 16.7486C50.1784 16.7626 50.1376 16.7731 50.0968 16.7801C50.0905 16.7813 50.0843 16.7824 50.0781 16.7836C50.0407 16.7905 50.0053 16.7971 49.9733 16.8011C49.9552 16.8034 49.9365 16.8045 49.9184 16.8057C49.8992 16.8069 49.8805 16.808 49.8637 16.8104C49.8311 16.8139 49.8019 16.8139 49.7845 16.8139C49.6737 16.8186 49.5642 16.7941 49.4663 16.744C49.3836 16.695 49.3369 16.6018 49.3451 16.5062C49.3416 16.4398 49.3591 16.3734 49.3929 16.3163C49.4243 16.2732 49.4651 16.2382 49.5129 16.2137C49.563 16.1881 49.6166 16.1741 49.6726 16.1694C49.7227 16.1648 49.7728 16.1625 49.8229 16.1625Z", fill: "#FEFEFF" }),
    React__default.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M53.7468 17.054C53.7993 17.0307 53.8307 16.9782 53.8261 16.9211L53.8331 15.7196C53.8331 15.483 53.7631 15.3024 53.6291 15.18C53.4951 15.0576 53.283 14.9959 52.994 14.9959C52.8623 14.997 52.7294 15.0052 52.5977 15.0215C52.4672 15.0402 52.339 15.0705 52.2143 15.1136C52.177 15.1229 52.1665 15.1509 52.177 15.1928L52.2353 15.4224C52.2423 15.4469 52.2505 15.4632 52.2668 15.4679C52.2889 15.4714 52.3111 15.4702 52.332 15.4632C52.4206 15.4364 52.5103 15.4178 52.6024 15.4073C52.7038 15.391 52.8075 15.3816 52.9112 15.3816C52.9847 15.3793 53.0592 15.3886 53.1303 15.4108C53.1793 15.4236 53.2247 15.4481 53.262 15.4819C53.2923 15.5133 53.3133 15.5529 53.325 15.5949C53.3378 15.6415 53.3436 15.6893 53.3413 15.7371V15.8047H52.8763C52.7329 15.8023 52.5896 15.8222 52.4521 15.8641C52.3495 15.8979 52.2528 15.9515 52.1712 16.0226C52.1001 16.0855 52.0465 16.1636 52.0115 16.251C51.9801 16.3384 51.9626 16.4305 51.9626 16.5226C51.9626 16.6111 51.9777 16.6985 52.0057 16.7813C52.0349 16.8652 52.0861 16.9386 52.1537 16.9945C52.2388 17.0633 52.3367 17.1123 52.4416 17.1402C52.5907 17.1798 52.7434 17.1973 52.8973 17.1938C52.9637 17.1938 53.0348 17.1938 53.1082 17.1892C53.1828 17.1833 53.2515 17.1764 53.3319 17.167C53.4054 17.1554 53.4788 17.1402 53.5499 17.1216C53.6175 17.1041 53.6839 17.082 53.7468 17.054ZM52.9369 16.1625H53.3343L53.3308 16.7486C53.2923 16.7626 53.2527 16.7731 53.2119 16.7801C53.2041 16.7816 53.1964 16.783 53.1888 16.7844C53.1533 16.791 53.1201 16.7973 53.0884 16.8011C53.0702 16.8034 53.0517 16.8045 53.0336 16.8057C53.0146 16.8069 52.9961 16.808 52.9788 16.8104C52.9439 16.8139 52.9182 16.8139 52.8996 16.8139C52.7889 16.8186 52.6793 16.7941 52.5814 16.744C52.4987 16.695 52.4521 16.6018 52.4602 16.5062C52.4567 16.4398 52.473 16.3734 52.508 16.3163C52.5395 16.272 52.5814 16.237 52.6304 16.2137C52.6782 16.1893 52.7306 16.1741 52.7842 16.1694C52.8355 16.1648 52.8856 16.1625 52.9369 16.1625Z", fill: "#FEFEFF" }),
    React__default.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M57.9341 15.2325C57.7803 15.074 57.5425 14.9947 57.2256 14.9947L57.2279 14.9901C57.0904 14.9889 56.954 15.0157 56.827 15.0693C56.7058 15.1159 56.5974 15.1893 56.5088 15.2837C56.4179 15.3863 56.348 15.5052 56.3037 15.6345C56.249 15.786 56.2221 15.9457 56.2245 16.1065C56.2245 16.4841 56.3154 16.7591 56.4925 16.9328C56.6685 17.1076 56.9249 17.195 57.2629 17.195C57.3841 17.1938 57.5041 17.1822 57.623 17.1589C57.7442 17.1356 57.8607 17.0924 57.9679 17.0295C57.9697 17.0283 57.9714 17.0275 57.9732 17.0266C57.9749 17.0257 57.9767 17.0249 57.9784 17.0237C58.0075 17.005 58.0169 16.9654 57.9982 16.9363L57.8852 16.7079C57.8665 16.6729 57.8362 16.6671 57.7919 16.6846C57.7209 16.7183 57.6451 16.7452 57.5682 16.7638C57.4819 16.7848 57.3945 16.7953 57.306 16.7953C57.2291 16.7953 57.1521 16.7859 57.0787 16.7661C57.0111 16.7498 56.9494 16.7172 56.8969 16.6717C56.8421 16.6251 56.799 16.5657 56.7711 16.5004C56.7384 16.4165 56.7209 16.3268 56.7221 16.237H57.8339C57.8782 16.237 57.9225 16.2312 57.9656 16.2196C58.004 16.2102 58.0402 16.1916 58.0705 16.1648C58.1019 16.1345 58.1264 16.0972 58.1416 16.0576C58.159 16.0016 58.1672 15.9422 58.1649 15.8839C58.1649 15.6077 58.088 15.3921 57.9341 15.2325ZM57.0321 15.4236C57.095 15.3945 57.1626 15.3805 57.2326 15.3816V15.384C57.3118 15.3816 57.3922 15.3956 57.4668 15.4248C57.5216 15.4504 57.5682 15.4877 57.6055 15.5355C57.6404 15.5832 57.6661 15.6369 57.6789 15.6951C57.6929 15.7616 57.6999 15.8291 57.6999 15.8967H56.7408C56.7431 15.8303 56.7559 15.765 56.7815 15.7033C56.8072 15.6438 56.8421 15.5879 56.8829 15.5378C56.9249 15.49 56.975 15.4516 57.0321 15.4236Z", fill: "#FEFEFF" }),
    React__default.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M62.161 17.054C62.2123 17.0307 62.2438 16.9782 62.2403 16.9211L62.2356 15.7196C62.2484 15.5191 62.175 15.3234 62.034 15.18C61.9012 15.0576 61.6902 14.9959 61.4 14.9959C61.2683 14.997 61.139 15.0052 61.0085 15.0215C60.8791 15.039 60.7509 15.0705 60.6262 15.1136C60.5877 15.1229 60.5773 15.1509 60.5877 15.1928L60.646 15.4224C60.6507 15.4469 60.66 15.4632 60.6752 15.4679C60.6973 15.4714 60.7206 15.4702 60.7416 15.4632C60.8302 15.4376 60.9199 15.4189 61.0108 15.4073C61.1145 15.391 61.2182 15.3816 61.322 15.3816C61.3965 15.3793 61.4723 15.3886 61.5434 15.4108C61.5923 15.4236 61.6366 15.4481 61.6739 15.4819C61.7042 15.5133 61.7263 15.5518 61.738 15.5949C61.7508 15.6415 61.7566 15.6893 61.7555 15.7371V15.8047H61.2882C61.1448 15.8023 61.0026 15.8222 60.8651 15.8641C60.7614 15.8967 60.6658 15.9515 60.5843 16.0226C60.5132 16.0844 60.4584 16.1636 60.4258 16.251C60.3931 16.3384 60.3768 16.4305 60.3768 16.5226C60.3768 16.6111 60.3908 16.6985 60.4199 16.7813C60.4491 16.864 60.4992 16.9374 60.5668 16.9945C60.6518 17.0621 60.7486 17.1111 60.8535 17.1402C61.0026 17.1798 61.1565 17.1973 61.3115 17.1938C61.3791 17.1938 61.4536 17.1938 61.5236 17.1892C61.5935 17.1833 61.6704 17.1764 61.745 17.167C61.8184 17.1554 61.8918 17.1402 61.9653 17.1216C62.0328 17.1041 62.0981 17.082 62.161 17.054ZM61.3464 16.1625H61.7473L61.738 16.7486C61.6995 16.7626 61.6611 16.7731 61.6203 16.7801C61.6129 16.7815 61.6057 16.7828 61.5985 16.7842C61.5625 16.7909 61.5286 16.7972 61.4956 16.8011C61.477 16.8033 61.4589 16.8045 61.4414 16.8056C61.4227 16.8068 61.4047 16.808 61.3872 16.8104C61.3523 16.8139 61.3278 16.8139 61.3068 16.8139C61.1973 16.8186 61.0877 16.7941 60.9898 16.744C60.9082 16.6939 60.8616 16.6018 60.8698 16.5062C60.8663 16.4398 60.8826 16.3734 60.9164 16.3163C60.9479 16.2732 60.9898 16.2382 61.0376 16.2137C61.0877 16.1893 61.1413 16.1741 61.1961 16.1694C61.2462 16.1648 61.2963 16.1625 61.3464 16.1625Z", fill: "#FEFEFF" }),
    React__default.createElement("path", { d: "M48.8638 14.3339C48.9034 14.3339 48.9267 14.3398 48.9372 14.3549V14.3572C48.9489 14.3794 48.9547 14.4039 48.9535 14.4295V14.6731C48.9559 14.6987 48.95 14.7243 48.9337 14.7453C48.9244 14.7593 48.8988 14.7663 48.8638 14.7663H47.7485C47.6693 14.7581 47.5888 14.7814 47.5259 14.8327C47.4781 14.8863 47.4548 14.9574 47.4595 15.0285V15.4982H48.71C48.7472 15.4982 48.7694 15.5052 48.7776 15.5168C48.7927 15.539 48.7985 15.5646 48.795 15.5902V15.8187C48.7962 15.842 48.7904 15.8664 48.7776 15.8874C48.7694 15.9026 48.7472 15.9107 48.71 15.9107H47.4595V16.8384C47.4606 16.8477 47.4618 16.857 47.4618 16.8664C47.4618 17.0225 47.3348 17.1484 47.1786 17.1484H47.0493C47.0236 17.1495 46.9991 17.1437 46.977 17.1321C46.9595 17.1216 46.9525 17.0994 46.9525 17.0645V15.0553C46.9514 14.9644 46.963 14.8735 46.9863 14.7849C47.0085 14.6987 47.0516 14.6195 47.111 14.553C47.1728 14.4831 47.2509 14.4283 47.3383 14.3945C47.4513 14.3502 47.5714 14.3304 47.6926 14.3339H48.8638Z", fill: "#FEFEFF" }),
    React__default.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M55.8329 15.3001C55.9121 15.4026 55.9704 15.5203 56.0031 15.645L56.0007 15.6462C56.0392 15.786 56.0578 15.9305 56.0567 16.075C56.0578 16.2265 56.038 16.3769 55.9972 16.5226C55.9599 16.6531 55.8947 16.7743 55.8073 16.8768C55.7187 16.9806 55.6068 17.0621 55.4798 17.1146C55.3283 17.174 55.1675 17.202 55.0043 17.1985C54.8586 17.1997 54.7129 17.188 54.5684 17.1647C54.4263 17.1472 54.2887 17.1099 54.1571 17.0517C54.1372 17.0458 54.1198 17.033 54.1069 17.0167C54.0999 16.998 54.0953 16.9771 54.0953 16.9573V14.5029C54.0918 14.3479 54.2153 14.2209 54.3703 14.2174H54.5008C54.523 14.2162 54.544 14.2197 54.5638 14.2302C54.5778 14.2477 54.5836 14.2687 54.5801 14.2908L54.5836 15.0693C54.671 15.0437 54.7596 15.025 54.8481 15.0145C54.9344 15.0029 55.0195 14.9959 55.1057 14.9959C55.2525 14.9912 55.3994 15.0192 55.5357 15.0751C55.6523 15.1252 55.7537 15.2022 55.8329 15.3001ZM55.4005 16.6344C55.453 16.5715 55.4926 16.4981 55.5171 16.4177V16.42C55.5474 16.3151 55.5602 16.2056 55.5579 16.0949C55.5579 15.8583 55.5124 15.6846 55.418 15.5704C55.3201 15.4527 55.1733 15.3875 55.0206 15.3956C54.9827 15.3956 54.935 15.3997 54.8715 15.4051C54.8589 15.4062 54.8457 15.4073 54.8318 15.4084C54.7456 15.4178 54.6605 15.4352 54.5778 15.4609V16.744C54.6454 16.765 54.7153 16.7801 54.7864 16.7859C54.8586 16.7953 54.9227 16.7953 54.9787 16.7953C55.0602 16.7964 55.1407 16.7848 55.2164 16.758C55.2875 16.7323 55.3504 16.6904 55.4005 16.6344Z", fill: "#FEFEFF" }),
    React__default.createElement("path", { d: "M59.2114 16.8267L59.2394 17.054L59.2382 17.0505C59.244 17.068 59.244 17.0866 59.2382 17.1041C59.2242 17.1192 59.2067 17.1309 59.1869 17.1391C59.1263 17.1612 59.0622 17.1752 58.9981 17.181C58.9259 17.1868 58.8641 17.1915 58.8093 17.1915C58.6858 17.202 58.5634 17.1647 58.4667 17.0855C58.3945 17.0155 58.356 16.8943 58.356 16.7207V14.5006C58.3525 14.3444 58.4749 14.2139 58.631 14.2104H58.7674C58.7895 14.2081 58.8117 14.2116 58.8315 14.2221C58.8443 14.2314 58.8513 14.2477 58.8513 14.278V16.6076C58.8466 16.6577 58.856 16.7067 58.8793 16.751C58.9084 16.7801 58.9504 16.7941 58.9911 16.7894H59.0704L59.1438 16.7801C59.159 16.7766 59.1764 16.779 59.1904 16.7871C59.2009 16.7929 59.2067 16.8058 59.2114 16.8267Z", fill: "#FEFEFF" }),
    React__default.createElement("path", { d: "M60.2789 16.8267L60.3069 17.054L60.3046 17.0505C60.3115 17.068 60.3115 17.0866 60.3046 17.1041C60.2917 17.1192 60.2743 17.1309 60.2556 17.1391C60.1938 17.1612 60.1309 17.1752 60.0668 17.181C59.9946 17.1868 59.9316 17.1915 59.8757 17.1915C59.7522 17.202 59.6298 17.1647 59.5342 17.0855C59.462 17.0155 59.4247 16.8943 59.4247 16.7207V14.5169C59.4235 14.5099 59.4235 14.5041 59.4235 14.4971C59.4212 14.3409 59.5459 14.2127 59.702 14.2104H59.8349C59.857 14.2081 59.8792 14.2116 59.899 14.2221C59.9141 14.236 59.9211 14.257 59.9188 14.278V16.6076C59.9141 16.6566 59.9235 16.7067 59.9456 16.751C59.9759 16.7801 60.0179 16.7941 60.0587 16.7894H60.1391L60.2125 16.7801C60.2288 16.7766 60.2451 16.779 60.2591 16.7871C60.2708 16.7964 60.2778 16.8116 60.2789 16.8267Z", fill: "#FEFEFF" }),
    React__default.createElement("path", { d: "M51.8705 16.8267L51.902 17.054L51.8997 17.0505C51.9055 17.068 51.9055 17.0866 51.8997 17.1041C51.8845 17.1181 51.867 17.1297 51.8484 17.1391C51.7878 17.1612 51.7237 17.1752 51.6596 17.181C51.5873 17.1868 51.5244 17.1915 51.4696 17.1915C51.3461 17.202 51.2237 17.1647 51.1282 17.0855C51.0547 17.0155 51.0186 16.8943 51.0186 16.7207V14.5204C51.0198 14.264 51.1993 14.2186 51.2925 14.2104H51.4277C51.4498 14.2081 51.472 14.2116 51.4929 14.2221C51.5046 14.2314 51.5093 14.2477 51.5093 14.278V16.6076C51.5034 16.6577 51.5139 16.7079 51.5396 16.751C51.5699 16.7801 51.6106 16.7941 51.6526 16.7894H51.7318L51.8041 16.7801C51.8204 16.7766 51.8379 16.779 51.8519 16.7871C51.8635 16.7929 51.8705 16.8058 51.8705 16.8267Z", fill: "#FEFEFF" }),
    React__default.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M47.0143 13.024C47.0574 13.0765 47.1448 13.1033 47.2765 13.1033L48.3568 13.1056C48.6342 13.1149 48.9092 13.059 49.1621 12.9448C49.3684 12.8457 49.5467 12.6989 49.6842 12.5171C49.8171 12.3318 49.9127 12.122 49.9616 11.8994C50.0175 11.6617 50.0443 11.4181 50.0432 11.1734C50.0455 10.9275 50.014 10.6827 49.9511 10.445C49.894 10.2247 49.7926 10.0196 49.6504 9.8413C49.5048 9.66416 49.3195 9.52431 49.1097 9.43225C48.8533 9.32387 48.5759 9.27142 48.2974 9.27958H47.2765C47.146 9.27958 47.0574 9.30638 47.0143 9.36116C46.9653 9.43807 46.9432 9.52781 46.9514 9.61755V12.7723C46.9432 12.8609 46.9653 12.9494 47.0143 13.024ZM49.1435 10.7072C49.1726 10.8599 49.1878 11.0149 49.1866 11.171C49.1878 11.333 49.1726 11.495 49.1423 11.6547C49.1178 11.7899 49.0666 11.9192 48.9897 12.0334C48.9139 12.1406 48.8113 12.2269 48.6925 12.2817C48.5433 12.3469 48.3825 12.3772 48.2205 12.3714H47.7788V10.0033H48.2042C48.3697 9.99747 48.5328 10.0278 48.6843 10.093C48.8055 10.1478 48.9092 10.2317 48.9873 10.3378C49.0654 10.4485 49.1178 10.5743 49.1435 10.7072Z", fill: "#FEFEFF" }),
    React__default.createElement("path", { d: "M51.2121 9.83431C51.2156 9.85063 51.2331 9.86461 51.2657 9.8751L51.5722 9.97882C51.5943 9.98581 51.6176 9.98814 51.6398 9.98465C51.6573 9.98115 51.6759 9.96251 51.6981 9.92987L52.3355 8.99056C52.3658 8.94045 52.3507 8.90782 52.2924 8.89267L51.7738 8.75631C51.7412 8.74699 51.7074 8.74116 51.6736 8.74C51.6456 8.74 51.6235 8.76564 51.6048 8.81575L51.2226 9.76672C51.2132 9.7877 51.2097 9.811 51.2121 9.83431Z", fill: "#FEFEFF" }),
    React__default.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M52.4917 10.4858C52.2796 10.2737 51.9591 10.1665 51.5337 10.1665C51.3414 10.1641 51.1515 10.1979 50.9732 10.2678C50.81 10.3331 50.6643 10.4357 50.5466 10.565C50.4243 10.7049 50.3322 10.8669 50.2751 11.0428C50.2075 11.2503 50.176 11.467 50.1784 11.685C50.1784 12.1977 50.2937 12.573 50.5222 12.8096C50.7517 13.045 51.1049 13.1639 51.5815 13.1639C51.7482 13.1615 51.916 13.1429 52.0791 13.1068C52.2446 13.073 52.4043 13.0089 52.5476 12.918C52.5709 12.904 52.5907 12.8853 52.6047 12.8632C52.6141 12.8376 52.6106 12.8096 52.5966 12.7863L52.4218 12.3935C52.4136 12.3702 52.3973 12.3527 52.3763 12.3411C52.3449 12.3376 52.3145 12.3423 52.2866 12.3551C52.1875 12.397 52.085 12.4297 51.9801 12.453C51.874 12.4786 51.7668 12.4914 51.6584 12.4914C51.5734 12.4926 51.4883 12.4833 51.4055 12.4646C51.3309 12.4483 51.261 12.4145 51.2004 12.3667C51.141 12.3166 51.0932 12.2525 51.0617 12.1803C51.0244 12.0894 51.0046 11.9903 51.0046 11.8912H52.402C52.5115 11.8971 52.6187 11.8598 52.7015 11.7875C52.7749 11.7188 52.811 11.5894 52.811 11.3994C52.811 11.0032 52.705 10.699 52.4917 10.4858ZM51.1841 10.9799C51.2762 10.875 51.4102 10.8167 51.55 10.8214C51.6328 10.8179 51.7144 10.8331 51.7901 10.8645C51.8484 10.8913 51.8997 10.9321 51.9381 10.9822C51.9754 11.0358 52.0034 11.0964 52.0174 11.1605C52.0337 11.234 52.0418 11.3085 52.0418 11.3831H51.021C51.0291 11.2351 51.0862 11.0929 51.1841 10.9799Z", fill: "#FEFEFF" }),
    React__default.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M55.925 12.2385C55.9786 12.0451 56.0054 11.8458 56.0042 11.6465C56.0054 11.4507 55.9809 11.2549 55.932 11.0661C55.89 10.8948 55.8143 10.734 55.7082 10.593C55.6045 10.459 55.4716 10.3506 55.3178 10.2783C55.1395 10.1968 54.946 10.1583 54.7502 10.163C54.6395 10.163 54.5277 10.1711 54.4169 10.1851C54.3121 10.1968 54.2083 10.2189 54.1069 10.248L54.1011 9.22247C54.1011 9.17469 54.0918 9.14556 54.0743 9.13274C54.0475 9.11759 54.0172 9.1106 53.9869 9.11293H53.4287C53.3972 9.10943 53.3646 9.11759 53.3366 9.13507C53.3156 9.15838 53.3051 9.18984 53.3098 9.22247V12.7886C53.3086 12.8224 53.3145 12.8562 53.3261 12.8877C53.3448 12.918 53.3739 12.9401 53.4077 12.9506C53.5872 13.0298 53.776 13.0858 53.9706 13.1138C54.1699 13.1464 54.3727 13.1627 54.5754 13.1627C54.8004 13.1685 55.0229 13.1277 55.2316 13.0427C55.4017 12.9704 55.552 12.8585 55.6721 12.7187C55.7898 12.5777 55.876 12.4145 55.925 12.2385ZM55.0206 11.0463C55.1265 11.1813 55.1801 11.3872 55.1803 11.664C55.1801 11.9676 55.123 12.1816 55.0078 12.3061C54.8831 12.4332 54.7083 12.5008 54.53 12.4914C54.4787 12.4914 54.4134 12.4879 54.333 12.4809C54.2549 12.474 54.178 12.4588 54.1023 12.4367V10.9146C54.1897 10.8855 54.2794 10.8657 54.3715 10.8575C54.4636 10.8482 54.5323 10.8436 54.5789 10.8436C54.7502 10.8366 54.9146 10.9123 55.0206 11.0463Z", fill: "#FEFEFF" }),
    React__default.createElement("path", { d: "M57.1778 13.0835C57.1953 13.0613 57.2034 13.0333 57.1999 13.0042V10.3378C57.2034 10.3098 57.1953 10.2818 57.1778 10.2585C57.1475 10.2422 57.1137 10.2364 57.0787 10.2399H56.5228C56.4925 10.2375 56.4622 10.2445 56.4354 10.2585C56.4144 10.2795 56.4039 10.3086 56.4086 10.3378V13.0042C56.4039 13.0333 56.4144 13.0636 56.4354 13.0835C56.4622 13.0986 56.4925 13.1056 56.5228 13.1033H57.0787C57.1137 13.1068 57.1475 13.0998 57.1778 13.0835Z", fill: "#FEFEFF" }),
    React__default.createElement("path", { d: "M57.137 9.80984C57.2244 9.72593 57.2733 9.61055 57.2698 9.48935C57.271 9.42759 57.2594 9.36698 57.2349 9.30988C57.2127 9.25627 57.1789 9.20732 57.137 9.1677C57.0939 9.12691 57.0438 9.09428 56.9901 9.07214C56.933 9.04883 56.8724 9.03601 56.8118 9.03718C56.7524 9.03601 56.6918 9.04883 56.637 9.07214C56.5834 9.09428 56.5345 9.12691 56.4925 9.1677C56.4506 9.20732 56.4168 9.25627 56.3946 9.30988C56.3702 9.36698 56.3585 9.42759 56.3585 9.48935C56.3562 9.61055 56.4051 9.72593 56.4925 9.80984C56.5776 9.89258 56.693 9.93803 56.8118 9.9357C56.933 9.93803 57.0496 9.89258 57.137 9.80984Z", fill: "#FEFEFF" }),
    React__default.createElement("path", { d: "M59.5307 13.0718C59.5704 13.0567 59.5902 13.0415 59.5902 13.0252C59.5902 13.003 59.5878 12.9797 59.5843 12.9588L59.5214 12.5043C59.5179 12.4565 59.4876 12.439 59.4293 12.4495C59.4037 12.453 59.3722 12.4565 59.3361 12.46L59.2242 12.4705L59.1123 12.4821C59.0809 12.4856 59.0506 12.4868 59.0191 12.4879C58.9364 12.4938 58.8525 12.4728 58.7825 12.4273C58.7289 12.3877 58.7021 12.3143 58.7021 12.2082V10.8202H59.3035C59.3478 10.8202 59.3722 10.8121 59.3792 10.7958C59.3885 10.7701 59.392 10.7433 59.3909 10.7165V10.3296C59.392 10.3063 59.3874 10.283 59.3769 10.262C59.3699 10.2457 59.3454 10.2375 59.3035 10.2375H58.7021V9.50567C58.7068 9.4742 58.6963 9.44157 58.673 9.41826C58.6497 9.40311 58.6205 9.39962 58.5937 9.40777L58.0891 9.57093C58.0565 9.57909 58.025 9.59424 57.9994 9.61522C57.9761 9.64085 57.9633 9.67349 57.9633 9.70845L57.926 10.2212L57.5216 10.3133C57.4936 10.318 57.468 10.3308 57.447 10.3494C57.4365 10.3727 57.4318 10.3972 57.4342 10.4228V10.7177C57.433 10.7433 57.4388 10.769 57.4505 10.7911C57.4645 10.8109 57.4866 10.8214 57.5274 10.8214H57.9097V12.3772C57.9097 12.6359 57.9761 12.8317 58.1113 12.9646C58.2453 13.0963 58.469 13.1627 58.7825 13.1627C58.8769 13.1627 58.9923 13.158 59.1263 13.1476C59.2627 13.1359 59.3979 13.1103 59.5307 13.0718Z", fill: "#FEFEFF" }),
    React__default.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M61.8988 12.77C62.1284 12.5077 62.2426 12.1371 62.2426 11.657C62.2426 11.1908 62.1272 10.8249 61.8965 10.5615C61.6657 10.2981 61.3208 10.1665 60.8616 10.1665C60.6647 10.163 60.47 10.1979 60.2859 10.2702C60.1228 10.3354 59.9759 10.4357 59.8547 10.5639C59.7312 10.7002 59.6391 10.861 59.582 11.037C59.5156 11.2398 59.4829 11.4531 59.4864 11.6675C59.4864 12.1406 59.596 12.5077 59.8139 12.77C60.033 13.0322 60.378 13.1627 60.8511 13.1627C61.3196 13.1627 61.6692 13.0322 61.8988 12.77ZM61.3919 11.3342C61.4105 11.4461 61.4198 11.5591 61.4187 11.6733C61.4187 11.7805 61.4082 11.8889 61.3861 11.995C61.3686 12.0859 61.3348 12.1744 61.287 12.2537C61.245 12.3259 61.1856 12.3854 61.1133 12.4285C61.0341 12.4728 60.9444 12.4938 60.8546 12.4914C60.6577 12.4914 60.5143 12.4215 60.4316 12.2782C60.3477 12.136 60.3046 11.9285 60.3046 11.6558C60.3057 11.4053 60.3523 11.206 60.4444 11.058C60.5365 10.9111 60.7008 10.8261 60.8733 10.8377C60.963 10.8342 61.0516 10.8564 61.1297 10.9007C61.1984 10.9438 61.2567 11.002 61.2986 11.072C61.3453 11.1535 61.3767 11.2421 61.3919 11.3342Z", fill: "#FEFEFF" }))); };

var css_248z$8 = ".styles-module_discount-badge__-JnXN{align-items:flex-start;background:#eb0029;border-radius:3px;display:flex;gap:4px;height:-moz-fit-content;height:fit-content;margin-left:4px;padding:2px 5px;width:-moz-fit-content;width:fit-content}.styles-module_badge-label__Pz1hx{font-size:12px;font-weight:700;-webkit-font-smoothing:antialiased}";
var styles$1 = {"discount-badge":"styles-module_discount-badge__-JnXN","badge-label":"styles-module_badge-label__Pz1hx"};
styleInject(css_248z$8);

function SponsoredProductBadge(_a) {
    var badge = _a.badge, containerStyles = _a.containerStyles, textStyles = _a.textStyles;
    if (!badge || badge.length === 0) {
        return null;
    }
    var firstBadge = badge.find(function (badgeItem) { return badgeItem.position === 2; });
    if (!firstBadge) {
        return null;
    }
    var label = firstBadge.label, _b = firstBadge.styles, backgroundColor = _b.backgroundColor, textColor = _b.textColor;
    return (React__default.createElement("div", { className: styles$1['discount-badge'], style: __assign(__assign({}, containerStyles), { backgroundColor: backgroundColor }) },
        React__default.createElement("span", { className: styles$1['badge-label'], style: __assign(__assign({}, textStyles), { color: textColor }) }, label)));
}

var constants = {
    MIN_MAX_SB_PRODUCTS: [1, 3],
    DECIMAL_SEPARATOR: {
        cl: ',',
        co: ',',
        ar: ',',
        pe: '.',
    },
    DOT_SEPARATOR: {
        cl: '.',
        co: '.',
        ar: '.',
        pe: ',',
    },
    SPONSORED_PRODUCTS_RESPONSE: {
        success: true,
        errors: [],
        sponsoredProducts: [
            {
                id: '119714138',
                productId: '119714135',
                displayName: 'Apple iPhone 13 Pro 256GB Azul Sierra - Reacondicionado',
                brand: 'APPLE',
                offeringId: '119714138',
                rating: 4.8,
                totalReviews: 6,
                prices: [
                    {
                        label: '',
                        originalPrice: '639.990',
                        symbol: '$ ',
                        type: 1,
                        isLoyalty: false,
                        opportunidadUnica: true,
                        crossed: false,
                    },
                    {
                        label: '',
                        originalPrice: '689.990',
                        symbol: '$ ',
                        type: 2,
                        isLoyalty: false,
                        opportunidadUnica: false,
                        crossed: false,
                    },
                    {
                        label: '',
                        originalPrice: '1.099.990',
                        symbol: '$ ',
                        type: 3,
                        isLoyalty: false,
                        opportunidadUnica: false,
                        crossed: true,
                    },
                ],
                badges: [
                    {
                        position: 1,
                        label: 'BLACK OUT',
                        styles: {
                            backgroundColor: '#333333',
                            textColor: '#ffffff',
                        },
                    },
                    {
                        position: 2,
                        label: '-42%',
                        styles: {
                            backgroundColor: '#EB0029',
                            textColor: '#ffffff',
                        },
                    },
                ],
                discountBadge: {
                    label: '-42%',
                    styles: {
                        backgroundColor: '#EB0029',
                        textColor: '#ffffff',
                    },
                },
                meatStickers: [
                    {
                        label: 'BLACK OUT',
                        type: 'event',
                        styles: {
                            backgroundColor: '#333333',
                            textColor: '#ffffff',
                        },
                    },
                ],
                url: 'https://www.falabella.com/falabella-cl/product/119714135/Apple-iPhone-13-Pro-256GB-Azul-Sierra-Reacondicionado/119714138',
                published: true,
                variations: {
                    color: [],
                    size: [],
                },
                categoryPaths: [
                    'cat7090034||Tecnología/cat16400010||Telefonía/cat2018||Celulares y Teléfonos/cat720161||Smartphones',
                ],
                sellerName: 'Conexiones Renovadas',
                mediaUrls: [
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/119714138_01/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/119714138_02/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/119714138_03/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/119714138_04/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/119714138_05/public',
                ],
                adInfo: 'v2_3cKZO52EYJReXmuT5_x3qOLl2pSjgBFrI8m0qOqC0DJX0BO0xoSlpBVy_qXIgJqAb4M55wHdFRgc858l9O_B1rVuTW6a3Cd8LdzJPQfyntQIsJTKnKW7TsYdOEbJiSDtGUabCCa-2RKgWUaYyP8jBA==_ark1911363e05da915cc6',
                adIdentifier: 'v2_3cKZO52EYJReXmuT5_x3qOLl2pSjgBFrI8m0qOqC0DJX0BO0xoSlpBVy_qXIgJqAb4M55wHdFRgc858l9O_B1rVuTW6a3Cd8LdzJPQfyntQIsJTKnKW7TsYdOEbJiSDtGUabCCa-2RKgWUaYyP8jBA==_ark1911363e05da915cc6',
            },
            {
                id: '120461946',
                productId: '120461944',
                displayName: 'Celular ASUS ROG 5s 12GB 256GB - Negro',
                brand: 'ASUS',
                offeringId: '120461946',
                rating: 5,
                totalReviews: 3,
                prices: [
                    {
                        label: '',
                        originalPrice: '439.990',
                        symbol: '$ ',
                        type: 2,
                        isLoyalty: false,
                        opportunidadUnica: false,
                        crossed: false,
                    },
                    {
                        label: '',
                        originalPrice: '779.990',
                        symbol: '$ ',
                        type: 3,
                        isLoyalty: false,
                        opportunidadUnica: false,
                        crossed: true,
                    },
                ],
                badges: [
                    {
                        position: 1,
                        label: 'BLACK OUT',
                        styles: {
                            backgroundColor: '#333333',
                            textColor: '#ffffff',
                        },
                    },
                    {
                        position: 2,
                        label: '-44%',
                        styles: {
                            backgroundColor: '#EB0029',
                            textColor: '#ffffff',
                        },
                    },
                ],
                discountBadge: {
                    label: '-44%',
                    styles: {
                        backgroundColor: '#EB0029',
                        textColor: '#ffffff',
                    },
                },
                meatStickers: [
                    {
                        label: 'BLACK OUT',
                        type: 'event',
                        styles: {
                            backgroundColor: '#333333',
                            textColor: '#ffffff',
                        },
                    },
                ],
                url: 'https://www.falabella.com/falabella-cl/product/120461944/Celular-ASUS-ROG-5s-12GB-256GB-Negro/120461946',
                published: true,
                variations: {
                    color: [],
                    size: [],
                },
                categoryPaths: [
                    'cat7090034||Tecnología/cat16400010||Telefonía/cat2018||Celulares y Teléfonos/cat720161||Smartphones',
                ],
                sellerName: 'ROG International Store',
                mediaUrls: [
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/120461946_01/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/120461946_02/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/120461946_03/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/120461946_04/public',
                ],
                adInfo: 'v2_oEucMRIOxbV5tgexDj5W7dgz-7lc7YQbw7VpoaY-JiFX0BO0xoSlpBVy_qXIgJqA9hqlrsc9WaoNc9Wz6igd94svnMwsy6aRZaKhL4ajCojx5fVIEAoBGUFB3NZOlgEFrpnHfoiUbloOwAKGo0ibTecccL5kLZM0Kefp8HQ9s4c=_ark1911363e05da915cc6',
                adIdentifier: 'v2_oEucMRIOxbV5tgexDj5W7dgz-7lc7YQbw7VpoaY-JiFX0BO0xoSlpBVy_qXIgJqA9hqlrsc9WaoNc9Wz6igd94svnMwsy6aRZaKhL4ajCojx5fVIEAoBGUFB3NZOlgEFrpnHfoiUbloOwAKGo0ibTecccL5kLZM0Kefp8HQ9s4c=_ark1911363e05da915cc6',
            },
            {
                id: '120572730',
                productId: '120572729',
                displayName: 'iPhone 12 64GB Black',
                brand: 'APPLE',
                offeringId: '120572730',
                rating: 5,
                totalReviews: 1,
                prices: [
                    {
                        label: '',
                        originalPrice: '519.990',
                        symbol: '$ ',
                        type: 3,
                        isLoyalty: false,
                        opportunidadUnica: false,
                        crossed: false,
                    },
                ],
                discountBadge: {
                    label: '-31%',
                    styles: {
                        backgroundColor: '#EB0029',
                        textColor: '#ffffff',
                    },
                },
                meatStickers: [
                    {
                        label: 'BLACK OUT',
                        type: 'event',
                        styles: {
                            backgroundColor: '#333333',
                            textColor: '#ffffff',
                        },
                    },
                ],
                url: 'https://www.falabella.com/falabella-cl/product/120572729/iPhone-12-64GB-Black/120572730',
                published: true,
                variations: {
                    color: [],
                    size: [],
                },
                categoryPaths: [
                    'cat7090034||Tecnología/cat16400010||Telefonía/cat2018||Celulares y Teléfonos/cat720161||Smartphones',
                ],
                sellerName: 'Movistar',
                mediaUrls: [
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/120572730_01/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/120572730_02/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/120572730_03/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/120572730_04/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/120572730_05/public',
                ],
                adInfo: 'v2_kKDFTFvX8o5OmF2OFo2cPrriYkdGI6IN_oe6ceLXKOBX0BO0xoSlpBVy_qXIgJqALEXqdlJFn5g1pOMb3KqHSsnx1WxMgHEmuRf4-Zg4GQv54IvZ3_6BegWi1ZzT_DxHX8j2mknEvinnnf2SrofzfecccL5kLZM0Kefp8HQ9s4c=_ark1911363e05da915cc6',
                adIdentifier: 'v2_kKDFTFvX8o5OmF2OFo2cPrriYkdGI6IN_oe6ceLXKOBX0BO0xoSlpBVy_qXIgJqALEXqdlJFn5g1pOMb3KqHSsnx1WxMgHEmuRf4-Zg4GQv54IvZ3_6BegWi1ZzT_DxHX8j2mknEvinnnf2SrofzfecccL5kLZM0Kefp8HQ9s4c=_ark1911363e05da915cc6',
            },
        ],
    },
    SPONSORED_BRAND_RESPONSE: {
        success: true,
        errors: [],
        sponsoredProducts: [
            {
                id: '126362268',
                productId: '126362267',
                displayName: 'Licuadora Ultra Silver Gran capacidad 2,2L + vaso to go EasyWays',
                brand: 'EASYWAYS',
                offeringId: '126362268',
                rating: 0,
                prices: [
                    {
                        label: '',
                        originalPrice: '86.990',
                        symbol: '$ ',
                        type: 3,
                        isLoyalty: false,
                        opportunidadUnica: false,
                        crossed: false,
                    },
                ],
                badges: [
                    {
                        position: 2,
                        label: '-25%',
                        styles: {
                            backgroundColor: '#EB0029',
                            textColor: '#ffffff',
                        },
                    },
                ],
                discountBadge: {
                    label: '-25%',
                    styles: {
                        backgroundColor: '#EB0029',
                        textColor: '#ffffff',
                    },
                },
                meatStickers: [],
                url: 'https://www.falabella.com/falabella-cl/product/126362267/Licuadora-Ultra-Silver-Gran-capacidad-2,2L-+-vaso-to-go-EasyWays/126362268',
                published: true,
                variations: {
                    color: [],
                    size: [],
                },
                categoryPaths: [
                    'cat16510006||Electrohogar/cat2034||Electrodomésticos Cocina/cat3141||Licuadoras',
                ],
                sellerName: 'EASYWAYS',
                mediaUrls: [
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/126362268_01/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/126362268_02/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/126362268_03/public',
                ],
                adInfo: 'v2_TM3uMCwtKkFGb6C0uGEULL9hh-93UUKFHOys8qQAIEcLimq7ResVmm0cgp8J47ClY1ul_2VACcY9U5kFDgc4CgLW2tC77HtpByG2ynlKMA8DPUkGcBFapAHbBgChD1z9Nuv3nKzkpE-QP-oPwsu98foqFF_DHMqLEfon1YzsnPgQWHnlf6WCaSAR6ycTI9TU_ark1942c99767753d90d3',
                adIdentifier: 'v2_TM3uMCwtKkFGb6C0uGEULL9hh-93UUKFHOys8qQAIEcLimq7ResVmm0cgp8J47ClY1ul_2VACcY9U5kFDgc4CgLW2tC77HtpByG2ynlKMA8DPUkGcBFapAHbBgChD1z9Nuv3nKzkpE-QP-oPwsu98foqFF_DHMqLEfon1YzsnPgQWHnlf6WCaSAR6ycTI9TU_ark1942c99767753d90d3',
                measurement: {},
            },
            {
                id: '126362270',
                productId: '126362269',
                displayName: "Licuadora Crush n' roll Negra 1,5 Litros EasyWays",
                brand: 'EASYWAYS',
                offeringId: '126362270',
                rating: 0,
                prices: [
                    {
                        label: '',
                        originalPrice: '34.990',
                        symbol: '$ ',
                        type: 3,
                        isLoyalty: false,
                        opportunidadUnica: false,
                        crossed: false,
                    },
                ],
                badges: [
                    {
                        position: 2,
                        label: '-36%',
                        styles: {
                            backgroundColor: '#EB0029',
                            textColor: '#ffffff',
                        },
                    },
                ],
                discountBadge: {
                    label: '-36%',
                    styles: {
                        backgroundColor: '#EB0029',
                        textColor: '#ffffff',
                    },
                },
                meatStickers: [],
                url: "https://www.falabella.com/falabella-cl/product/126362269/Licuadora-Crush-n'-roll-Negra-1,5-Litros-EasyWays/126362270",
                published: true,
                variations: {
                    color: [],
                    size: [],
                },
                categoryPaths: [
                    'cat16510006||Electrohogar/cat2034||Electrodomésticos Cocina/cat3141||Licuadoras',
                ],
                sellerName: 'EASYWAYS',
                mediaUrls: [
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/126362270_01/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/126362270_02/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/126362270_03/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/126362270_04/public',
                ],
                adInfo: 'v2_QDX8n0Cw_zC4aHghBWRNzb9hh-93UUKFHOys8qQAIEcLimq7ResVmm0cgp8J47ClY1ul_2VACcY9U5kFDgc4CgLW2tC77HtpByG2ynlKMA9EcAC2jdTRUcsEiSEH3ahFCwwHyV1hqTcGJs99ShQcfPoqFF_DHMqLEfon1YzsnPgQWHnlf6WCaSAR6ycTI9TU_ark1942c99767753d90d3',
                adIdentifier: 'v2_QDX8n0Cw_zC4aHghBWRNzb9hh-93UUKFHOys8qQAIEcLimq7ResVmm0cgp8J47ClY1ul_2VACcY9U5kFDgc4CgLW2tC77HtpByG2ynlKMA9EcAC2jdTRUcsEiSEH3ahFCwwHyV1hqTcGJs99ShQcfPoqFF_DHMqLEfon1YzsnPgQWHnlf6WCaSAR6ycTI9TU_ark1942c99767753d90d3',
                measurement: {},
            },
            {
                id: '126362272',
                productId: '126362271',
                displayName: 'Licuadora Ultra Roja Gran capacidad 2,2L + vaso to go EasyWays',
                brand: 'EASYWAYS',
                offeringId: '126362272',
                rating: 0,
                prices: [
                    {
                        label: '',
                        originalPrice: '86.990',
                        symbol: '$ ',
                        type: 3,
                        isLoyalty: false,
                        opportunidadUnica: false,
                        crossed: false,
                    },
                ],
                badges: [
                    {
                        position: 2,
                        label: '-25%',
                        styles: {
                            backgroundColor: '#EB0029',
                            textColor: '#ffffff',
                        },
                    },
                ],
                discountBadge: {
                    label: '-25%',
                    styles: {
                        backgroundColor: '#EB0029',
                        textColor: '#ffffff',
                    },
                },
                meatStickers: [],
                url: 'https://www.falabella.com/falabella-cl/product/126362271/Licuadora-Ultra-Roja-Gran-capacidad-2,2L-+-vaso-to-go-EasyWays/126362272',
                published: true,
                variations: {
                    color: [],
                    size: [],
                },
                categoryPaths: [
                    'cat16510006||Electrohogar/cat2034||Electrodomésticos Cocina/cat3141||Licuadoras',
                ],
                sellerName: 'EASYWAYS',
                mediaUrls: [
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/126362272_01/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/126362272_02/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/126362272_03/public',
                ],
                adInfo: 'v2_QSDmmtJIlOzBvypB2iEpDr9hh-93UUKFHOys8qQAIEcLimq7ResVmm0cgp8J47ClY1ul_2VACcY9U5kFDgc4CgLW2tC77HtpByG2ynlKMA9NNGnAd0txn8YbRVnCPcpJNuv3nKzkpE-QP-oPwsu98foqFF_DHMqLEfon1YzsnPgQWHnlf6WCaSAR6ycTI9TU_ark1942c99767753d90d3',
                adIdentifier: 'v2_QSDmmtJIlOzBvypB2iEpDr9hh-93UUKFHOys8qQAIEcLimq7ResVmm0cgp8J47ClY1ul_2VACcY9U5kFDgc4CgLW2tC77HtpByG2ynlKMA9NNGnAd0txn8YbRVnCPcpJNuv3nKzkpE-QP-oPwsu98foqFF_DHMqLEfon1YzsnPgQWHnlf6WCaSAR6ycTI9TU_ark1942c99767753d90d3',
                measurement: {},
            },
            {
                id: '126362274',
                productId: '126362273',
                displayName: "Licuadora Crush n' roll Silver  1,5 Litros EasyWays",
                brand: 'EASYWAYS',
                offeringId: '126362274',
                rating: 0,
                prices: [
                    {
                        label: '',
                        originalPrice: '34.990',
                        symbol: '$ ',
                        type: 3,
                        isLoyalty: false,
                        opportunidadUnica: false,
                        crossed: false,
                    },
                ],
                badges: [
                    {
                        position: 2,
                        label: '-36%',
                        styles: {
                            backgroundColor: '#EB0029',
                            textColor: '#ffffff',
                        },
                    },
                ],
                discountBadge: {
                    label: '-36%',
                    styles: {
                        backgroundColor: '#EB0029',
                        textColor: '#ffffff',
                    },
                },
                meatStickers: [],
                url: "https://www.falabella.com/falabella-cl/product/126362273/Licuadora-Crush-n'-roll-Silver-1,5-Litros-EasyWays/126362274",
                published: true,
                variations: {
                    color: [],
                    size: [],
                },
                categoryPaths: [
                    'cat16510006||Electrohogar/cat2034||Electrodomésticos Cocina/cat3141||Licuadoras',
                ],
                sellerName: 'EASYWAYS',
                mediaUrls: [
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/126362274_01/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/126362274_02/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/126362274_03/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/126362274_04/public',
                ],
                adInfo: 'v2_UOv1W6DQ8eVYZTXcXs1-_L9hh-93UUKFHOys8qQAIEcLimq7ResVmm0cgp8J47ClY1ul_2VACcY9U5kFDgc4CgLW2tC77HtpByG2ynlKMA9JMNAcM2AR_3AnFi-QSN8VCwwHyV1hqTcGJs99ShQcfPoqFF_DHMqLEfon1YzsnPgQWHnlf6WCaSAR6ycTI9TU_ark1942c99767753d90d3',
                adIdentifier: 'v2_UOv1W6DQ8eVYZTXcXs1-_L9hh-93UUKFHOys8qQAIEcLimq7ResVmm0cgp8J47ClY1ul_2VACcY9U5kFDgc4CgLW2tC77HtpByG2ynlKMA9JMNAcM2AR_3AnFi-QSN8VCwwHyV1hqTcGJs99ShQcfPoqFF_DHMqLEfon1YzsnPgQWHnlf6WCaSAR6ycTI9TU_ark1942c99767753d90d3',
                measurement: {},
            },
        ],
        promotionText: '¡Haz tu vida más simple con EasyWays!',
        merchantName: 'EASYWAYS',
        image: 'https://res.cloudinary.com/dvkezxchf/image/upload/ukqszcajzu3gotlzksti',
        landingPageUrl: 'https://www.falabella.com/falabella-cl/brand/EASYWAYS',
        mabayaAdInfo: 'v2_URGeXujDFT6bEczUkQcsnb9hh-93UUKFHOys8qQAIEcLimq7ResVmm0cgp8J47ClY1ul_2VACcY9U5kFDgc4Cnt8eo-aub_xuNNdb1xqtkCDH_xngfnrmuXrym25N_FLjJE2fwlZqIdd9-4YExFUhm3_7Sja2Y5AzoqoI9M-pMI=_ark1942c99767753d90d3',
        scndImg: 'https://mabaya-public-uploads.s3.amazonaws.com/images/headlineCampaigns/3817074_2024-12-31-14-40-58-174.jpg',
        scndImg2: 'https://res.cloudinary.com/dvkezxchf/image/upload/h_230,w_238/nbftpsztpzghkneqmx1w',
        scndImages: {},
    },
    SPONSORED_BRAND_RESPONSE_WITH_MULTPLES_PRICES: {
        success: true,
        errors: [],
        sponsoredProducts: [
            {
                id: '14454016',
                productId: '14454016',
                displayName: 'Refrigerador Bottom Freezer 311 Litros No Frost Rb31K3210S9/Zs Samsung',
                brand: 'SAMSUNG',
                offeringId: '14454016',
                rating: 4.6,
                totalReviews: 337,
                prices: [
                    {
                        label: '',
                        originalPrice: '429.990',
                        symbol: '$ ',
                        type: 3,
                        isLoyalty: false,
                        opportunidadUnica: false,
                        crossed: false,
                    },
                    {
                        label: '',
                        originalPrice: '659.990',
                        symbol: '$ ',
                        type: 2,
                        isLoyalty: false,
                        opportunidadUnica: false,
                        crossed: true,
                    },
                ],
                badges: [
                    {
                        position: 1,
                        label: 'HOT SALE',
                        styles: {
                            backgroundColor: '#000000',
                            textColor: '#FFFFFF',
                        },
                    },
                    {
                        position: 2,
                        label: '-35%',
                        styles: {
                            backgroundColor: '#EB0029',
                            textColor: '#ffffff',
                        },
                    },
                ],
                discountBadge: {
                    label: '-35%',
                    styles: {
                        backgroundColor: '#EB0029',
                        textColor: '#ffffff',
                    },
                },
                meatStickers: [
                    {
                        label: 'HOT SALE',
                        type: 'event',
                        styles: {
                            backgroundColor: '#000000',
                            textColor: '#FFFFFF',
                        },
                    },
                ],
                url: 'https://www.falabella.com/falabella-cl/product/14454016/Refrigerador-Bottom-Freezer-311-Litros-No-Frost-Rb31K3210S9-Zs-Samsung/14454016',
                published: true,
                variations: {
                    color: [],
                    size: [],
                },
                categoryPaths: [
                    'cat16510006||Electrohogar/cat7090035||Línea blanca/CATG19018||Refrigeración/cat3205||Refrigeradores/CATG19020||Bottom Freezer',
                ],
                sellerName: 'FALABELLA',
                mediaUrls: [
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/14454016_1/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/14454016_3/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/14454016_4/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/14454016_6/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/14454016_7/public',
                ],
                adInfo: 'v2_tcIYitgmV5NCaBE3Caym-poLclPeCLE0ENbSZ3UNQp0Limq7ResVmm0cgp8J47ClyVdss2VkzodqLS487oj71Tgv-md6f7xOVSivyCPfiaDnUmsHSsnBiXZZs0sLnD-J_bQVTQ0XsASomiGfjlC8J4st0JiLfnuKymvoY4Cg7L4=_ark194b7cea57521a27c',
                adIdentifier: 'v2_tcIYitgmV5NCaBE3Caym-poLclPeCLE0ENbSZ3UNQp0Limq7ResVmm0cgp8J47ClyVdss2VkzodqLS487oj71Tgv-md6f7xOVSivyCPfiaDnUmsHSsnBiXZZs0sLnD-J_bQVTQ0XsASomiGfjlC8J4st0JiLfnuKymvoY4Cg7L4=_ark194b7cea57521a27c',
                measurement: {},
            },
            {
                id: '14873869',
                productId: '14873869',
                displayName: 'Refrigerador Side by Side Family Hub 585 Litros No Frost RS58T5561B1/ZS Samsung',
                brand: 'SAMSUNG',
                offeringId: '14873869',
                rating: 4.1,
                totalReviews: 63,
                prices: [
                    {
                        label: '',
                        originalPrice: '1.429.990',
                        symbol: '$ ',
                        type: 3,
                        isLoyalty: false,
                        opportunidadUnica: false,
                        crossed: false,
                    },
                    {
                        label: '',
                        originalPrice: '2.349.990',
                        symbol: '$ ',
                        type: 2,
                        isLoyalty: false,
                        opportunidadUnica: false,
                        crossed: true,
                    },
                ],
                badges: [
                    {
                        position: 1,
                        label: 'HOT SALE',
                        styles: {
                            backgroundColor: '#000000',
                            textColor: '#FFFFFF',
                        },
                    },
                    {
                        position: 2,
                        label: '-39%',
                        styles: {
                            backgroundColor: '#EB0029',
                            textColor: '#ffffff',
                        },
                    },
                ],
                discountBadge: {
                    label: '-39%',
                    styles: {
                        backgroundColor: '#EB0029',
                        textColor: '#ffffff',
                    },
                },
                meatStickers: [
                    {
                        label: 'HOT SALE',
                        type: 'event',
                        styles: {
                            backgroundColor: '#000000',
                            textColor: '#FFFFFF',
                        },
                    },
                ],
                url: 'https://www.falabella.com/falabella-cl/product/14873869/Refrigerador-Side-by-Side-Family-Hub-585-Litros-No-Frost-RS58T5561B1-ZS-Samsung/14873869',
                published: true,
                variations: {
                    color: [],
                    size: [],
                },
                categoryPaths: [
                    'cat16510006||Electrohogar/cat7090035||Línea blanca/CATG19018||Refrigeración/cat3205||Refrigeradores/cat4091||Refrigerador Side by Side',
                ],
                sellerName: 'FALABELLA',
                mediaUrls: [
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/14873869_1/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/14873869_10/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/14873869_11/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/14873869_12/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/14873869_13/public',
                ],
                adInfo: 'v2_Z5o4Bv9aW-T3XTxvHqbQMpoLclPeCLE0ENbSZ3UNQp0Limq7ResVmm0cgp8J47ClyVdss2VkzodqLS487oj71Tgv-md6f7xOVSivyCPfiaAlHzlqs06Io1a6hKppZcnEGlVDUSrYYG4lqWg0jS4Vz_i6DLvodD6ty1IZ2iuGpo8=_ark194b7cea57521a27c',
                adIdentifier: 'v2_Z5o4Bv9aW-T3XTxvHqbQMpoLclPeCLE0ENbSZ3UNQp0Limq7ResVmm0cgp8J47ClyVdss2VkzodqLS487oj71Tgv-md6f7xOVSivyCPfiaAlHzlqs06Io1a6hKppZcnEGlVDUSrYYG4lqWg0jS4Vz_i6DLvodD6ty1IZ2iuGpo8=_ark194b7cea57521a27c',
                measurement: {},
            },
            {
                id: '16839818',
                productId: '16839818',
                displayName: 'Refrigerador Side by Side 490 Litros No Frost RS52B3000M9/ZS Samsung',
                brand: 'SAMSUNG',
                offeringId: '16839818',
                rating: 4.6,
                totalReviews: 61,
                prices: [
                    {
                        label: '',
                        originalPrice: '489.990',
                        symbol: '$ ',
                        type: 1,
                        isLoyalty: false,
                        opportunidadUnica: true,
                        crossed: false,
                    },
                    {
                        label: '',
                        originalPrice: '499.990',
                        symbol: '$ ',
                        type: 3,
                        isLoyalty: false,
                        opportunidadUnica: false,
                        crossed: false,
                    },
                    {
                        label: '',
                        originalPrice: '899.990',
                        symbol: '$ ',
                        type: 2,
                        isLoyalty: false,
                        opportunidadUnica: false,
                        crossed: true,
                    },
                ],
                badges: [
                    {
                        position: 2,
                        label: '-46%',
                        styles: {
                            backgroundColor: '#EB0029',
                            textColor: '#ffffff',
                        },
                    },
                ],
                discountBadge: {
                    label: '-46%',
                    styles: {
                        backgroundColor: '#EB0029',
                        textColor: '#ffffff',
                    },
                },
                meatStickers: [],
                url: 'https://www.falabella.com/falabella-cl/product/16839818/Refrigerador-Side-by-Side-490-Litros-No-Frost-RS52B3000M9-ZS-Samsung/16839818',
                published: true,
                variations: {
                    color: [],
                    size: [],
                },
                categoryPaths: [
                    'cat16510006||Electrohogar/cat7090035||Línea blanca/CATG19018||Refrigeración/cat3205||Refrigeradores/cat4091||Refrigerador Side by Side',
                ],
                sellerName: 'FALABELLA',
                mediaUrls: [
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/16839818_1/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/16839818_2/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/16839818_3/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/16839818_4/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/16839818_5/public',
                ],
                adInfo: 'v2_U8eplRqg16AlVECbr498CZoLclPeCLE0ENbSZ3UNQp0Limq7ResVmm0cgp8J47ClyVdss2VkzodqLS487oj71Tgv-md6f7xOVSivyCPfiaDnUmsHSsnBiXZZs0sLnD-JUfzupgwCiJLZzaNy7Q6jznq7FG_p5M7YINdCGGJdrOM=_ark194b7cea57521a27c',
                adIdentifier: 'v2_U8eplRqg16AlVECbr498CZoLclPeCLE0ENbSZ3UNQp0Limq7ResVmm0cgp8J47ClyVdss2VkzodqLS487oj71Tgv-md6f7xOVSivyCPfiaDnUmsHSsnBiXZZs0sLnD-JUfzupgwCiJLZzaNy7Q6jznq7FG_p5M7YINdCGGJdrOM=_ark194b7cea57521a27c',
                measurement: {},
            },
            {
                id: '16880707',
                productId: '16880707',
                displayName: 'Refrigerador Top Mount Freezer Water Dispenser 341Lts No Frost RT35CG5540S9ZS Samsung',
                brand: 'SAMSUNG',
                offeringId: '16880707',
                rating: 4,
                totalReviews: 60,
                prices: [
                    {
                        label: '',
                        originalPrice: '379.990',
                        symbol: '$ ',
                        type: 3,
                        isLoyalty: false,
                        opportunidadUnica: false,
                        crossed: false,
                    },
                    {
                        label: '',
                        originalPrice: '609.990',
                        symbol: '$ ',
                        type: 2,
                        isLoyalty: false,
                        opportunidadUnica: false,
                        crossed: true,
                    },
                ],
                badges: [
                    {
                        position: 2,
                        label: '-38%',
                        styles: {
                            backgroundColor: '#EB0029',
                            textColor: '#ffffff',
                        },
                    },
                ],
                discountBadge: {
                    label: '-38%',
                    styles: {
                        backgroundColor: '#EB0029',
                        textColor: '#ffffff',
                    },
                },
                meatStickers: [],
                url: 'https://www.falabella.com/falabella-cl/product/16880707/Refrigerador-Top-Mount-Freezer-Water-Dispenser-341Lts-No-Frost-RT35CG5540S9ZS-Samsung/16880707',
                published: true,
                variations: {
                    color: [],
                    size: [],
                },
                categoryPaths: [
                    'cat16510006||Electrohogar/cat7090035||Línea blanca/CATG19018||Refrigeración/cat3205||Refrigeradores/CATG19019||Top Freezer',
                    'cat16510006||Electrohogar/cat7090035||Línea blanca/CATG19018||Refrigeración/cat3205||Refrigeradores',
                ],
                sellerName: 'FALABELLA',
                mediaUrls: [
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/16880707_1/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/16880707_2/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/16880707_3/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/16880707_4/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/16880707_5/public',
                ],
                adInfo: 'v2_yaQvy2HgMByc4fdCYjm_c5oLclPeCLE0ENbSZ3UNQp0Limq7ResVmm0cgp8J47ClyVdss2VkzodqLS487oj71Tgv-md6f7xOVSivyCPfiaAlHzlqs06Io1a6hKppZcnEDNJg-Xr1FIKbxgumfxGGQr6Uu-KYldNF2aMKyCgWvIs=_ark194b7cea57521a27c',
                adIdentifier: 'v2_yaQvy2HgMByc4fdCYjm_c5oLclPeCLE0ENbSZ3UNQp0Limq7ResVmm0cgp8J47ClyVdss2VkzodqLS487oj71Tgv-md6f7xOVSivyCPfiaAlHzlqs06Io1a6hKppZcnEDNJg-Xr1FIKbxgumfxGGQr6Uu-KYldNF2aMKyCgWvIs=_ark194b7cea57521a27c',
                measurement: {},
            },
            {
                id: '17336224',
                productId: '17336224',
                displayName: 'Refrigerador Side By Side 560 Litros RS57DG4100B4ZS Samsung',
                brand: 'SAMSUNG',
                offeringId: '17336224',
                rating: 5,
                totalReviews: 1,
                prices: [
                    {
                        label: '',
                        originalPrice: '819.990',
                        symbol: '$ ',
                        type: 3,
                        isLoyalty: false,
                        opportunidadUnica: false,
                        crossed: false,
                    },
                    {
                        label: '',
                        originalPrice: '1.099.990',
                        symbol: '$ ',
                        type: 2,
                        isLoyalty: false,
                        opportunidadUnica: false,
                        crossed: true,
                    },
                ],
                badges: [
                    {
                        position: 1,
                        label: 'HOT SALE',
                        styles: {
                            backgroundColor: '#000000',
                            textColor: '#FFFFFF',
                        },
                    },
                    {
                        position: 2,
                        label: '-25%',
                        styles: {
                            backgroundColor: '#EB0029',
                            textColor: '#ffffff',
                        },
                    },
                ],
                discountBadge: {
                    label: '-25%',
                    styles: {
                        backgroundColor: '#EB0029',
                        textColor: '#ffffff',
                    },
                },
                meatStickers: [
                    {
                        label: 'HOT SALE',
                        type: 'event',
                        styles: {
                            backgroundColor: '#000000',
                            textColor: '#FFFFFF',
                        },
                    },
                ],
                url: 'https://www.falabella.com/falabella-cl/product/17336224/Refrigerador-Side-By-Side-560-Litros-RS57DG4100B4ZS-Samsung/17336224',
                published: true,
                variations: {
                    color: [],
                    size: [],
                },
                categoryPaths: [
                    'cat16510006||Electrohogar/cat7090035||Línea blanca/CATG19018||Refrigeración/cat3205||Refrigeradores/cat4091||Refrigerador Side by Side',
                ],
                sellerName: 'FALABELLA',
                mediaUrls: [
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/17336224_1/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/17336224_2/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/17336224_3/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/17336224_4/public',
                    'https://imagedelivery.net/4fYuQyy-r8_rpBpcY7lH_A/falabellaCL/17336224_5/public',
                ],
                adInfo: 'v2_REgT0kTtob7CS9Yp3V1QuZoLclPeCLE0ENbSZ3UNQp0Limq7ResVmm0cgp8J47ClyVdss2VkzodqLS487oj71Tgv-md6f7xOVSivyCPfiaDnUmsHSsnBiXZZs0sLnD-JlFnaqPfQV0ZXyvgyyT4PTvi6DLvodD6ty1IZ2iuGpo8=_ark194b7cea57521a27c',
                adIdentifier: 'v2_REgT0kTtob7CS9Yp3V1QuZoLclPeCLE0ENbSZ3UNQp0Limq7ResVmm0cgp8J47ClyVdss2VkzodqLS487oj71Tgv-md6f7xOVSivyCPfiaDnUmsHSsnBiXZZs0sLnD-JlFnaqPfQV0ZXyvgyyT4PTvi6DLvodD6ty1IZ2iuGpo8=_ark194b7cea57521a27c',
                measurement: {},
            },
        ],
        promotionText: 'Renueva tu hogar con Samsung',
        merchantName: 'FACL_SAMSUNG-ELECTRONIC',
        image: 'https://res.cloudinary.com/dvkezxchf/image/upload/xhxemcm84dmgvcxflwsh',
        landingPageUrl: 'https://www.falabella.com/falabella-cl/category/cat3205/Refrigeradores?facetSelected=true&f.derived.variant.sellerId=FALABELLA&f.product.brandName=samsung',
        mabayaAdInfo: 'v2_cjbdsVD93YXipOtXiKPqf5oLclPeCLE0ENbSZ3UNQp0Limq7ResVmm0cgp8J47ClyVdss2VkzodqLS487oj71XNBpE4XGpwG0585Yczex2VXMvd9PMWuEP931RvcnpFWw1CDetsAqSbMGBScQbAYVA==_ark194b7cea57521a27c',
        scndImg: 'https://mabaya-public-uploads.s3.amazonaws.com/images/headlineCampaigns/3895076_2025-01-14-13-57-36-446.jpg',
        scndImg2: 'https://res.cloudinary.com/dvkezxchf/image/upload/h_230,w_238/wl3uog79odffzkgsubvw',
        scndImages: {},
    },
    FETCH_ADS_BY_PERCENTAGE: {
        NINETY: 90,
        ONE_HOUNDRED_FIFTY: 150,
    },
    RANGE_DERIVED_PRICE: {
        FROM: 'DESDE $',
        TO: 'HASTA $',
    },
    AD_TYPES: {
        display: 'display',
        brands: 'brands',
        strip: 'strip',
    },
    CMR_ICON: {
        noDebitCountries: ['sodimac-cl', 'falabella-cl', 'falabella-pe'],
    }
};

var PRICE_TYPES;
(function (PRICE_TYPES) {
    PRICE_TYPES["cmrPrice"] = "cmrPrice";
    PRICE_TYPES["internetPrice"] = "internetPrice";
    PRICE_TYPES["normalPrice"] = "normalPrice";
})(PRICE_TYPES || (PRICE_TYPES = {}));
var UNIT_PRICE_TYPES;
(function (UNIT_PRICE_TYPES) {
    UNIT_PRICE_TYPES["M2"] = "M2";
})(UNIT_PRICE_TYPES || (UNIT_PRICE_TYPES = {}));
var atgPriceTypeMap = {
    1: PRICE_TYPES.cmrPrice,
    // It seems 2 should be 3 and 3 should be 2
    2: PRICE_TYPES.internetPrice,
    3: PRICE_TYPES.normalPrice,
    4: PRICE_TYPES.normalPrice,
};
var LABELS_MAP = {
    'Sub-total productos': '',
    'Monto final a pagar:': '',
    'Con CMR:': '',
    CMR: '',
};
var ICONS_MAP = {
    CMR: 'cmr-icon',
    'Con CMR:': 'cmr-icon',
    cmr: 'cmr-icon',
};
var getPriceType = function (type, atgPriceOverwrite) {
    if (typeof type === 'string') {
        return type;
    }
    if (atgPriceOverwrite)
        return atgPriceOverwrite[type] || '';
    return atgPriceTypeMap[type] || '';
};
var willBeCrossedOut = function (isNormalPriceCrossedOut, prices) {
    var isPriceCrossedOut = prices.length === 3 || (prices.length === 2 && !prices[0].icons);
    if (isNormalPriceCrossedOut && isPriceCrossedOut) {
        return true;
    }
    return false;
};
var getPriceToNumber = function (price, tenant) {
    var decimalSeparator = constants.DECIMAL_SEPARATOR[tenant];
    var dotSeparator = constants.DOT_SEPARATOR[tenant];
    var decimalRegex = new RegExp("\\".concat(decimalSeparator), 'ig');
    var dotRegex = new RegExp("\\".concat(dotSeparator), 'ig');
    var formatedPrice = price
        .replace(decimalRegex, '[DOT]')
        .replace(dotRegex, '')
        .replace('[DOT]', '.');
    return Number(formatedPrice);
};
var getLabel = function (label) {
    return typeof LABELS_MAP[label] === 'undefined' ? label : LABELS_MAP[label];
};
var getUnitPrice = function (unitPrice) {
    if (!unitPrice) {
        return null;
    }
    return unitPrice.type == UNIT_PRICE_TYPES.M2 ? "".concat(unitPrice.price, " m\u00B2") : null;
};
var mapATGPricesToCatalystPrices = function (prices, tenant, isDisabledPriceCrossed, toggles, atgPriceOverwrite) {
    var _a;
    if (prices === void 0) { prices = []; }
    if (tenant === void 0) { tenant = 'cl'; }
    if (isDisabledPriceCrossed === void 0) { isDisabledPriceCrossed = false; }
    prices.sort(function (a, b) {
        return (getPriceToNumber(a.purchasedPrice || a.originalPrice, tenant) -
            getPriceToNumber(b.purchasedPrice || b.originalPrice, tenant));
    });
    var isNormalPriceCrossedOut = (_a = toggles === null || toggles === void 0 ? void 0 : toggles.isNormalPriceCrossedOut) !== null && _a !== void 0 ? _a : false;
    var formattedPrice = prices.map(function (_a) {
        var label = _a.label, purchasedPrice = _a.purchasedPrice, originalPrice = _a.originalPrice, symbol = _a.symbol, type = _a.type, opportunidadUnica = _a.opportunidadUnica, unitPrice = _a.unitPrice;
        return {
            label: getLabel(label),
            price: [purchasedPrice || getUnitPrice(unitPrice) || originalPrice],
            symbol: symbol,
            icons: opportunidadUnica ? 'cmr-icon' : ICONS_MAP[label] || '',
            type: getPriceType(type, atgPriceOverwrite),
            crossed: false,
        };
    });
    if (formattedPrice.length) {
        var indexOfProduct = formattedPrice.length - 1;
        formattedPrice[indexOfProduct].crossed = willBeCrossedOut(isNormalPriceCrossedOut, formattedPrice);
        if (formattedPrice[indexOfProduct].crossed && isDisabledPriceCrossed) {
            formattedPrice.splice(indexOfProduct, 1);
        }
    }
    return formattedPrice;
};

var getPriceColor = function (_a) {
    var _b;
    var type = _a.type;
    var DEFAULT_COLOR = '#717171';
    var COLOR_UNIQUE_OPPORTUNITY = '#e4022d';
    var colorStylesForType = {
        cmrPrice: COLOR_UNIQUE_OPPORTUNITY,
        normalPrice: DEFAULT_COLOR,
        internetPrice: DEFAULT_COLOR,
    };
    var colorStyle = (_b = colorStylesForType[type]) !== null && _b !== void 0 ? _b : DEFAULT_COLOR;
    return colorStyle;
};
var getTextDecoration = function (_a) {
    var _b;
    var type = _a.type;
    var DEFAULT_TEXT_DECORATION = 'none';
    var colorStylesForType = {
        internetPrice: 'line-through',
    };
    var textDecoration = (_b = colorStylesForType[type]) !== null && _b !== void 0 ? _b : DEFAULT_TEXT_DECORATION;
    return textDecoration;
};
var getStyles = function (priceItem) {
    return {
        color: getPriceColor(priceItem),
        textDecoration: getTextDecoration(priceItem),
    };
};

var isWindow = function () { return typeof window !== 'undefined'; };
var getCookie = function (cookieName) {
    if (!isWindow())
        return '';
    var result = document.cookie.match("(^|;)\\s*".concat(cookieName, "\\s*=\\s*([^;]+)"));
    return result ? result.pop() : '';
};
var validateMaxProducts = function (maxProducts) {
    return maxProducts >= constants.MIN_MAX_SB_PRODUCTS[0] &&
        maxProducts <= constants.MIN_MAX_SB_PRODUCTS[1]
        ? maxProducts
        : constants.MIN_MAX_SB_PRODUCTS[1];
};
var getCountryFromProductURL = function (url) {
    var brandCountryMatch = /(falabella|sodimac)-([a-z]{2,3})/i.exec(url);
    if (brandCountryMatch && brandCountryMatch[0])
        return brandCountryMatch[0].toLowerCase();
    return null;
};
var getShowDebitCardIcon = function (regionCode) {
    var debitCardCountries = constants.CMR_ICON.noDebitCountries;
    if (regionCode && debitCardCountries.includes(regionCode.toLowerCase())) {
        return false;
    }
    return true;
};

var getSponsoredProductsUrl = function (_a) {
    var _b;
    var providedId = _a.providedId, providedParam = _a.providedParam, categoryName = _a.categoryName, zones = _a.zones, regionCode = _a.regionCode, deviceType = _a.deviceType, site = _a.site, store = _a.store, urlBase = _a.urlBase, // = 'https://beta.falabella.com',
    facetParams = _a.facetParams, _c = _a.adType, adType = _c === void 0 ? 'display' : _c, _d = _a.averageOverallRating, averageOverallRating = _d === void 0 ? 4 : _d, categoryId = _a.categoryId, gender = _a.gender, multiWidget = _a.multiWidget, minPrice = _a.minPrice, brand = _a.brand, isOrchestratorEnabled = _a.isOrchestratorEnabled, priceGroupId = _a.priceGroupId, politicalId = _a.politicalId;
    var isDebugAdsEnabled = getCookie('isDebugAdsEnabled') === 'true';
    // Use /s/browse/v1/sponsored-ads for brands or display adType on category/search pages
    var isCategoryOrSearchPage = categoryName || (providedParam && providedParam !== 'productId');
    var useSponsoredAdsEndpoint = adType === 'brands' || (adType === 'display' && isCategoryOrSearchPage);
    var queryParams = __assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign((_b = {}, _b[providedParam] = providedId, _b.categoryName = categoryName, _b), facetParams), { zones: zones, deviceType: deviceType, site: site, store: store, adType: adType, 'f.range.product.averageOverallRating': averageOverallRating, channel: deviceType, categoryId: categoryId }), (gender && { gender: gender })), { debug: isDebugAdsEnabled }), (multiWidget !== undefined ? { multiWidget: multiWidget } : {})), (typeof minPrice === 'string' && {
        'f.range.derived.price.search': minPrice,
    })), { brand: brand }), (!useSponsoredAdsEndpoint && {
        isOrchestratorEnabled: isOrchestratorEnabled ? 'true' : 'false',
    })), { priceGroupId: priceGroupId, politicalId: politicalId });
    var query = constructQuery(queryParams);
    var basePath = useSponsoredAdsEndpoint
        ? '/s/browse/v1/sponsored-ads'
        : '/s/browse/v1/sponsored-products';
    var URL = "".concat(urlBase).concat(basePath, "/").concat(regionCode).concat(query);
    return URL;
};

var useSponsoredProducts = function (_a) {
    var _b = useState(null), horizontalProduct = _b[0], setHorizontalProduct = _b[1];
    var _c = useState(null), verticalProduct = _c[0], setVerticalProduct = _c[1];
    var _d = useState({}), productList = _d[0], setproductList = _d[1];
    var _e = useState(false), loading = _e[0], setLoading = _e[1];
    var fetchSponsoredProducts = function (_a) {
        var config = _a.config;
        return __awaiter(void 0, void 0, void 0, function () {
            var productUrl, response, data, productsWithMedia, singleProduct, error_1;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 3, 4, 5]);
                        setLoading(true);
                        config.brand = config === null || config === void 0 ? void 0 : config.brandName;
                        productUrl = getSponsoredProductsUrl(config);
                        return [4 /*yield*/, fetch(productUrl)];
                    case 1:
                        response = _b.sent();
                        return [4 /*yield*/, response.json()];
                    case 2:
                        data = _b.sent();
                        if (!data.sponsoredProducts || data.sponsoredProducts.length === 0) {
                            setHorizontalProduct(null);
                            setVerticalProduct(null);
                            setproductList([]);
                            return [2 /*return*/, null];
                        }
                        productsWithMedia = data.sponsoredProducts.filter(function (product) { return product.mediaUrls && product.mediaUrls.length > 0; });
                        if (productsWithMedia.length === 0) {
                            setproductList([]);
                            setHorizontalProduct(null);
                            setVerticalProduct(null);
                            return [2 /*return*/, null];
                        }
                        if (productsWithMedia.length === 1) {
                            singleProduct = productsWithMedia[0];
                            setproductList(productsWithMedia);
                            setHorizontalProduct(singleProduct);
                            setVerticalProduct(singleProduct);
                            return [2 /*return*/, singleProduct];
                        }
                        setHorizontalProduct(productsWithMedia[0]);
                        setVerticalProduct(productsWithMedia[1]);
                        setproductList(productsWithMedia);
                        return [2 /*return*/, productsWithMedia];
                    case 3:
                        error_1 = _b.sent();
                        console.error('Error fetching sponsored products:', error_1);
                        setHorizontalProduct(null);
                        setVerticalProduct(null);
                        return [2 /*return*/, null];
                    case 4:
                        setLoading(false);
                        return [7 /*endfinally*/];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    return {
        loading: loading,
        horizontalProduct: horizontalProduct,
        verticalProduct: verticalProduct,
        productList: productList,
        fetchSponsoredProducts: fetchSponsoredProducts,
    };
};

var useSponsoredStrip = function (_a) {
    var _b = useState({}), stripProducts = _b[0], setStripProduct = _b[1];
    var fetchSponsoredStrip = function (_a) {
        var config = _a.config;
        return __awaiter(void 0, void 0, void 0, function () {
            var productPrice, maxPrice, tempProductPrice, productUrl, response, data, productsWithMedia, singleProduct, error_1;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 3, , 4]);
                        productPrice = config === null || config === void 0 ? void 0 : config.productPrice;
                        maxPrice = undefined;
                        if (typeof productPrice !== 'undefined') {
                            tempProductPrice = productPrice;
                            productPrice = Math.floor(tempProductPrice * (constants.FETCH_ADS_BY_PERCENTAGE.NINETY / 100));
                            maxPrice = Math.floor(tempProductPrice * (constants.FETCH_ADS_BY_PERCENTAGE.ONE_HOUNDRED_FIFTY / 100));
                        }
                        config.multiWidget = false;
                        config.adType = constants.AD_TYPES.strip;
                        config.brand = config === null || config === void 0 ? void 0 : config.brandName;
                        config.minPrice = "".concat(constants.RANGE_DERIVED_PRICE.FROM).concat(productPrice).concat(maxPrice ? " ".concat(constants.RANGE_DERIVED_PRICE.TO).concat(maxPrice) : '');
                        productUrl = getSponsoredProductsUrl(config);
                        return [4 /*yield*/, fetch(productUrl)];
                    case 1:
                        response = _b.sent();
                        return [4 /*yield*/, response.json()];
                    case 2:
                        data = _b.sent();
                        if (!data.sponsoredProducts || data.sponsoredProducts.length === 0) {
                            setStripProduct([]);
                            return [2 /*return*/, null];
                        }
                        productsWithMedia = data.sponsoredProducts.filter(function (product) { return product.mediaUrls && product.mediaUrls.length > 0; });
                        if (productsWithMedia.length === 0) {
                            setStripProduct([]);
                            return [2 /*return*/, null];
                        }
                        if (productsWithMedia.length === 1) {
                            singleProduct = productsWithMedia[0];
                            setStripProduct(productsWithMedia);
                            return [2 /*return*/, singleProduct];
                        }
                        setStripProduct(productsWithMedia);
                        return [2 /*return*/, productsWithMedia];
                    case 3:
                        error_1 = _b.sent();
                        console.error('Error fetching sponsored products:', error_1);
                        return [2 /*return*/, null];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    return {
        stripProducts: stripProducts,
        fetchSponsoredStrip: fetchSponsoredStrip,
    };
};

var useSponsoredBrandProducts = function () {
    var _a = useState(null), products = _a[0], setProducts = _a[1];
    var _b = useState(null), brandDetails = _b[0], setBrandDetails = _b[1];
    var _c = useState(false), loading = _c[0], setLoading = _c[1];
    var fetchSponsoredBrandProducts = function (_a) {
        var config = _a.config, _b = _a.maxProducts, maxProducts = _b === void 0 ? 3 : _b, onFetchComplete = _a.onFetchComplete;
        return __awaiter(void 0, void 0, void 0, function () {
            var productUrl, response, data, _c, sponsoredProducts, brandData, productsWithMedia, finalProducts, error_1;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        _d.trys.push([0, 3, 4, 5]);
                        setLoading(true);
                        productUrl = getSponsoredProductsUrl(config);
                        if (!productUrl) {
                            setProducts(null);
                            setBrandDetails(null);
                            return [2 /*return*/, null];
                        }
                        return [4 /*yield*/, fetch(productUrl)];
                    case 1:
                        response = _d.sent();
                        return [4 /*yield*/, response.json()];
                    case 2:
                        data = _d.sent();
                        if (!(data === null || data === void 0 ? void 0 : data.sponsoredProducts) || data.sponsoredProducts.length === 0) {
                            setProducts(null);
                            setBrandDetails(null);
                            return [2 /*return*/, null];
                        }
                        _c = data.sponsoredProducts, sponsoredProducts = _c === void 0 ? [] : _c, brandData = __rest(data, ["sponsoredProducts"]);
                        setBrandDetails(brandData);
                        productsWithMedia = sponsoredProducts.filter(function (product) { return product.mediaUrls && product.mediaUrls.length > 0; });
                        if (productsWithMedia.length === 0) {
                            setProducts(null);
                            return [2 /*return*/, null];
                        }
                        finalProducts = productsWithMedia.slice(0, maxProducts);
                        setProducts(finalProducts);
                        if (onFetchComplete)
                            onFetchComplete(data);
                        return [2 /*return*/, finalProducts];
                    case 3:
                        error_1 = _d.sent();
                        // keep previous behavior: reset and return null
                        // eslint-disable-next-line no-console
                        console.error('Error fetching sponsored brand products:', error_1);
                        setProducts(null);
                        setBrandDetails(null);
                        return [2 /*return*/, null];
                    case 4:
                        setLoading(false);
                        return [7 /*endfinally*/];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    return {
        loading: loading,
        products: products,
        brandDetails: brandDetails,
        fetchSponsoredBrandProducts: fetchSponsoredBrandProducts,
    };
};

var defaultValue = {
    regionCode: 'cl',
    deviceType: 'mobile',
    facetParams: {},
    toggles: {
        disableSponsoredDisplay: false,
        disableSponsoredBrands: false,
        disableSponsoredProducts: false,
        disableSponsoredStrip: false,
        disableSponsoredCarousel: false,
        disableSponsoredProductBanner: false
    },
    configurations: {
        priceHiddenCategoriesIds: [],
    },
};
var SponsoredProductsContext = createContext(defaultValue);
function SponsoredProvider(_a) {
    var _b = _a.config, config = _b === void 0 ? defaultValue : _b, children = _a.children;
    var _c = useSponsoredProducts(), fetchSponsoredProducts = _c.fetchSponsoredProducts, horizontalProduct = _c.horizontalProduct, verticalProduct = _c.verticalProduct, productList = _c.productList;
    var _d = useSponsoredBrandProducts(), brandProducts = _d.products, brandDetails = _d.brandDetails, fetchSponsoredBrandProducts = _d.fetchSponsoredBrandProducts;
    var _e = useSponsoredStrip(), stripProducts = _e.stripProducts, fetchSponsoredStrip = _e.fetchSponsoredStrip;
    var value = __assign(__assign({}, config), { horizontalProduct: horizontalProduct, verticalProduct: verticalProduct, productList: productList, fetchSponsoredProducts: fetchSponsoredProducts, stripProducts: stripProducts, fetchSponsoredStrip: fetchSponsoredStrip, brandProducts: brandProducts, brandDetails: brandDetails, fetchSponsoredBrandProducts: fetchSponsoredBrandProducts });
    useEffect(function () {
        var _a, _b, _c, _d, _e;
        if (!((_a = config.toggles) === null || _a === void 0 ? void 0 : _a.disableSponsoredBrands)) {
            var brandConfig = {
                providedParam: config.providedParam,
                categoryName: config.categoryName,
                regionCode: config.regionCode,
                deviceType: config.deviceType,
                site: config.site,
                isOrchestratorEnabled: config.isOrchestratorEnabled,
                priceGroupId: config.priceGroupId,
                politicalId: config.politicalId,
                store: config.store,
                urlBase: config.urlBase,
                facetParams: config.facetParams,
                adType: 'brands',
                averageOverallRating: null,
                categoryId: config.categoryId,
            };
            fetchSponsoredBrandProducts({
                config: brandConfig,
                maxProducts: validateMaxProducts(3),
                onFetchComplete: undefined,
            });
        }
        if (!((_b = config.toggles) === null || _b === void 0 ? void 0 : _b.disableSponsoredProducts) &&
            !((_c = config.toggles) === null || _c === void 0 ? void 0 : _c.disableSponsoredDisplay) &&
            !((_d = config.toggles) === null || _d === void 0 ? void 0 : _d.disableSponsoredProductBanner)) {
            fetchSponsoredProducts({ config: config });
        }
        if (!((_e = config.toggles) === null || _e === void 0 ? void 0 : _e.disableSponsoredStrip) && typeof config.productPrice !== 'undefined') {
            fetchSponsoredStrip({ config: config });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config.facetParams]);
    return (React__default.createElement(SponsoredProductsContext.Provider, { value: value }, children));
}

var SponsoredProductPrice = function (_a) {
    var badge = _a.badge, prices = _a.prices, _b = _a.isBrand, isBrand = _b === void 0 ? false : _b, containerStyles = _a.containerStyles, _c = _a.isDisabledPriceCrossed, isDisabledPriceCrossed = _c === void 0 ? false : _c, _d = _a.urlProduct, urlProduct = _d === void 0 ? '' : _d;
    var showDebitCardIcon = getShowDebitCardIcon(getCountryFromProductURL(urlProduct) || 'falabella-cl');
    var priceLists = mapATGPricesToCatalystPrices(prices, 'cl', isDisabledPriceCrossed);
    var deviceType = useContext(SponsoredProductsContext).deviceType;
    var isMobile = deviceType === 'mobile';
    var brandMobileClass = isBrand && isMobile ? styles$2.brandMobile : '';
    var normalPriceClass = isBrand ? styles$2.normalPriceBrand : '';
    return (React__default.createElement("div", { className: "".concat(styles$2['price-list'], " ").concat(brandMobileClass), style: __assign({ gap: '4px' }, containerStyles) },
        (priceLists === null || priceLists === void 0 ? void 0 : priceLists.length) > 0 && priceLists[0].icons === 'cmr-icon' && React__default.createElement(CmrIcon, { showDebitCardIcon: showDebitCardIcon }),
        (priceLists === null || priceLists === void 0 ? void 0 : priceLists.length) > 0 &&
            priceLists.map(function (priceItem, index) { return (React__default.createElement("div", { key: index, className: styles$2['price-item'] },
                priceItem.price.map(function (price, idx) {
                    var currentClassName = "".concat(styles$2.price, " ").concat(styles$2[priceItem.type], " ").concat(priceItem.crossed ? styles$2['crossed'] : '', " ").concat(normalPriceClass);
                    var currentStyles = getStyles(priceItem);
                    return (React__default.createElement("div", { key: idx, className: currentClassName, style: currentStyles },
                        React__default.createElement("span", { className: styles$2['dollar'] }, priceItem.symbol),
                        React__default.createElement("span", null, price),
                        index === 0 && badge.length > 0 && !(isMobile && isBrand) && (React__default.createElement(SponsoredProductBadge, { badge: badge }))));
                }),
                priceItem.label && React__default.createElement("span", { className: styles$2['label'] }, priceItem.label))); })));
};

var css_248z$7 = ".styles-module_reviewCount__3GFJ7{color:#bbb;font-family:Lato;font-size:12px;font-style:normal;font-weight:400;line-height:15px;margin-left:4px}@media (max-width:430px){.styles-module_reviewCount__3GFJ7{color:#bbb;font-family:Lato;font-size:10px;font-style:normal;font-weight:400;line-height:15px;margin-left:1px}}@media (max-width:350px){.styles-module_reviewCount__3GFJ7{color:#bbb;font-family:Lato;font-size:8px;font-style:normal;font-weight:400;line-height:15px;margin-left:1px}}";
var styles = {"reviewCount":"styles-module_reviewCount__3GFJ7"};
styleInject(css_248z$7);

var SponsoredProductReview = function (_a) {
    var value = _a.value;
    return (React__default.createElement("span", { "data-rating": value, className: styles['reviewCount'] }, "(".concat(value, ")")));
};

var css_248z$6 = ".banner-module_horizontal-banner__UJRmW{background:#fff;border:1px solid #eee;border-radius:8px;height:auto;width:633px}@media (min-width:0px) and (max-width:1024px){.banner-module_horizontal-banner__UJRmW{height:auto;width:375px}}.banner-module_vertical-banner__pb5Yd{background:#fff;border:1px solid #eee;border-radius:8px;cursor:pointer;width:257px}.banner-module_horizontal-banner-layout__Ikwk5{cursor:pointer;display:flex;flex-direction:row;margin:8px 8px 16px}.banner-module_vertical-banner-layout__UG6Rj{align-items:flex-start;display:flex;flex-direction:column;gap:12px;padding:12px 8px 12px 12px}.banner-module_horizontal-product-details__cnY89{display:flex;flex-direction:column;gap:7px;margin-left:15px;margin-top:12px;width:-moz-max-content;width:max-content}@media (min-width:0px) and (max-width:1024px){.banner-module_horizontal-product-details__cnY89{margin-top:6px;width:182px}}.banner-module_vertical-product-details__0g-S1{display:flex;flex-direction:column;gap:7px;margin-top:12px;width:100%}.banner-module_sponsored-text-vertical__7WBFs{color:#767676;font-family:Lato;font-size:14px;font-style:normal;font-weight:400;letter-spacing:1px;line-height:12px}.banner-module_rating-styles__0YTnd{display:flex;flex-direction:row;height:21px}.banner-module_sponsored-text__iuZSo{align-items:flex-end;color:#767676;display:flex;font-family:Lato;font-size:12px;font-weight:400;letter-spacing:1px;line-height:14px;position:relative;right:86px}@media (min-width:0px) and (max-width:1024px){.banner-module_sponsored-text__iuZSo{margin-right:10px;right:90px}}.banner-module_sponsored-title__iuQLE{display:flex;flex-direction:row;justify-content:space-between;width:432px}@media (min-width:0px) and (max-width:1024px){.banner-module_sponsored-title__iuQLE{width:182px}}";
var banner = {"horizontal-banner":"banner-module_horizontal-banner__UJRmW","vertical-banner":"banner-module_vertical-banner__pb5Yd","horizontal-banner-layout":"banner-module_horizontal-banner-layout__Ikwk5","vertical-banner-layout":"banner-module_vertical-banner-layout__UG6Rj","horizontal-product-details":"banner-module_horizontal-product-details__cnY89","vertical-product-details":"banner-module_vertical-product-details__0g-S1","sponsored-text-vertical":"banner-module_sponsored-text-vertical__7WBFs","rating-styles":"banner-module_rating-styles__0YTnd","sponsored-text":"banner-module_sponsored-text__iuZSo","sponsored-title":"banner-module_sponsored-title__iuQLE"};
styleInject(css_248z$6);

var labels = {
    sponsoredText: 'Patrocinado',
};

var setSponsoredDisplayDigitalDataRender = function (isVerticalBanner, product) {
    var SponsoredDisplayPosition = isVerticalBanner ? 'side' : 'bottom';
    var eventToDigitalData = new CustomEvent('DDXLPSponsoredDisplayRender', {
        bubbles: true,
        detail: {
            payload: "".concat(product === null || product === void 0 ? void 0 : product.id, "|").concat(SponsoredDisplayPosition),
        },
    });
    window.dispatchEvent(eventToDigitalData);
};
var setSponsoredDisplayDigitalDataRenderPDP = function (product, isStrip) {
    if (!product)
        return;
    var sdComponent = isStrip ? 'SD_Strip' : 'SD';
    var eventToDigitalData = new CustomEvent('DDPDPSponsoredDisplayRender', {
        bubbles: true,
        detail: {
            payload: "".concat(sdComponent, "|").concat(product === null || product === void 0 ? void 0 : product.id),
        },
    });
    window.dispatchEvent(eventToDigitalData);
};

var SponsoredProductBanner = function (_a) {
    var _b, _c;
    var onBannerClick = _a.onBannerClick, isVerticalBanner = _a.isVerticalBanner, product = _a.product; _a.store;
    var selectedProduct = product;
    var meatStickers = (_b = selectedProduct === null || selectedProduct === void 0 ? void 0 : selectedProduct.meatStickers) !== null && _b !== void 0 ? _b : [];
    var _e = useState(false), hasBeenInView = _e[0], setHasBeenInView = _e[1];
    var _f = useState(false), isWindowDefined = _f[0], setIsWindowDefined = _f[1];
    var _g = useInView({
        threshold: 0.3,
        initialInView: false,
    }), ref = _g.ref, inView = _g.inView;
    useEffect(function () {
        var checkWindow = setInterval(function () {
            if (typeof window !== 'undefined') {
                setIsWindowDefined(true);
                clearInterval(checkWindow);
            }
        }, 200);
        return function () { return clearInterval(checkWindow); };
    }, []);
    useEffect(function () {
        if (isWindowDefined) {
            setSponsoredDisplayDigitalDataRender(isVerticalBanner, product);
        }
    }, [isWindowDefined, isVerticalBanner, product]);
    useEffect(function () {
        if (inView && !hasBeenInView && selectedProduct) {
            try {
                onBannerClick(selectedProduct, 'impression', 'display');
                setHasBeenInView(true);
            }
            catch (error) {
                console.error('SponsoredProductBanner: Error triggering impression:', error);
            }
        }
    }, [inView, hasBeenInView, selectedProduct, onBannerClick]);
    var handleClick = useCallback(function () {
        if (onBannerClick) {
            onBannerClick(selectedProduct, 'visit', 'display');
            onBannerClick(selectedProduct, 'click', 'display');
        }
    }, [onBannerClick, selectedProduct]);
    if (!selectedProduct) {
        return null;
    }
    var currentUrl = selectedProduct === null || selectedProduct === void 0 ? void 0 : selectedProduct.url;
    var bannerStyles = isVerticalBanner ? 'vertical' : 'horizontal';
    var handleClickProduct = function (e) {
        var shouldOpenInNewTab = e.metaKey || e.ctrlKey;
        if (shouldOpenInNewTab) {
            window.open(currentUrl, '_blank');
            e.preventDefault();
        }
        handleClick();
    };
    var handleAuxClickProduct = function (e) {
        var shouldOpenInNewTab = e.button === 1;
        if (shouldOpenInNewTab) {
            window.open(currentUrl, '_blank');
            e.preventDefault();
            handleClick();
        }
    };
    return (React__default.createElement("div", { ref: ref, className: banner["".concat(bannerStyles, "-banner")] },
        React__default.createElement("a", { href: currentUrl, onClick: handleClickProduct, onAuxClick: handleAuxClickProduct, style: {
                textDecoration: 'none',
            } },
            React__default.createElement("div", { className: banner["".concat(bannerStyles, "-banner-layout")] },
                isVerticalBanner && (React__default.createElement("div", { className: banner['sponsored-text-vertical'] }, labels.sponsoredText)),
                React__default.createElement(SponsoredProductImage, { imageUrl: (_c = selectedProduct.mediaUrls) === null || _c === void 0 ? void 0 : _c[0], altText: "".concat(selectedProduct.brand, " - ").concat(selectedProduct.displayName), isVerticalBanner: isVerticalBanner, meatStickers: meatStickers }),
                React__default.createElement("div", { className: banner["".concat(bannerStyles, "-product-details")] },
                    React__default.createElement(SponsoredProductTitle, { brandName: selectedProduct.brand, productName: selectedProduct.displayName, sellerName: selectedProduct.sellerName }),
                    React__default.createElement("div", { className: banner['rating-styles'] },
                        React__default.createElement(SponsoredProductRating, { rating: selectedProduct.rating, maxRating: 5 }),
                        selectedProduct.totalReviews && (React__default.createElement(SponsoredProductReview, { value: selectedProduct.totalReviews }))),
                    React__default.createElement("div", { className: banner['sponsored-title'] },
                        React__default.createElement(SponsoredProductPrice, { badge: selectedProduct.badges, prices: selectedProduct.prices, priceList: [], urlProduct: selectedProduct.url }))),
                !isVerticalBanner && (React__default.createElement("div", { className: banner['sponsored-text'] }, labels.sponsoredText))))));
};

var classnames = {exports: {}};

/*!
	Copyright (c) 2018 Jed Watson.
	Licensed under the MIT License (MIT), see
	http://jedwatson.github.io/classnames
*/
(function (module) {
(function () {
	var hasOwn = {}.hasOwnProperty;
	function classNames () {
		var classes = '';
		for (var i = 0; i < arguments.length; i++) {
			var arg = arguments[i];
			if (arg) {
				classes = appendClass(classes, parseValue(arg));
			}
		}
		return classes;
	}
	function parseValue (arg) {
		if (typeof arg === 'string' || typeof arg === 'number') {
			return arg;
		}
		if (typeof arg !== 'object') {
			return '';
		}
		if (Array.isArray(arg)) {
			return classNames.apply(null, arg);
		}
		if (arg.toString !== Object.prototype.toString && !arg.toString.toString().includes('[native code]')) {
			return arg.toString();
		}
		var classes = '';
		for (var key in arg) {
			if (hasOwn.call(arg, key) && arg[key]) {
				classes = appendClass(classes, key);
			}
		}
		return classes;
	}
	function appendClass (value, newClass) {
		if (!newClass) {
			return value;
		}
		if (value) {
			return value + ' ' + newClass;
		}
		return value + newClass;
	}
	if (module.exports) {
		classNames.default = classNames;
		module.exports = classNames;
	} else {
		window.classNames = classNames;
	}
}());
}(classnames));
var cn = classnames.exports;

var css_248z$5 = ".styles-module_sponsored-brand-container__DYujK{background-color:#fff;border-radius:7px;box-shadow:0 4px 6px rgba(0,0,0,.1),0 1px 3px rgba(0,0,0,.06);box-sizing:border-box;display:grid;overflow:hidden;padding:3px 16px 16px;width:100%}.styles-module_sponsored-brand-container__DYujK.styles-module_vertical__1fzB9{padding:4px 0 14px}.styles-module_sponsored-brand-container__DYujK.styles-module_vertical__1fzB9 .styles-module_title__NtIAR{padding-left:12px;padding-right:12px;padding-top:4px}.styles-module_sponsored-brand-container__DYujK.styles-module_vertical__1fzB9 .styles-module_sponsored-label__mDmxC{padding-left:12px;padding-right:12px}.styles-module_sponsored-brand-container__DYujK .styles-module_product-details__-sYDj{flex:1;padding:12px 12px 12px 24px}@media (max-width:750px){.styles-module_sponsored-brand-container__DYujK .styles-module_product-details__-sYDj{padding:2px;width:120px}}@media (max-width:600px){.styles-module_sponsored-brand-container__DYujK .styles-module_product-details__-sYDj{padding:2px;width:100px}}@media (max-width:420px){.styles-module_sponsored-brand-container__DYujK .styles-module_product-details__-sYDj{padding:2px;width:65px}}@media (max-width:350px){.styles-module_sponsored-brand-container__DYujK .styles-module_product-details__-sYDj{padding:1.5px}}@media (max-width:300px){.styles-module_sponsored-brand-container__DYujK .styles-module_product-details__-sYDj{padding:1.5px}}@media (min-width:0px) and (max-width:1024px){.styles-module_sponsored-brand-container__DYujK{grid-template-columns:repeat(5,1fr);grid-template-rows:repeat(5,1fr)}}@media (min-width:768px){.styles-module_sponsored-brand-container__DYujK{grid-template-columns:repeat(auto-fill,minmax(200px,1fr));grid-template-rows:40px 1fr}}.styles-module_sponsored-brand-container__DYujK .styles-module_title__NtIAR{color:#4a4a4a;font-family:Lato;font-size:20px;font-weight:700;line-height:24px;margin:0 0 5px}.styles-module_sponsored-brand-container__DYujK .styles-module_rating-styles__1JEEC{display:flex;flex-direction:row;height:21px}.styles-module_sponsored-brand-container__DYujK .styles-module_sponsored-label__mDmxC{color:#767676;font-family:Lato;font-size:12px;font-weight:400;line-height:14px;margin:0;padding-bottom:4px}.styles-module_sponsored-brand-container__DYujK .styles-module_custom-image-desktop__-bdZD{background-position:50%;background-repeat:no-repeat;background-size:cover;color:#767676;cursor:pointer;display:grid;font-size:16px;grid-template-columns:78px 1fr;grid-template-rows:1fr;height:100%;margin-right:10px;padding:8px 0 0 12px;position:relative}.styles-module_sponsored-brand-container__DYujK .styles-module_logo-image-desktop__S5GQa{background-color:#fff;left:6px;-o-object-fit:contain;object-fit:contain;position:absolute;top:6px;width:80px}.styles-module_sponsored-brand-container__DYujK .styles-module_custom-image-mobile__59woK{background-position:50%;background-repeat:no-repeat;background-size:cover;color:#767676;cursor:pointer;display:grid;font-size:16px;grid-template-columns:78px 1fr;grid-template-rows:1fr;margin-top:5px;position:relative;width:100%}.styles-module_sponsored-brand-container__DYujK .styles-module_logo-mobile__bYR3-{background-color:#fff;left:6px;margin-right:7px;-o-object-fit:contain;object-fit:contain;position:absolute;top:6px;width:80px}.styles-module_border-radius__JU0dY{border-radius:7px;box-shadow:0 4px 6px rgba(0,0,0,.1),0 1px 3px rgba(0,0,0,.06)}.styles-module_sponsored-text__z-SjG{color:#767676;font-family:Lato;font-size:12px;font-style:normal;font-weight:400;letter-spacing:1px;line-height:14px;padding-top:4px}.styles-module_image-container__zEsIC{display:flex;flex-direction:column;margin-bottom:21px;margin-top:21px}.styles-module_title-container__idfo9{display:flex;flex-direction:column;grid-column:1/5;justify-content:space-between;margin-bottom:10px;margin-top:10px;width:100%}.styles-module_title-wrapper__-AlZF{align-items:center;display:flex;justify-content:space-between;width:100%}.styles-module_sponsored-brand-products-container__HNRe7{gap:16px;grid-column:1/5}.styles-module_sponsored-brand-wrapper__AzY8U{align-items:center;display:flex;justify-content:space-between;width:100%}.styles-module_product-link__KwQUm{text-decoration:none}.styles-module_sponsored-brand-container__DYujK>a:first-of-type .styles-module_product__u1ZhQ.styles-module_vertical__1fzB9{padding-left:15px}.styles-module_sponsored-brand-container__DYujK>a:first-of-type .styles-module_product__u1ZhQ.styles-module_vertical__1fzB9 .styles-module_image-container__zEsIC{right:0}.styles-module_product__u1ZhQ{cursor:pointer;display:flex;flex-direction:column;gap:6px}.styles-module_product__u1ZhQ.styles-module_vertical__1fzB9{display:flex;flex-direction:column;padding:0 12px}.styles-module_product__u1ZhQ.styles-module_vertical__1fzB9 .styles-module_image-container__zEsIC{align-items:center;display:flex;flex-direction:column;position:relative;width:100%}@media (min-width:0px) and (max-width:1090px){.styles-module_product__u1ZhQ{display:flex;flex-direction:column;padding:0 12px}.styles-module_product__u1ZhQ .styles-module_image-container__zEsIC{align-items:center;display:flex;flex-direction:column;position:relative;width:100%}}.styles-module_product__u1ZhQ .styles-module_image-container__zEsIC{align-items:center;display:flex;flex-direction:column;gap:6px;position:relative}.styles-module_product__u1ZhQ .styles-module_display-name__n7PQL{color:#4a4a4a;display:-webkit-box;font-family:Lato;font-size:14px;font-weight:700;line-height:16px;margin:0 0 5px;word-break:break-all;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;overflow-wrap:normal;text-overflow:ellipsis;word-break:keep-all}.styles-module_product__u1ZhQ .styles-module_product-image__w30Y8{height:120px;max-height:120px;width:auto}@media (max-width:430px){.styles-module_product__u1ZhQ .styles-module_product-image__w30Y8{height:auto;max-height:130px;width:auto}}@media (max-width:340px){.styles-module_product__u1ZhQ .styles-module_product-image__w30Y8{height:auto;max-height:none;width:100%}}.styles-module_product-divider__NK9JF{background-color:#eee;height:90%;width:1px}.styles-module_product-last-child__gR7cf,.styles-module_product-last-child__gR7cf.styles-module_vertical__1fzB9{grid-template-areas:\"image\" \"name\" \"price\"}.styles-module_price-list__LjfNH{display:flex;flex-direction:column;font-family:Lato,sans-serif;font-weight:400;gap:4px;margin-bottom:0;margin-right:7px}@media (min-width:0px) and (max-width:1024px){.styles-module_price-list__LjfNH{margin-right:-20px}}.styles-module_price-item__WDzc1{align-items:center;display:flex}.styles-module_price-item__WDzc1 .styles-module_symbol__PRHP5{margin-right:5px}.styles-module_price-item__WDzc1 .styles-module_price__q-Jfk{align-items:center;display:flex;margin-right:5px;word-break:break-all}.styles-module_price-item__WDzc1 .styles-module_price__q-Jfk .styles-module_dollar__nwu3P{margin-right:3px}.styles-module_price-item__WDzc1 .styles-module_crossed__xm8Qs{text-decoration:line-through}.styles-module_price-item__WDzc1 .styles-module_label__R6Vtq{font-style:italic;margin-left:10px}.styles-module_cmrPrice__xvHSJ,.styles-module_eventPrice__TSTMi,.styles-module_normalPrice__oBjv1{font-size:1.8rem}.styles-module_internetPrice__--BX4{font-size:1.4rem}.styles-module_meatstickers__tqZAt{box-sizing:border-box;display:flex;margin-bottom:5px;min-height:15px}.styles-module_sticker-event__MXKbz{border-radius:3px;font-family:Lato;font-size:10.348px;font-style:normal;font-weight:700;line-height:12.565px;padding:.739px 4.435px 2.217px;width:-moz-max-content;width:max-content}@media (max-width:295px){.styles-module_sticker-event__MXKbz{padding:.5px .7px}}.styles-module_products-container__jRdkc{display:flex;width:100%}";
var s = {"sponsored-brand-container":"styles-module_sponsored-brand-container__DYujK","vertical":"styles-module_vertical__1fzB9","title":"styles-module_title__NtIAR","sponsored-label":"styles-module_sponsored-label__mDmxC","product-details":"styles-module_product-details__-sYDj","rating-styles":"styles-module_rating-styles__1JEEC","custom-image-desktop":"styles-module_custom-image-desktop__-bdZD","logo-image-desktop":"styles-module_logo-image-desktop__S5GQa","custom-image-mobile":"styles-module_custom-image-mobile__59woK","logo-mobile":"styles-module_logo-mobile__bYR3-","border-radius":"styles-module_border-radius__JU0dY","sponsored-text":"styles-module_sponsored-text__z-SjG","image-container":"styles-module_image-container__zEsIC","title-container":"styles-module_title-container__idfo9","title-wrapper":"styles-module_title-wrapper__-AlZF","sponsored-brand-products-container":"styles-module_sponsored-brand-products-container__HNRe7","sponsored-brand-wrapper":"styles-module_sponsored-brand-wrapper__AzY8U","product-link":"styles-module_product-link__KwQUm","product":"styles-module_product__u1ZhQ","display-name":"styles-module_display-name__n7PQL","product-image":"styles-module_product-image__w30Y8","product-divider":"styles-module_product-divider__NK9JF","product-last-child":"styles-module_product-last-child__gR7cf","price-list":"styles-module_price-list__LjfNH","price-item":"styles-module_price-item__WDzc1","symbol":"styles-module_symbol__PRHP5","price":"styles-module_price__q-Jfk","dollar":"styles-module_dollar__nwu3P","crossed":"styles-module_crossed__xm8Qs","label":"styles-module_label__R6Vtq","eventPrice":"styles-module_eventPrice__TSTMi","cmrPrice":"styles-module_cmrPrice__xvHSJ","normalPrice":"styles-module_normalPrice__oBjv1","internetPrice":"styles-module_internetPrice__--BX4","meatstickers":"styles-module_meatstickers__tqZAt","sticker-event":"styles-module_sticker-event__MXKbz","products-container":"styles-module_products-container__jRdkc"};
styleInject(css_248z$5);

var css_248z$4 = ".styles-module_product-image__8akA4{height:auto;max-height:168px;max-width:193px;width:auto}@media (max-width:430px){.styles-module_product-image__8akA4{height:84px;max-height:84px;max-width:84px;width:auto}}@media (max-width:340px){.styles-module_product-image__8akA4{height:auto;max-height:none;width:100%}}.styles-module_name-container__-ENzl{display:block;max-width:340px;text-overflow:ellipsis;white-space:nowrap}.styles-module_display-name__XWz8Y{color:#4a4a4a;display:-webkit-box;font-family:Lato;font-size:14px;font-weight:700;line-height:16px;margin:0 0 5px;word-break:break-all;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;text-overflow:ellipsis;white-space:normal}";
var expStyle = {"product-image":"styles-module_product-image__8akA4","name-container":"styles-module_name-container__-ENzl","display-name":"styles-module_display-name__XWz8Y"};
styleInject(css_248z$4);

var css_248z$3 = ".styles-module_name-container__W3PPd{display:block;max-width:159px;text-overflow:ellipsis;white-space:nowrap}.styles-module_display-name__DUNzD{color:#4a4a4a;display:block;font-family:Lato;font-size:14px;font-weight:700;line-height:16px;margin:0 0 5px;word-break:break-all;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;text-overflow:ellipsis}";
var productStyle$2 = {"name-container":"styles-module_name-container__W3PPd","display-name":"styles-module_display-name__DUNzD"};
styleInject(css_248z$3);

var Prices = function (_a) {
    var product = _a.product, prices = _a.prices, badges = _a.badges, _b = _a.priceList, priceList = _b === void 0 ? [] : _b, _c = _a.isBrand, isBrand = _c === void 0 ? false : _c, _d = _a.isDisabledPriceCrossed, isDisabledPriceCrossed = _d === void 0 ? false : _d, _e = _a.isExperimentDesign, isExperimentDesign = _e === void 0 ? false : _e, _f = _a.hidePrice, hidePrice = _f === void 0 ? false : _f;
    return (React__default.createElement("div", null,
        product.rating !== 0 && (React__default.createElement("div", { className: pricesStyles['rating-styles'] },
            React__default.createElement(SponsoredProductRating, { rating: product.rating, maxRating: 5 }),
            product.totalReviews && React__default.createElement(SponsoredProductReview, { value: product.totalReviews }))),
        !isExperimentDesign && !hidePrice && (React__default.createElement(SponsoredProductPrice, { badge: badges, prices: prices, priceList: priceList, isBrand: isBrand, isDisabledPriceCrossed: isDisabledPriceCrossed, urlProduct: product.url }))));
};

var SponsoredBrandsWithThreeProducts = function (_a) {
    var _b;
    var products = _a.products, isVertical = _a.isVertical, onClick = _a.onClick, sbExperimentStyle = _a.sbExperimentStyle, categoryId = _a.categoryId;
    var configurations = useContext(SponsoredProductsContext).configurations;
    var shouldHidePrice = !!(categoryId && ((_b = configurations === null || configurations === void 0 ? void 0 : configurations.priceHiddenCategoriesIds) === null || _b === void 0 ? void 0 : _b.includes(categoryId)));
    var handleClickProduct = function (e, url, product) {
        var shouldOpenInNewTab = e.metaKey || e.ctrlKey;
        if (shouldOpenInNewTab) {
            window.open(url, '_blank');
            e.preventDefault();
        }
        onClick(product, product.adIdentifier);
    };
    var handleAuxClickProduct = function (e, url, product) {
        var shouldOpenInNewTab = e.button === 1;
        if (shouldOpenInNewTab) {
            window.open(url, '_blank');
            e.preventDefault();
        }
        onClick(product, product.adIdentifier);
    };
    return (React__default.createElement("div", { className: s['products-container'] }, products === null || products === void 0 ? void 0 : products.map(function (product, idx) {
        var _a, _b, _c, _d, _e;
        var isLastElement = idx === products.length - 1;
        var displayName = product.displayName, badges = product.badges, prices = product.prices, mediaUrls = product.mediaUrls, url = product.url, meatStickers = product.meatStickers;
        var imageUrl = sbExperimentStyle
            ? mediaUrls[0]
            : mediaUrls[0].replace('/public', '/w=120,h=120,fit=pad');
        return (React__default.createElement("div", { className: s['product-details'] },
            React__default.createElement("a", { href: url, className: s['product-link'], onClick: function (e) { return handleClickProduct(e, url, product); }, onAuxClick: function (e) { return handleAuxClickProduct(e, url, product); } },
                React__default.createElement("div", { className: cn(s['product'], (_a = {},
                        _a[s['product-last-child']] = isLastElement,
                        _a[s['vertical']] = isVertical,
                        _a)) },
                    React__default.createElement("div", { className: s['image-container'], style: { gridArea: 'image' } },
                        React__default.createElement("img", { src: imageUrl, alt: "Product", className: cn((_b = {},
                                _b[expStyle['product-image']] = sbExperimentStyle,
                                _b[s['product-image']] = !sbExperimentStyle,
                                _b)) })),
                    React__default.createElement("div", { className: cn((_c = {},
                            _c[expStyle['name-container']] = sbExperimentStyle,
                            _c[productStyle$2['name-container']] = !sbExperimentStyle,
                            _c)) },
                        meatStickers && (React__default.createElement("div", { className: cn(s['meatstickers'], (_d = {},
                                _d[s['vertical']] = isVertical,
                                _d[s['horizontal']] = !isVertical,
                                _d)) }, meatStickers === null || meatStickers === void 0 ? void 0 : meatStickers.map(function (sticker) { return (React__default.createElement("span", { key: sticker.label, id: "testId-Pod-stickers-".concat(sticker.label), className: s['sticker-event'], style: {
                                backgroundColor: sticker.styles.backgroundColor,
                                color: sticker.styles.textColor,
                            } }, sticker.label)); }))),
                        React__default.createElement("h3", { style: { gridArea: 'name' }, className: cn((_e = {},
                                _e[expStyle['display-name']] = sbExperimentStyle,
                                _e[productStyle$2['display-name']] = !sbExperimentStyle,
                                _e)) }, displayName),
                        React__default.createElement(Prices, { product: product, prices: prices, badges: badges, isBrand: true, isDisabledPriceCrossed: true, isExperimentDesign: sbExperimentStyle, hidePrice: shouldHidePrice }))))));
    })));
};

var css_248z$2 = ".styles-module_name-container__IPrmO{display:block;max-width:159px;text-overflow:ellipsis;white-space:nowrap}.styles-module_display-name__sofvi{color:#4a4a4a;display:block;font-family:Lato;font-size:14px;font-weight:700;line-height:16px;margin:0 0 5px;word-break:break-all;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;text-overflow:ellipsis}";
var productStyle$1 = {"name-container":"styles-module_name-container__IPrmO","display-name":"styles-module_display-name__sofvi"};
styleInject(css_248z$2);

var SponsoredBrandsWithTwoProducts = function (_a) {
    var _b;
    var products = _a.products, isVertical = _a.isVertical, onClick = _a.onClick, sbExperimentStyle = _a.sbExperimentStyle, categoryId = _a.categoryId;
    var configurations = useContext(SponsoredProductsContext).configurations;
    var shouldHidePrice = !!(categoryId && ((_b = configurations === null || configurations === void 0 ? void 0 : configurations.priceHiddenCategoriesIds) === null || _b === void 0 ? void 0 : _b.includes(categoryId)));
    var handleClickProduct = function (e, url, product) {
        var shouldOpenInNewTab = e.metaKey || e.ctrlKey;
        if (shouldOpenInNewTab) {
            window.open(url, '_blank');
            e.preventDefault();
        }
        onClick(product, product.adIdentifier);
    };
    var handleAuxClickProduct = function (e, url, product) {
        var shouldOpenInNewTab = e.button === 1;
        if (shouldOpenInNewTab) {
            window.open(url, '_blank');
            e.preventDefault();
        }
        onClick(product, product.adIdentifier);
    };
    return (React__default.createElement("div", { className: s['products-container'] }, products === null || products === void 0 ? void 0 : products.map(function (product, idx) {
        var _a, _b, _c, _d, _e;
        var isLastElement = idx === products.length - 1;
        var displayName = product.displayName, badges = product.badges, prices = product.prices, mediaUrls = product.mediaUrls, url = product.url, meatStickers = product.meatStickers;
        var imageUrl = sbExperimentStyle
            ? mediaUrls[0]
            : mediaUrls[0].replace('/public', '/w=120,h=120,fit=pad');
        return (React__default.createElement("div", { className: s['product-details'] },
            React__default.createElement("a", { href: url, className: s['product-link'], onClick: function (e) { return handleClickProduct(e, url, product); }, onAuxClick: function (e) { return handleAuxClickProduct(e, url, product); } },
                React__default.createElement("div", { className: cn(s['product'], (_a = {},
                        _a[s['product-last-child']] = isLastElement,
                        _a[s['vertical']] = isVertical,
                        _a)) },
                    React__default.createElement("div", { className: s['image-container'], style: { gridArea: 'image' } },
                        React__default.createElement("img", { src: imageUrl, alt: "Product", className: cn((_b = {},
                                _b[expStyle['product-image']] = sbExperimentStyle,
                                _b[s['product-image']] = !sbExperimentStyle,
                                _b)) })),
                    React__default.createElement("div", { className: cn((_c = {},
                            _c[expStyle['name-container']] = sbExperimentStyle,
                            _c[productStyle$1['name-container']] = !sbExperimentStyle,
                            _c)) },
                        meatStickers && (React__default.createElement("div", { className: cn(s['meatstickers'], (_d = {},
                                _d[s['vertical']] = isVertical,
                                _d[s['horizontal']] = !isVertical,
                                _d)) }, meatStickers === null || meatStickers === void 0 ? void 0 : meatStickers.map(function (sticker) { return (React__default.createElement("span", { key: sticker.label, id: "testId-Pod-stickers-".concat(sticker.label), className: s['sticker-event'], style: {
                                backgroundColor: sticker.styles.backgroundColor,
                                color: sticker.styles.textColor,
                            } }, sticker.label)); }))),
                        React__default.createElement("h3", { style: { gridArea: 'name' }, className: cn((_e = {},
                                _e[expStyle['display-name']] = sbExperimentStyle,
                                _e[productStyle$1['display-name']] = !sbExperimentStyle,
                                _e)) }, displayName),
                        React__default.createElement(Prices, { product: product, prices: prices, badges: badges, isBrand: true, isDisabledPriceCrossed: true, isExperimentDesign: sbExperimentStyle, hidePrice: shouldHidePrice }))))));
    })));
};

var css_248z$1 = ".styles-module_product-image__-sv1t{height:218px;max-height:218px;width:auto}.styles-module_product__RzhIy{cursor:pointer;display:flex;gap:6px}.styles-module_product__RzhIy.styles-module_vertical__6aEre{display:flex;padding:0 12px}.styles-module_product__RzhIy.styles-module_vertical__6aEre .styles-module_image-container__P2Gng{align-items:center;display:flex;flex-direction:column;position:relative;width:100%}.styles-module_display-name__AoBU-{color:#4a4a4a;display:-webkit-box;font-family:Lato;font-size:14px;font-weight:700;line-height:16px;margin:0 0 5px;word-break:break-all;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;overflow-wrap:normal;text-overflow:ellipsis;word-break:keep-all}.styles-module_product-details__XCyAl{flex:1;margin-left:20px}.styles-module_name-container__ODZVy{display:flex;flex-direction:column;gap:8px;justify-content:center}";
var productStyle = {"product-image":"styles-module_product-image__-sv1t","product":"styles-module_product__RzhIy","vertical":"styles-module_vertical__6aEre","image-container":"styles-module_image-container__P2Gng","display-name":"styles-module_display-name__AoBU-","product-details":"styles-module_product-details__XCyAl","name-container":"styles-module_name-container__ODZVy"};
styleInject(css_248z$1);

var SponsoredBrandsWithOneProducts = function (_a) {
    var _b;
    var products = _a.products, isVertical = _a.isVertical, onClick = _a.onClick, sbExperimentStyle = _a.sbExperimentStyle, categoryId = _a.categoryId;
    var configurations = useContext(SponsoredProductsContext).configurations;
    var shouldHidePrice = !!(categoryId && ((_b = configurations === null || configurations === void 0 ? void 0 : configurations.priceHiddenCategoriesIds) === null || _b === void 0 ? void 0 : _b.includes(categoryId)));
    var handleClickProduct = function (e, url, product) {
        var shouldOpenInNewTab = e.metaKey || e.ctrlKey;
        if (shouldOpenInNewTab) {
            window.open(url, '_blank');
            e.preventDefault();
        }
        onClick(product, product.adIdentifier);
    };
    var handleAuxClickProduct = function (e, url, product) {
        var shouldOpenInNewTab = e.button === 1;
        if (shouldOpenInNewTab) {
            window.open(url, '_blank');
            e.preventDefault();
        }
        onClick(product, product.adIdentifier);
    };
    return (React__default.createElement("div", { className: s['products-container'] }, products === null || products === void 0 ? void 0 : products.map(function (product, idx) {
        var _a, _b;
        var isLastElement = idx === products.length - 1;
        var displayName = product.displayName, badges = product.badges, prices = product.prices, mediaUrls = product.mediaUrls, url = product.url, meatStickers = product.meatStickers;
        var imageUrl = sbExperimentStyle
            ? mediaUrls[0]
            : mediaUrls[0].replace('/public', '/w=120,h=120,fit=pad');
        return (React__default.createElement("div", { className: productStyle['product-details'] },
            React__default.createElement("a", { href: url, className: s['product-link'], onClick: function (e) { return handleClickProduct(e, url, product); }, onAuxClick: function (e) { return handleAuxClickProduct(e, url, product); } },
                React__default.createElement("div", { className: cn(productStyle['product'], (_a = {},
                        _a[productStyle['product-last-child']] = isLastElement,
                        _a[productStyle['vertical']] = isVertical,
                        _a)) },
                    React__default.createElement("div", { className: s['image-container'], style: { gridArea: 'image' } },
                        React__default.createElement("img", { src: imageUrl, alt: "Product", className: productStyle['product-image'] })),
                    React__default.createElement("div", { className: productStyle['name-container'] },
                        meatStickers && (React__default.createElement("div", { className: cn(s['meatstickers'], (_b = {},
                                _b[s['vertical']] = isVertical,
                                _b[s['horizontal']] = !isVertical,
                                _b)) }, meatStickers === null || meatStickers === void 0 ? void 0 : meatStickers.map(function (sticker) { return (React__default.createElement("span", { key: sticker.label, id: "testId-Pod-stickers-".concat(sticker.label), className: s['sticker-event'], style: {
                                backgroundColor: sticker.styles.backgroundColor,
                                color: sticker.styles.textColor,
                            } }, sticker.label)); }))),
                        React__default.createElement("h3", { style: { gridArea: 'name' }, className: productStyle['display-name'] }, displayName),
                        React__default.createElement(Prices, { product: product, prices: prices, badges: badges, isBrand: true, isDisabledPriceCrossed: true, isExperimentDesign: sbExperimentStyle, hidePrice: shouldHidePrice }))))));
    })));
};

var SponsoredBrandsProducts = function (_a) {
    var products = _a.products, isVertical = _a.isVertical, onClick = _a.onClick, otherProps = __rest(_a, ["products", "isVertical", "onClick"]);
    var productsComponents = {
        1: SponsoredBrandsWithOneProducts,
        2: SponsoredBrandsWithTwoProducts,
        3: SponsoredBrandsWithThreeProducts,
    };
    var ProductsComponent = productsComponents[products.length];
    if (!ProductsComponent) {
        return null;
    }
    return (React__default.createElement(ProductsComponent, __assign({ products: products, isVertical: isVertical, onClick: onClick }, otherProps)));
};

function SponsoredBrandWrapper(props) {
    var isSearchPage = props.isSearchPage, onBannerClick = props.onBannerClick, otherProps = __rest(props, ["isSearchPage", "onBannerClick"]);
    var _a = useContext(SponsoredProductsContext), deviceType = _a.deviceType, products = _a.brandProducts, brandDetails = _a.brandDetails;
    var isMobile = deviceType === 'mobile';
    if (!products || products.length === 0 || !brandDetails)
        return React__default.createElement("div", { style: { display: 'none' } });
    return (React__default.createElement(SponsoredBrand, __assign({ onBannerClick: onBannerClick, isMobile: isMobile, brandDetails: brandDetails, products: products, isSearchPage: isSearchPage }, otherProps)));
}
function getContainerStyle(isMobile, productCount, containerStyleProp) {
    if (containerStyleProp === void 0) { containerStyleProp = {}; }
    if (isMobile) {
        return __assign({ gridTemplateColumns: "1fr", gridTemplateRows: 'auto auto 1fr' }, containerStyleProp);
    }
    return __assign({ gridTemplateColumns: "248px 1fr", gridTemplateRows: '40px 1fr' }, containerStyleProp);
}
function SponsoredBrand(_a) {
    var _b;
    var productsProp = _a.products, brandDetails = _a.brandDetails, isMobile = _a.isMobile, isSearchPage = _a.isSearchPage, onBannerClick = _a.onBannerClick, containerStyleProp = _a.containerStyle, otherProps = __rest(_a, ["products", "brandDetails", "isMobile", "isSearchPage", "onBannerClick", "containerStyle"]);
    var _c = useState(false), hasBeenInView = _c[0], setHasBeenInView = _c[1];
    var secondImageUrl = brandDetails.scndImg;
    var desktopImage = brandDetails.scndImg2;
    var promotionText = brandDetails.promotionText;
    var products = useMemo(function () {
        if (!Array.isArray(productsProp))
            return [];
        if (productsProp.length > 3)
            return productsProp.slice(0, 3);
        return productsProp;
    }, [productsProp]);
    var productCount = products === null || products === void 0 ? void 0 : products.length;
    var containerStyle = getContainerStyle(isMobile, productCount, containerStyleProp);
    var _d = useInView({
        threshold: 0.3,
        initialInView: false,
    }), ref = _d.ref, inView = _d.inView;
    useEffect(function () {
        if (products != null) {
            if (inView && !hasBeenInView) {
                var productAdInfos = products.map(function (product) { return product.adIdentifier; });
                var brandAdInfo = brandDetails.mabayaAdInfo;
                onBannerClick(products[0], 'impression', __spreadArray([brandAdInfo], productAdInfos, true), true);
                setHasBeenInView(true);
            }
        }
    }, [inView, hasBeenInView, products, onBannerClick, brandDetails.mabayaAdInfo]);
    var handleClick = useCallback(function (product, adIdentifier) {
        if (onBannerClick) {
            onBannerClick(product, 'click', [adIdentifier], true);
        }
    }, [onBannerClick]);
    if ((products === null || products === void 0 ? void 0 : products.length) === 0)
        return null;
    var isVertical = isMobile;
    var handleClickImage = function (e) {
        var shouldOpenInNewTab = e.metaKey || e.ctrlKey;
        if (shouldOpenInNewTab) {
            window.open(brandDetails.landingPageUrl, '_blank');
            e.preventDefault();
        }
        handleClick(null, brandDetails.mabayaAdInfo);
    };
    var handleAuxClickImage = function (e) {
        var shouldOpenInNewTab = e.button === 1;
        if (shouldOpenInNewTab) {
            window.open(brandDetails.mabayaAdInfo, '_blank');
            e.preventDefault();
        }
        handleClick(null, brandDetails.mabayaAdInfo);
    };
    return (React__default.createElement("div", __assign({ className: cn(s['sponsored-brand-container'], (_b = {}, _b[s['vertical']] = isVertical, _b)), style: containerStyle, ref: ref }, otherProps),
        React__default.createElement("div", { className: s['title-container'] },
            React__default.createElement("div", { className: s['title-wrapper'] },
                React__default.createElement("h3", { className: s['title'] }, isSearchPage ? 'Marcas relacionadas con tu búsqueda' : promotionText),
                !isMobile && React__default.createElement("p", { className: s['sponsored-text'] }, "Patrocinado")),
            isMobile && React__default.createElement("p", { className: s['sponsored-label'] }, "Patrocinado"),
            isMobile && (React__default.createElement("a", { href: brandDetails.landingPageUrl },
                React__default.createElement("img", { onClick: function () { return handleClick(null, brandDetails.mabayaAdInfo); }, src: secondImageUrl, alt: "campaign-image", className: s['custom-image-mobile'] })))),
        React__default.createElement("div", { className: s['sponsored-brand-products-container'] },
            React__default.createElement("div", { className: s['sponsored-brand-wrapper'] },
                !isMobile && (React__default.createElement("div", { className: s['image-container'] },
                    React__default.createElement("a", { href: brandDetails.landingPageUrl, onClick: handleClickImage, onAuxClick: handleAuxClickImage },
                        React__default.createElement("div", { style: {
                                backgroundImage: "url(".concat(desktopImage, ")"),
                                height: '230px',
                                width: '238px',
                            }, className: s['custom-image-desktop'] })))),
                React__default.createElement(SponsoredBrandsProducts, __assign({ products: products, onClick: handleClick, isVertical: isVertical }, otherProps))))));
}

var css_248z = ".display-module_horizontal-banner__p48Tn{background:#fff;border:1px solid #eee;border-radius:8px;height:100%}@media (min-width:0px) and (max-width:1024px){.display-module_horizontal-banner__p48Tn{height:auto;min-height:244px;width:auto}}.display-module_horizontal-banner-layout__f8gpH{cursor:pointer;display:flex;flex-direction:row;height:100%;margin:20px 8px 16px;position:relative}@media (min-width:0px) and (max-width:1024px){.display-module_horizontal-banner-layout__f8gpH{margin:8px}}.display-module_horizontal-product-details__LZdDJ{display:flex;flex-direction:column;gap:7px;height:100%;margin-left:15px;width:-moz-max-content;width:max-content;width:100%}@media (min-width:0px) and (max-width:1024px){.display-module_horizontal-product-details__LZdDJ{margin-top:12px;width:auto}}.display-module_rating-styles__Jjm5Y{display:flex;flex-direction:row;height:21px}.display-module_banner-PDP__vlIfl{height:100%}.display-module_sponsored-text__86GKW{align-items:flex-end;color:#767676;display:flex;font-family:Lato;font-size:12px;font-weight:400;letter-spacing:1px;line-height:14px;position:relative;right:10px}@media (min-width:0px) and (max-width:1024px){.display-module_sponsored-text__86GKW{align-items:end;justify-content:flex-end;padding-bottom:0;right:4px}}@media (min-width:1279px){.display-module_sponsored-text__86GKW{padding-bottom:36px}}.display-module_sponsored-title__wXx3r{display:flex;flex-direction:row;justify-content:space-between}@media (min-width:0px) and (max-width:1024px){.display-module_sponsored-title__wXx3r{max-width:145px;min-width:120px}}.display-module_anchor-link__1yffI{text-decoration:none}";
var display = {"horizontal-banner":"display-module_horizontal-banner__p48Tn","horizontal-banner-layout":"display-module_horizontal-banner-layout__f8gpH","horizontal-product-details":"display-module_horizontal-product-details__LZdDJ","rating-styles":"display-module_rating-styles__Jjm5Y","banner-PDP":"display-module_banner-PDP__vlIfl","sponsored-text":"display-module_sponsored-text__86GKW","sponsored-title":"display-module_sponsored-title__wXx3r","anchor-link":"display-module_anchor-link__1yffI"};
styleInject(css_248z);

var SponsoredDisplayPDP = function (_a) {
    var _b, _c;
    var onBannerClick = _a.onBannerClick, product = _a.product; _a.store; var _e = _a.containerStyles, containerStyles = _e === void 0 ? {} : _e;
    var selectedProduct = product;
    var meatStickers = (_b = selectedProduct === null || selectedProduct === void 0 ? void 0 : selectedProduct.meatStickers) !== null && _b !== void 0 ? _b : [];
    var productDetails = useRef(null);
    var _f = useState(false), isWindowDefined = _f[0], setIsWindowDefined = _f[1];
    var _g = useState(false), hasBeenInView = _g[0], setHasBeenInView = _g[1];
    var deviceType = useContext(SponsoredProductsContext).deviceType;
    var isMobile = deviceType === 'mobile';
    var _h = useInView({
        threshold: 0.3,
        initialInView: false,
    }), ref = _h.ref, inView = _h.inView;
    useEffect(function () {
        var checkWindow = setInterval(function () {
            if (typeof window !== 'undefined') {
                setIsWindowDefined(true);
                clearInterval(checkWindow);
            }
        }, 200);
        return function () { return clearInterval(checkWindow); };
    }, []);
    useEffect(function () {
        if (isWindowDefined) {
            setSponsoredDisplayDigitalDataRenderPDP(selectedProduct, false);
        }
    }, [isWindowDefined, selectedProduct]);
    useEffect(function () {
        productDetails.current = selectedProduct;
        var product = productDetails.current;
        if (productDetails && inView && !hasBeenInView) {
            onBannerClick(product, 'impression', 'display');
            setHasBeenInView(true);
        }
    }, [selectedProduct, onBannerClick, productDetails, inView, hasBeenInView]);
    var handleClick = useCallback(function () {
        if (onBannerClick) {
            onBannerClick(selectedProduct, 'visit', 'display');
            onBannerClick(selectedProduct, 'click', 'display');
        }
    }, [onBannerClick, selectedProduct]);
    var handleClickProduct = function (e) {
        var shouldOpenInNewTab = e.metaKey || e.ctrlKey;
        if (shouldOpenInNewTab) {
            window.open(currentUrl, '_blank');
            e.preventDefault();
        }
        handleClick();
    };
    var handleAuxClickProduct = function (e) {
        var shouldOpenInNewTab = e.button === 1;
        if (shouldOpenInNewTab) {
            window.open(currentUrl, '_blank');
            e.preventDefault();
            handleClick();
        }
    };
    if (!selectedProduct) {
        return null;
    }
    var currentUrl = selectedProduct === null || selectedProduct === void 0 ? void 0 : selectedProduct.url;
    return (React__default.createElement("div", { ref: ref, className: "".concat(display["banner-PDP"], " sponsored-display-pdp"), style: containerStyles },
        React__default.createElement("a", { href: currentUrl, onClick: handleClickProduct, onAuxClick: handleAuxClickProduct, className: display["anchor-link"] },
            React__default.createElement("div", { className: display["horizontal-banner"] },
                React__default.createElement("div", { className: display["horizontal-banner-layout"] },
                    React__default.createElement(SponsoredProductImage, { imageUrl: (_c = selectedProduct.mediaUrls) === null || _c === void 0 ? void 0 : _c[0], altText: "".concat(selectedProduct.brand, " - ").concat(selectedProduct.displayName), isVerticalBanner: false, meatStickers: meatStickers, customSize: isMobile ? { width: '155px', height: '155px' } : undefined }),
                    React__default.createElement("div", { className: display["horizontal-product-details"] },
                        React__default.createElement(SponsoredProductTitle, { brandName: selectedProduct.brand, productName: selectedProduct.displayName, sellerName: selectedProduct.sellerName }),
                        React__default.createElement("div", { className: display['rating-styles'] },
                            React__default.createElement(SponsoredProductRating, { rating: selectedProduct.rating, maxRating: 5 }),
                            selectedProduct.totalReviews && (React__default.createElement(SponsoredProductReview, { value: selectedProduct.totalReviews }))),
                        React__default.createElement("div", { className: display['sponsored-title'] },
                            React__default.createElement(SponsoredProductPrice, { badge: selectedProduct.badges, prices: selectedProduct.prices, priceList: [], containerStyles: { gap: '2px', lineHeight: 'normal' }, urlProduct: selectedProduct.url }))),
                    React__default.createElement("div", { className: display['sponsored-text'] }, labels.sponsoredText))))));
};

function usePrices(pricesParam) {
    var atgPriceTypeMap = {
        1: PRICE_TYPES.cmrPrice,
        2: PRICE_TYPES.normalPrice,
        3: PRICE_TYPES.internetPrice,
        4: PRICE_TYPES.normalPrice,
    };
    var prices = mapATGPricesToCatalystPrices(pricesParam, 'cl', false, null, atgPriceTypeMap);
    var normalPrice = prices.find(function (price) { return price.type === 'normalPrice'; });
    var discountedPrice = prices.find(function (price) { return price.type === 'internetPrice'; });
    var OUPrice = prices.find(function (price) { return price.type === 'cmrPrice'; });
    var hasDiscountedPrice = Boolean(discountedPrice);
    var hasOUPrice = Boolean(OUPrice);
    var baseResponse = { normalPrice: normalPrice, discountedPrice: discountedPrice, OUPrice: OUPrice, hasDiscountedPrice: hasDiscountedPrice, hasOUPrice: hasOUPrice };
    if (!hasDiscountedPrice && !hasOUPrice)
        return __assign(__assign({}, baseResponse), { leftPrice: null, rightPrice: normalPrice, isLeftPriceCrossed: false, isRightPriceOU: false });
    if (!hasOUPrice)
        return __assign(__assign({}, baseResponse), { leftPrice: normalPrice, rightPrice: discountedPrice, isLeftPriceCrossed: true, isRightPriceOU: false });
    if (!hasDiscountedPrice)
        return __assign(__assign({}, baseResponse), { leftPrice: normalPrice, rightPrice: OUPrice, isLeftPriceCrossed: false, isRightPriceOU: true });
    return __assign(__assign({}, baseResponse), { leftPrice: discountedPrice, rightPrice: OUPrice, isLeftPriceCrossed: false, isRightPriceOU: true });
}
function useHandleInView(product, onStripClick) {
    var productDetails = useRef(null);
    var _a = useState(false), hasBeenInView = _a[0], setHasBeenInView = _a[1];
    var inView = useInView({
        threshold: 0.3,
        initialInView: true,
    }).inView;
    useEffect(function () {
        if (hasBeenInView)
            return;
        var interval = setInterval(function () {
            productDetails.current = product;
            var isWindowDefined = typeof window !== 'undefined';
            if (!isWindowDefined)
                return;
            var isStripVisible = window.isStripVisible;
            if (productDetails && inView && !hasBeenInView && isStripVisible) {
                onStripClick(product, 'impression', 'strip');
                setSponsoredDisplayDigitalDataRenderPDP(product, true);
                setHasBeenInView(true);
                clearInterval(interval);
            }
        }, 100);
        return function () { return clearInterval(interval); };
    }, [product, onStripClick, productDetails, inView, hasBeenInView]);
}
function SponsoredStrip(_a) {
    var _b, _c;
    var product = _a.product, _d = _a.containerStyles, containerStyles = _d === void 0 ? {} : _d, _e = _a.leftPriceStyles, leftPriceStyles = _e === void 0 ? {} : _e, onStripClick = _a.onStripClick, isMobile = _a.isMobile, otherProps = __rest(_a, ["product", "containerStyles", "leftPriceStyles", "onStripClick", "isMobile"]);
    var showDebitCardIcon = getShowDebitCardIcon(getCountryFromProductURL(product.url) || 'cl');
    var SellerIcon = useSellerIcon(product.sellerName);
    var prices = usePrices(product.prices);
    var normalPrice = prices.normalPrice, discountedPrice = prices.discountedPrice, OUPrice = prices.OUPrice, hasOUPrice = prices.hasOUPrice, hasDiscountedPrice = prices.hasDiscountedPrice, leftPrice = prices.leftPrice, rightPrice = prices.rightPrice, isLeftPriceCrossed = prices.isLeftPriceCrossed, isRightPriceOU = prices.isRightPriceOU;
    var badge = product === null || product === void 0 ? void 0 : product.badges;
    useHandleInView(product, onStripClick);
    var onClick = useCallback(function (e) {
        e.stopPropagation();
        if (onStripClick)
            onStripClick(product, 'click', 'strip');
    }, [product, onStripClick]);
    var imageURLMaker = function (filename) {
        return "/a/fa/product/static/images/".concat(filename);
    };
    var onErrorFallback = function (_a) {
        var currentTarget = _a.currentTarget;
        /* eslint-disable no-param-reassign */
        currentTarget.onerror = null;
        var fallbackSrc = imageURLMaker('defaultImage.jpg');
        currentTarget.src = fallbackSrc;
        currentTarget.srcset = fallbackSrc;
    };
    if (isMobile) {
        return (React__default.createElement("a", __assign({ href: product.url, style: __assign({ display: 'flex', flexDirection: 'column', padding: '4px 8px', backgroundColor: 'white', overflow: 'hidden', height: 70, boxSizing: 'border-box', justifyContent: 'center', textDecoration: 'none' }, containerStyles), onClick: onClick }, otherProps),
            React__default.createElement("div", __assign({ style: {
                    display: 'flex',
                    justifyContent: 'space-between',
                }, onClick: onClick }, otherProps),
                React__default.createElement("div", { style: { display: 'flex', gap: 5, flex: 1, minWidth: 150, alignItems: 'center' } },
                    React__default.createElement("img", { src: product.mediaUrls[0], alt: product.displayName, width: 48, height: 48, style: { minWidth: 48, maxHeight: 48, objectFit: 'contain' }, onError: onErrorFallback }),
                    React__default.createElement("div", { style: { display: 'flex', flexDirection: 'column', minWidth: 120 } },
                        React__default.createElement("div", { style: {
                                color: '#767676',
                                fontFamily: 'Lato',
                                fontSize: '11px',
                                fontStyle: 'bold',
                                fontWeight: 700,
                                lineHeight: 'normal',
                                letterSpacing: 1.2,
                                transform: 'uppercase',
                            } }, product.brand),
                        React__default.createElement("div", { style: {
                                color: '#333333',
                                fontFamily: 'Lato',
                                fontSize: '12px',
                                fontStyle: 'normal',
                                fontWeight: 700,
                                lineHeight: '14px',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                            } }, product.displayName),
                        React__default.createElement("div", { style: { display: 'flex', alignItems: 'center' } }, ((_b = product.rating) !== null && _b !== void 0 ? _b : 0) >= 1 && (React__default.createElement(React__default.Fragment, null,
                            React__default.createElement(SponsoredProductRating, { rating: product.rating, maxRating: 5, showRatingValue: true }),
                            React__default.createElement(SponsoredProductReview, { value: product.totalReviews })))))),
                React__default.createElement("div", { style: { display: 'flex', alignItems: 'flex-end', marginLeft: '2px', marginRight: '2px' } },
                    React__default.createElement("div", { style: { display: 'flex', flexDirection: 'column', alignItems: 'end', justifyContent: 'center', height: '100%', marginBottom: '2px' } },
                        Boolean(rightPrice) && isRightPriceOU && (React__default.createElement("div", { style: {
                                display: 'flex',
                                flexDirection: 'row',
                                marginLeft: 6,
                                alignItems: 'end',
                                gap: 2,
                            } },
                            React__default.createElement(CmrIcon, { showDebitCardIcon: showDebitCardIcon }),
                            React__default.createElement("div", { style: {
                                    color: '#E4022D',
                                    fontFamily: 'Lato',
                                    fontSize: '12px',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    lineHeight: '16px',
                                    whiteSpace: 'nowrap',
                                } },
                                rightPrice.symbol,
                                " ",
                                rightPrice.price))),
                        Boolean(rightPrice) && !isRightPriceOU && (React__default.createElement("div", { style: {
                                display: 'flex',
                                flexDirection: 'column',
                                marginLeft: 6,
                                alignSelf: 'flex-end',
                            } },
                            React__default.createElement("div", { style: {
                                    color: '#333333',
                                    fontFamily: 'Lato',
                                    fontSize: '12px',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    lineHeight: '16px',
                                    whiteSpace: 'nowrap',
                                } },
                                rightPrice.symbol,
                                " ",
                                rightPrice.price))),
                        Boolean(leftPrice) && (React__default.createElement("div", { style: __assign(__assign({ color: '#333333', fontFamily: 'Lato', fontSize: '12px', fontStyle: 'normal', fontWeight: 400, lineHeight: '16px', whiteSpace: 'nowrap', paddingTop: '1px', marginTop: '0px', marginLeft: '6px' }, (isLeftPriceCrossed ? { textDecoration: 'line-through', color: '#717171' } : {})), leftPriceStyles) },
                            leftPrice.symbol,
                            " ",
                            leftPrice.price)),
                        Boolean(rightPrice) && (React__default.createElement("div", { style: {
                                color: '#717171',
                                fontFamily: 'Lato',
                                fontSize: '12px',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: '14px',
                                letterSpacing: 1,
                            } }, "Patrocinado")))))));
    }
    return (React__default.createElement("a", __assign({ href: product.url, style: __assign({ display: 'flex', justifyContent: 'space-between', padding: '4px 8px 6px', backgroundColor: 'white', overflow: 'hidden', textDecoration: 'none' }, containerStyles), onClick: onClick }, otherProps),
        React__default.createElement("div", { style: { display: 'flex', gap: 12, flex: 1, minWidth: 210, alignItems: 'center' } },
            React__default.createElement("img", { src: product.mediaUrls[0], alt: product.displayName, width: 48, height: 48, style: { minWidth: 48, maxHeight: 48, objectFit: 'contain' }, onError: onErrorFallback }),
            React__default.createElement("div", { style: { display: 'flex', flexDirection: 'column', minWidth: 150 } },
                React__default.createElement("div", { style: {
                        color: '#767676',
                        fontFamily: 'Lato',
                        fontSize: '10px',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        lineHeight: '14px',
                        letterSpacing: 1.2,
                        transform: 'uppercase',
                    } }, product.brand),
                React__default.createElement("div", { style: {
                        color: '#333333',
                        fontFamily: 'Lato',
                        fontSize: '12px',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        lineHeight: '16px',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                    } }, product.displayName),
                React__default.createElement("div", { style: { display: 'flex', gap: 4, alignItems: 'center' } },
                    React__default.createElement("div", { style: {
                            color: '#333',
                            fontFamily: 'Lato',
                            fontSize: '11px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: '14px',
                        } },
                        "Por ",
                        capitalizeText(product === null || product === void 0 ? void 0 : product.sellerName),
                        ' '),
                    SellerIcon && React__default.createElement(SellerIcon, null)))),
        React__default.createElement("div", { style: { display: 'flex', alignItems: 'flex-end', marginLeft: 10 } },
            React__default.createElement("div", { style: { display: 'flex', height: '100%', alignItems: 'center' } }, ((_c = product.rating) !== null && _c !== void 0 ? _c : 0) >= 1 && (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(SponsoredProductRating, { rating: product.rating, maxRating: 5 }),
                React__default.createElement(SponsoredProductReview, { value: product.totalReviews })))),
            React__default.createElement("div", { style: __assign(__assign({ display: 'flex', marginLeft: 52, alignItems: 'flex-end', paddingBottom: 2 }, (!hasOUPrice ? { alignSelf: 'center', alignItems: 'center', paddingTop: 2 } : {})), (!hasDiscountedPrice && !hasOUPrice ? { paddingTop: 4 } : {})) },
                Boolean(normalPrice) && (React__default.createElement("div", { style: __assign(__assign({ color: '#333333', fontFamily: 'Lato', fontSize: '14px', fontStyle: 'normal', fontWeight: 400, lineHeight: 'normal', whiteSpace: 'nowrap' }, (hasDiscountedPrice ? { textDecoration: 'line-through' } : {})), (!hasDiscountedPrice && !hasOUPrice ? { fontSize: '18px' } : {})) },
                    normalPrice.symbol,
                    " ",
                    normalPrice.price)),
                Boolean(discountedPrice) && (React__default.createElement("div", { style: {
                        marginLeft: 24,
                        color: '#717171',
                        fontFamily: 'Lato',
                        fontSize: '18px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: 'normal',
                        whiteSpace: 'nowrap',
                    } },
                    discountedPrice.symbol,
                    " ",
                    discountedPrice.price)),
                Boolean(OUPrice) && (React__default.createElement("div", { style: { display: 'flex', flexDirection: 'column', marginLeft: 20 } },
                    React__default.createElement(CmrIcon, { showDebitCardIcon: showDebitCardIcon }),
                    React__default.createElement("div", { style: { display: 'flex', gap: 4, alignItems: 'center' } },
                        React__default.createElement("div", { style: {
                                color: '#E4022D',
                                fontFamily: 'Lato',
                                fontSize: '18px',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: 'normal',
                                whiteSpace: 'nowrap',
                            } },
                            OUPrice.symbol,
                            " ",
                            OUPrice.price),
                        badge && !isMobile && (React__default.createElement(SponsoredProductBadge, { badge: badge, textStyles: {
                                fontFamily: 'Lato',
                                fontSize: '12px',
                                fontStyle: 'normal',
                                fontWeight: 700,
                                lineHeight: 'normal',
                            } })))))),
            React__default.createElement("div", { style: {
                    marginLeft: 100,
                    color: '#717171',
                    fontFamily: 'Lato',
                    fontSize: '12px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '14px',
                    letterSpacing: 1,
                    paddingBottom: 4,
                } }, "Patrocinado"))));
}

export { SponsoredBrandWrapper, SponsoredDisplayPDP, SponsoredProductBanner, SponsoredProductTitle, SponsoredProductsContext, SponsoredProvider, SponsoredStrip, useSponsoredProducts };
